'use client';

import type { MysteryRoomDoorRuntimeState, MysteryRoomLocalRuntime } from '../core/localRuntime';
import {
  getNearestLocalInteractable,
  getPrimaryLocalAction,
  getVisibleLocalInteractables,
  hasInventoryFlashlight,
  isLocalLightSwitchEnabled,
} from '../systems/interactionSystem';
import { isMysteryRoomPistolActive } from '../systems/combatSystem';
import { facingFromVelocity } from '../systems/movementSystem';
import { mysteryRoomMoveVectorFromKeys } from '../systems/inputSystem';
import {
  buildSightBlockingSegments,
  castVisibilityCone,
  isCircleVisible,
  isRectVisible,
} from '../systems/visibilitySystem';
import { mysteryRoomPlayerColor, mysteryRoomPlayerStrokeColor } from '../world/markers';
import { registerMysteryRoomCustomObjectAlphaMask } from '../world/assetAlphaCollision';
import {
  MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN,
  resolveMysteryRoomCharacterFrame,
  type MysteryRoomCharacterSkin,
} from '../world/characterAssets';
import { getMysteryRoomTextureTileSize, getMysteryRoomTextureWorldTileSize, resolveMysteryRoomTextureAssetUrl } from '../world/defaultTextures';
import { getMysteryRoomMapDefinition } from '../world/mapRegistry';
import {
  MYSTERY_ROOM_FLASHLIGHT_ASSETS,
  MYSTERY_ROOM_GLOCK19_ASSETS,
  getMysteryRoomAccessCardVisual,
  getMysteryRoomEffectiveCustomObjectAsset,
  getMysteryRoomDefaultItemVisual,
  hasMysteryRoomCustomObjectTextureSkin,
  type MysteryRoomEffectiveCustomObjectAsset,
} from '../world/itemAssets';
import { MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS } from '../world/resourceDefinitions';
import {
  rectCenter,
  type MysteryRoomCustomObjectDefinition,
  type MysteryRoomInteractableKind,
  type MysteryRoomPropDefinition,
  type MysteryRoomRect,
  type MysteryRoomResourceSourceKind,
  type MysteryRoomSegment,
  type MysteryRoomSurfaceTexture,
  type MysteryRoomTextureLayer,
  type MysteryRoomVec2,
  type MysteryRoomMapDefinition,
} from '../world/mapTypes';
import type { MrMatchSnapshot } from '../net/mysteryRoomApi';
import { mrIsNetworkMovementActive, mrSmoothNetworkPosition } from '../net/interpolation';

type PhaserModule = typeof import('phaser');

const FLASHLIGHT_RADIUS = 920;
const FLASHLIGHT_HALF_ANGLE = Math.PI / 5.8;
const FLASHLIGHT_MASK_ALPHA = 0.94;
const HERO_EQUIPMENT_ANCHOR_OFFSET_Y = -25;
const GAME_CAMERA_DEFAULT_ZOOM = 1.4;
const CAMERA_MIN_ZOOM = 0.3;
const CAMERA_MAX_ZOOM = 1.75;
const LOCAL_CAMERA_MIN_ZOOM = 0.68;
const LOCAL_CAMERA_MAX_ZOOM = 1.75;
const LOCAL_CAMERA_MAX_DISTANCE_FROM_HERO = 620;
const CAMERA_BOUNDS_ANCHOR_MARGIN = 48;
const LIGHT_MASK_MIN_SIZE = 64;
const LIGHT_MASK_MAX_SIZE = 4096;
const LOCAL_CAMERA_FOLLOW_EVENT = 'mystery-room:set-camera-follow';
const MYSTERY_ROOM_AUDIO_SETTINGS_EVENT = 'mystery-room:audio-settings';
const MYSTERY_ROOM_AUDIO_SETTINGS_STORAGE_KEY = 'mysteryRoomAudioSettings';
const PISTOL_CROSSHAIR_CURSOR =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2732%27 height=%2732%27 viewBox=%270 0 32 32%27%3E%3Ccircle cx=%2716%27 cy=%2716%27 r=%2711%27 fill=%27none%27 stroke=%27%2322c55e%27 stroke-width=%271.6%27/%3E%3Cpath d=%27M16 6v7M16 19v7M6 16h7M19 16h7%27 stroke=%27%2322c55e%27 stroke-width=%271.6%27 stroke-linecap=%27round%27/%3E%3C/svg%3E") 16 16, crosshair';

type MysteryRoomAudioSettings = {
  musicVolume: number;
  sfxVolume: number;
};

type RuntimeAdapter = {
  getMode: () => 'server' | 'local';
  getSnapshot: () => MrMatchSnapshot | null;
  getSelfPlayerId: () => string | null;
  getServerNowMs: () => number;
  getMapDefinition?: () => MysteryRoomMapDefinition | undefined;
  getServerEquipment?: () => { activeInventoryItemId: string | null; flashlightEnabled: boolean };
  onMoveIntent?: (x: number, y: number) => void;
  onShootIntent?: (x: number, y: number) => void;
  onInteractIntent?: (interactableId: string) => void;
  getLocalRuntime?: () => MysteryRoomLocalRuntime | null;
  advancePredictedRuntime?: (dtMs: number) => void;
  onLocalMoveVectorChange?: (x: number, y: number) => void;
  onLocalPointerTarget?: (x: number, y: number) => void;
  onLocalInteract?: (interactableId: string) => void;
  onLocalShoot?: (x: number, y: number) => void;
};

type PlayerShape = {
  container: any;
  shadow: any;
  dust: any;
  body: any;
  skin: any;
  heldWeapon: any;
  heldTool: any;
  label: any;
  target: any;
  facing: 'up' | 'down' | 'left' | 'right';
  previousPosition: MysteryRoomVec2 | null;
  renderPosition: MysteryRoomVec2 | null;
  skinFrameKey: string | null;
};

type ServerPlayerDrawState = {
  playerId: string;
  x: number;
  y: number;
  isSelf: boolean;
  visionRadius: number;
  facing: 'up' | 'down' | 'left' | 'right';
};

type ActorShape = {
  container: any;
  shadow: any;
  dust: any;
  skin: any;
  body: any;
  visor: any;
  direction: any;
  heldWeapon: any;
  heldTool: any;
  label: any;
  seed: number;
  skinFrameKey: string | null;
  previousPosition: MysteryRoomVec2 | null;
  renderPosition: MysteryRoomVec2 | null;
};

type HeldEquipmentShape = Pick<PlayerShape | ActorShape, 'heldWeapon' | 'heldTool'>;

type IncidentShape = {
  container: any;
  marker: any;
  label: any;
};

type InteractableShape = {
  marker: any;
  label: any;
};

type DoorShape = {
  frame: any;
  panelA: any;
  panelB: any;
  seam: any;
  glow: any;
  baseRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  orientation: 'horizontal' | 'vertical';
};

type DoorLeafRects = {
  a: MysteryRoomRect;
  b: MysteryRoomRect;
};

function doorLeafRects(rect: MysteryRoomRect, orientation: 'horizontal' | 'vertical'): DoorLeafRects {
  const gap = 4;
  if (orientation === 'vertical') {
    const height = Math.max(4, rect.height / 2 - gap / 2);
    return {
      a: { x: rect.x, y: rect.y, width: rect.width, height },
      b: { x: rect.x, y: rect.y + rect.height / 2 + gap / 2, width: rect.width, height },
    };
  }

  const width = Math.max(4, rect.width / 2 - gap / 2);
  return {
    a: { x: rect.x, y: rect.y, width, height: rect.height },
    b: { x: rect.x + rect.width / 2 + gap / 2, y: rect.y, width, height: rect.height },
  };
}

function setDoorRectGeometry(object: any, rect: MysteryRoomRect, offset: MysteryRoomVec2 = { x: 0, y: 0 }) {
  object.setPosition(rect.x + rect.width / 2 + offset.x, rect.y + rect.height / 2 + offset.y);
  object.setDisplaySize(rect.width, rect.height);
}

function applyDoorOpenProgress(shape: DoorShape, openProgress: number) {
  const progress = Math.max(0, Math.min(1, openProgress));
  const leaves = doorLeafRects(shape.baseRect, shape.orientation);
  const travel =
    shape.orientation === 'vertical'
      ? Math.max(8, shape.baseRect.height * 0.42) * progress
      : Math.max(8, shape.baseRect.width * 0.42) * progress;
  const offsetA = shape.orientation === 'vertical' ? { x: 0, y: -travel } : { x: -travel, y: 0 };
  const offsetB = shape.orientation === 'vertical' ? { x: 0, y: travel } : { x: travel, y: 0 };
  setDoorRectGeometry(shape.panelA, leaves.a, offsetA);
  setDoorRectGeometry(shape.panelB, leaves.b, offsetB);
  shape.seam.setVisible(progress < 0.92);
}

function setDoorLeafTint(shape: DoorShape, tint: number) {
  shape.panelA.fillColor = tint;
  shape.panelB.fillColor = tint;
  shape.glow.fillColor = tint;
}

function setDoorLeafAlpha(shape: DoorShape, alpha: number) {
  shape.panelA.setAlpha(alpha);
  shape.panelB.setAlpha(alpha);
  shape.seam.setAlpha(Math.min(0.78, alpha));
}

type MysteryRoomAudioLoop = {
  gain: GainNode;
  sources: AudioScheduledSourceNode[];
};

type MysteryRoomAudioDebugState = {
  unlocked: boolean;
  activeLoops: number;
  oneShots: number;
  footsteps: number;
};

export function createLabScene(Phaser: PhaserModule, adapter: RuntimeAdapter) {
  return class LabScene extends Phaser.Scene {
    private readonly playerShapes = new Map<string, PlayerShape>();
    private readonly npcShapes = new Map<string, ActorShape>();
    private readonly localBotShapes = new Map<string, ActorShape>();
    private readonly incidentShapes = new Map<string, IncidentShape>();
    private readonly doorShapes = new Map<string, DoorShape>();
    private readonly interactableShapes = new Map<string, InteractableShape>();
    private heroShape: ActorShape | null = null;
    private targetMarker: any = null;
    private followsPlayerId: string | null = null;
    private cameraPanState: { pointerId: number; x: number; y: number } | null = null;
    private cameraSpawnSignature: string | null = null;
    private cameraSelfCenteredForPlayerId: string | null = null;
    private cameraBoundsSize = { width: 4096, height: 2304 };
    private localCameraFollowHero = true;
    private latestLocalHeroPosition: MysteryRoomVec2 | null = null;
    private latestServerSelfPosition: MysteryRoomVec2 | null = null;
    private latestPointerWorldPoint: MysteryRoomVec2 | null = null;
    private localCursorMode: 'default' | 'pistol' = 'default';
    private localStaticGraphics: any = null;
    private localPropShadowGraphics: any = null;
    private localPropGraphics: any = null;
    private localDarknessGraphics: any = null;
    private localLightGraphics: any = null;
    private localLightMaskTexture: any = null;
    private localLightMaskTextureKey: string | null = null;
    private localLightMaskContext: CanvasRenderingContext2D | null = null;
    private localLightMaskSize = { width: 0, height: 0 };
    private localLightDarknessOverlay: any = null;
    private localEffectGraphics: any = null;
    private localWallGraphics: any = null;
    private localOccluderGraphics: any = null;
    private localFixtureObjects: Array<{
      object: any;
      rect: MysteryRoomRect;
      sourceId?: string;
      offset?: MysteryRoomVec2;
      stableTexture?: boolean;
      role?: 'label';
    }> = [];
    private localFixtureMaskObjects: any[] = [];
    private localFixtureMaskSyncObjects: Array<{ object: any; sourceId: string; offset: MysteryRoomVec2; stableTexture?: boolean }> = [];
    private localSurfaceTextureObjects: any[] = [];
    private readonly pendingAssetTextureLoads = new Set<string>();
    private serverStaticGraphics: any = null;
    private serverWallGraphics: any = null;
    private serverVisionGraphics: any = null;
    private serverLightGraphics: any = null;
    private serverEffectGraphics: any = null;
    private serverLabelLayer: any = null;
    private serverSurfaceTextureObjects: any[] = [];
    private npcVisionGraphics: any = null;
    private npcTargetGraphics: any = null;
    private localLabelLayer: any = null;
    private keys: any = null;
    private lastMoveVector = { x: 0, y: 0 };
    private lastServerMoveVector = { x: 0, y: 0 };
    private mapInitializedForLocal = false;
    private localMapSignature: string | null = null;
    private mapInitializedForServer = false;
    private serverMapSignature: string | null = null;
    private serverClockAnchor: { serverNowMs: number; sceneTimeMs: number } | null = null;
    private audioSystem: MysteryRoomRuntimeAudioSystem | null = null;
    private readonly serverNpcFacingById = new Map<string, 'up' | 'down' | 'left' | 'right'>();
    private readonly centerCameraOnPlayerHandler = () => this.centerCameraOnCurrentPlayer(false);
    private readonly cameraFollowToggleHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
      this.setLocalCameraFollowHero(detail?.enabled !== false);
    };
    private readonly scaleResizeHandler = (gameSize: { width?: number; height?: number }) => this.handleScaleResize(gameSize);

    /** Зачем: при ожидании респавна камера только на теле героя, без панорамирования/ввода. */
    private isLocalRespawnSpectating(): boolean {
      if (adapter.getMode() !== 'local') return false;
      return adapter.getLocalRuntime?.()?.gameStatus === 'respawning';
    }

    constructor() {
      super({ key: 'LabScene' });
    }

    preload() {
      const frames = [
        MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.idle,
        ...Object.values(MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.frames).flat(),
      ];
      const seen = new Set<string>();
      for (const frame of frames) {
        if (seen.has(frame.key) || this.textures.exists(frame.key)) {
          continue;
        }
        seen.add(frame.key);
        this.load.image(frame.key, frame.url);
      }
    }

    create() {
      const map =
        adapter.getMode() === 'local'
          ? adapter.getLocalRuntime?.()?.map ?? getMysteryRoomMapDefinition()
          : this.resolveServerMapDefinition(adapter.getSnapshot());

      this.configureMapCamera(map, adapter.getMode());
      this.audioSystem = new MysteryRoomRuntimeAudioSystem();

      if (adapter.getMode() === 'local') {
        this.createLocalWorld(map);
      } else {
        this.createServerWorld(map);
      }
      this.createKeyboardInput(Phaser);

      this.targetMarker = this.add.circle(map.spawn.x, map.spawn.y, 10, 0xf59e0b, 0.22);
      this.targetMarker.setStrokeStyle(2, 0xf59e0b, 0.85);
      this.targetMarker.setVisible(false);
      this.targetMarker.setDepth(160);

      this.input.on('pointerdown', (pointer: any) => {
        this.audioSystem?.unlock();
        if (this.isLocalRespawnSpectating()) {
          return;
        }
        if (pointer.middleButtonDown?.()) {
          this.beginCameraPan(pointer);
          return;
        }
        if (!pointer.leftButtonDown()) {
          return;
        }
        const worldPoint = pointer.positionToCamera(this.cameras.main) as any;
        this.latestPointerWorldPoint = { x: worldPoint.x, y: worldPoint.y };
        if (adapter.getMode() === 'local') {
          const runtime = adapter.getLocalRuntime?.();
          if (runtime && isMysteryRoomPistolActive(runtime)) {
            this.targetMarker?.setVisible(false);
            adapter.onLocalShoot?.(worldPoint.x, worldPoint.y);
            return;
          }
        } else if (this.isServerWeaponActive()) {
          this.targetMarker?.setVisible(false);
          adapter.onShootIntent?.(worldPoint.x, worldPoint.y);
          return;
        }
        this.targetMarker?.setPosition(worldPoint.x, worldPoint.y);
        this.targetMarker?.setVisible(true);
        if (adapter.getMode() === 'local') {
          adapter.onLocalPointerTarget?.(worldPoint.x, worldPoint.y);
          return;
        }
        adapter.onLocalPointerTarget?.(worldPoint.x, worldPoint.y);
        adapter.onMoveIntent?.(worldPoint.x, worldPoint.y);
      });
      this.input.on('pointermove', (pointer: any) => {
        const worldPoint = pointer.positionToCamera(this.cameras.main) as any;
        this.latestPointerWorldPoint = { x: worldPoint.x, y: worldPoint.y };
        if (!this.isLocalRespawnSpectating()) {
          this.updateCameraPan(pointer);
        }
      });
      this.input.on('pointerup', (pointer: any) => this.endCameraPan(pointer));
      this.input.on('wheel', (pointer: any, _gameObjects: unknown, _deltaX: number, deltaY: number) => {
        this.zoomCameraAtPointer(pointer, deltaY);
      });
      this.input.mouse?.disableContextMenu?.();
      this.input.keyboard?.on('keydown', () => this.audioSystem?.unlock());
      this.scale.on(Phaser.Scale.Events.RESIZE, this.scaleResizeHandler);
      if (typeof window !== 'undefined') {
        window.addEventListener('mystery-room:center-camera-on-player', this.centerCameraOnPlayerHandler);
        window.addEventListener(LOCAL_CAMERA_FOLLOW_EVENT, this.cameraFollowToggleHandler);
      }
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.scale.off(Phaser.Scale.Events.RESIZE, this.scaleResizeHandler);
        this.destroyLocalLightMaskResources();
        if (typeof window !== 'undefined') {
          window.removeEventListener('mystery-room:center-camera-on-player', this.centerCameraOnPlayerHandler);
          window.removeEventListener(LOCAL_CAMERA_FOLLOW_EVENT, this.cameraFollowToggleHandler);
          delete (window as any).__mysteryRoomCameraDebug;
        }
        this.setLocalCursor(false);
      });
    }

    private configureStableCameraRendering() {
      const camera = this.cameras.main as any;
      camera.roundPixels = true;
    }

    private configureMapCamera(map: Pick<MysteryRoomMapDefinition, 'id' | 'width' | 'height' | 'spawn'>, mode: 'server' | 'local') {
      const width = Math.max(1, map.width);
      const height = Math.max(1, map.height);
      this.cameraBoundsSize = { width, height };
      this.physics.world.setBounds(0, 0, width, height);
      this.cameras.main.removeBounds?.();
      this.cameras.main.setBackgroundColor(mode === 'local' ? 0x020617 : 0x071226);
      this.cameras.main.setZoom(GAME_CAMERA_DEFAULT_ZOOM);
      this.applyCameraZoomBounds();
      this.configureStableCameraRendering();
      this.centerCameraOnSpawn(map, mode);
    }

    private handleScaleResize(gameSize: { width?: number; height?: number }) {
      const width = Math.max(1, Math.floor(gameSize.width ?? this.scale.width ?? this.cameras.main.width));
      const height = Math.max(1, Math.floor(gameSize.height ?? this.scale.height ?? this.cameras.main.height));
      this.cameras.resize(width, height);
      this.applyCameraZoomBounds();
      this.clampCameraScroll();
      if (adapter.getMode() === 'local') {
        this.ensureLocalLightMaskViewport();
      }
      this.publishCameraDebug();
    }

    private centerCameraOnSpawn(map: Pick<MysteryRoomMapDefinition, 'id' | 'width' | 'height' | 'spawn'>, mode: 'server' | 'local') {
      const signature = `${mode}:${map.id}:${map.width}:${map.height}:${Math.round(map.spawn.x)}:${Math.round(map.spawn.y)}`;
      if (this.cameraSpawnSignature === signature) {
        return;
      }
      this.cameraSpawnSignature = signature;
      this.cameraSelfCenteredForPlayerId = null;
      this.stopCameraFollow();
      this.centerCameraOnPoint(map.spawn, false);
    }

    private centerCameraOnCurrentPlayer(animated: boolean) {
      if (adapter.getMode() === 'local') {
        const runtime = adapter.getLocalRuntime?.();
        const target = this.latestLocalHeroPosition ?? runtime?.hero.position ?? runtime?.map.spawn;
        if (target) {
          this.centerCameraOnPoint(target, animated);
        }
        return;
      }

      const snapshot = adapter.getSnapshot();
      const selfPlayerId = adapter.getSelfPlayerId();
      const runtime = adapter.getLocalRuntime?.();
      if (runtime) {
        this.centerCameraOnPoint(runtime.hero.position, animated);
        return;
      }
      const selfPlayer = snapshot?.players.find((player) => player.playerId === selfPlayerId);
      if (selfPlayer) {
        this.centerCameraOnPoint({ x: selfPlayer.x, y: selfPlayer.y }, animated);
        return;
      }
      const map = this.resolveServerMapDefinition(snapshot);
      this.centerCameraOnPoint(map.spawn, animated);
    }

    private centerCameraOnPoint(point: MysteryRoomVec2, animated: boolean) {
      const camera = this.cameras.main;
      if ((adapter.getMode() === 'local' && !this.localCameraFollowHero) || adapter.getMode() === 'server') {
        this.stopCameraFollow();
      }
      if (animated && typeof (camera as any).pan === 'function') {
        (camera as any).pan(point.x, point.y, 220, 'Sine.easeOut', true);
      } else {
        camera.centerOn(point.x, point.y);
      }
      this.clampCameraScroll();
      this.publishCameraDebug();
    }

    private setLocalCameraFollowHero(enabled: boolean) {
      if (adapter.getMode() !== 'local') {
        return;
      }
      this.localCameraFollowHero = enabled;
      if (enabled) {
        this.cameraPanState = null;
        this.followsPlayerId = 'local-hero';
        if (this.latestLocalHeroPosition) {
          this.centerCameraOnPoint(this.latestLocalHeroPosition, false);
        }
      } else {
        this.stopCameraFollow();
        this.clampCameraScroll();
      }
      this.publishCameraDebug();
    }

    private stopCameraFollow() {
      const camera = this.cameras.main as any;
      camera.stopFollow?.();
      this.followsPlayerId = null;
    }

    private beginCameraPan(pointer: any) {
      pointer.event?.preventDefault?.();
      if (this.isLocalRespawnSpectating()) {
        return;
      }
      if (
        (adapter.getMode() === 'local' && this.localCameraFollowHero) ||
        (adapter.getMode() === 'server' && this.latestServerSelfPosition)
      ) {
        return;
      }
      this.stopCameraFollow();
      this.cameraPanState = { pointerId: pointer.id ?? 0, x: pointer.x, y: pointer.y };
    }

    private updateCameraPan(pointer: any) {
      if (!this.cameraPanState || this.cameraPanState.pointerId !== (pointer.id ?? 0)) {
        return;
      }
      pointer.event?.preventDefault?.();
      const camera = this.cameras.main;
      const dx = pointer.x - this.cameraPanState.x;
      const dy = pointer.y - this.cameraPanState.y;
      camera.scrollX -= dx / Math.max(0.001, camera.zoom);
      camera.scrollY -= dy / Math.max(0.001, camera.zoom);
      this.cameraPanState = { pointerId: this.cameraPanState.pointerId, x: pointer.x, y: pointer.y };
      this.clampCameraScroll();
      this.publishCameraDebug();
    }

    private endCameraPan(pointer: any) {
      if (!this.cameraPanState || this.cameraPanState.pointerId !== (pointer.id ?? 0)) {
        return;
      }
      pointer.event?.preventDefault?.();
      this.cameraPanState = null;
      this.clampCameraScroll();
      this.publishCameraDebug();
    }

    private zoomCameraAtPointer(pointer: any, deltaY: number) {
      pointer.event?.preventDefault?.();
      if (this.isLocalRespawnSpectating()) {
        return;
      }
      const camera = this.cameras.main;
      const before = pointer.positionToCamera(camera) as MysteryRoomVec2;
      const zoomFactor = deltaY > 0 ? 0.9 : 1.1;
      const nextZoom = Math.max(this.getEffectiveCameraMinZoom(), Math.min(this.getEffectiveCameraMaxZoom(), camera.zoom * zoomFactor));
      if (Math.abs(nextZoom - camera.zoom) < 0.0001) {
        return;
      }
      if (adapter.getMode() === 'local' && !this.localCameraFollowHero) {
        this.stopCameraFollow();
      }
      camera.setZoom(nextZoom);
      const after = pointer.positionToCamera(camera) as MysteryRoomVec2;
      camera.scrollX += before.x - after.x;
      camera.scrollY += before.y - after.y;
      this.clampCameraScroll();
      if (adapter.getMode() === 'local') {
        this.ensureLocalLightMaskViewport();
      }
      this.publishCameraDebug();
    }

    private applyCameraZoomBounds() {
      const camera = this.cameras.main;
      const minZoom = this.getEffectiveCameraMinZoom();
      const maxZoom = this.getEffectiveCameraMaxZoom();
      if (camera.zoom < minZoom) {
        camera.setZoom(minZoom);
      } else if (camera.zoom > maxZoom) {
        camera.setZoom(maxZoom);
      }
    }

    private getCameraBoundsSize() {
      return {
        width: this.cameraBoundsSize.width,
        height: this.cameraBoundsSize.height,
      };
    }

    private getEffectiveCameraMinZoom() {
      const camera = this.cameras.main;
      const bounds = this.getCameraBoundsSize();
      const fitZoom = Math.max(
        camera.width / Math.max(1, bounds.width),
        camera.height / Math.max(1, bounds.height),
      );
      const baseMinZoom = adapter.getMode() === 'local' ? LOCAL_CAMERA_MIN_ZOOM : CAMERA_MIN_ZOOM;
      return Math.max(baseMinZoom, fitZoom + 0.002);
    }

    private getEffectiveCameraMaxZoom() {
      return adapter.getMode() === 'local' ? LOCAL_CAMERA_MAX_ZOOM : CAMERA_MAX_ZOOM;
    }

    private clampCameraScroll() {
      this.applyCameraZoomBounds();
      const camera = this.cameras.main;
      const bounds = this.getCameraBoundsSize();
      const boundsWidth = bounds.width;
      const boundsHeight = bounds.height;
      const viewWidth = camera.width / Math.max(0.001, camera.zoom);
      const viewHeight = camera.height / Math.max(0.001, camera.zoom);
      const cameraCenterOffsetX = camera.width * 0.5;
      const cameraCenterOffsetY = camera.height * 0.5;
      const heroPosition = adapter.getMode() === 'local' ? this.latestLocalHeroPosition : this.latestServerSelfPosition;
      const respawnSpectateLocal = adapter.getMode() === 'local' && adapter.getLocalRuntime?.()?.gameStatus === 'respawning';
      if (heroPosition) {
        let centerX = camera.scrollX + cameraCenterOffsetX;
        let centerY = camera.scrollY + cameraCenterOffsetY;
        if (respawnSpectateLocal || adapter.getMode() === 'server' || this.localCameraFollowHero) {
          centerX = heroPosition.x;
          centerY = heroPosition.y;
          this.followsPlayerId = adapter.getMode() === 'server' ? adapter.getSelfPlayerId() ?? 'server-self' : 'local-hero';
        } else {
          const dx = centerX - heroPosition.x;
          const dy = centerY - heroPosition.y;
          const distance = Math.hypot(dx, dy);
          if (distance > LOCAL_CAMERA_MAX_DISTANCE_FROM_HERO) {
            const ratio = LOCAL_CAMERA_MAX_DISTANCE_FROM_HERO / distance;
            centerX = heroPosition.x + dx * ratio;
            centerY = heroPosition.y + dy * ratio;
          }
        }
        camera.scrollX = centerX - cameraCenterOffsetX;
        camera.scrollY = centerY - cameraCenterOffsetY;
      }
      camera.scrollX = this.clampCameraAxisScroll(camera.scrollX, viewWidth, boundsWidth);
      camera.scrollY = this.clampCameraAxisScroll(camera.scrollY, viewHeight, boundsHeight);
    }

    private clampCameraAxisScroll(scroll: number, viewSize: number, boundsSize: number) {
      if (boundsSize <= viewSize + CAMERA_BOUNDS_ANCHOR_MARGIN * 2) {
        return (boundsSize - viewSize) / 2;
      }

      const anchor = Math.min(CAMERA_BOUNDS_ANCHOR_MARGIN, boundsSize / 2);
      return Math.max(-viewSize + anchor, Math.min(scroll, boundsSize - anchor));
    }

    private publishCameraDebug() {
      if (typeof window === 'undefined') {
        return;
      }
      const camera = this.cameras.main;
      (window as any).__mysteryRoomCameraDebug = {
        mode: adapter.getMode(),
        centerX: Math.round(camera.scrollX + camera.width / 2),
        centerY: Math.round(camera.scrollY + camera.height / 2),
        scrollX: Math.round(camera.scrollX),
        scrollY: Math.round(camera.scrollY),
        zoom: Math.round(camera.zoom * 1000) / 1000,
        following: this.followsPlayerId,
      };
    }

    private stableTexturePoint(x: number, y: number): MysteryRoomVec2 {
      return {
        x: Math.round(x),
        y: Math.round(y),
      };
    }

    private configureStableTextureVisual(object: any) {
      if (!object) {
        return;
      }
      object.roundPixels = true;
      object.setOrigin?.(0.5, 0.5);
    }

    private ensureStaticImageTexture(textureKey: string, url: string, onReady?: () => void) {
      if (this.textures.exists(textureKey)) {
        onReady?.();
        return;
      }
      const fileCompleteEvent = `filecomplete-image-${textureKey}`;
      if (onReady) {
        this.load.once(fileCompleteEvent, () => {
          this.pendingAssetTextureLoads.delete(textureKey);
          onReady();
        });
      }
      this.load.once('loaderror', (file: { key?: string }) => {
        if (file?.key === textureKey) {
          this.pendingAssetTextureLoads.delete(textureKey);
        }
      });
      if (!this.pendingAssetTextureLoads.has(textureKey)) {
        this.pendingAssetTextureLoads.add(textureKey);
        this.load.image(textureKey, url);
      }
      if (!this.load.isLoading()) {
        this.load.start();
      }
    }

    update() {
      if (adapter.getMode() === 'local') {
        this.updateLocalMode();
        return;
      }

      const snapshot = adapter.getSnapshot();
      const selfPlayerId = adapter.getSelfPlayerId();

      if (!snapshot) {
        return;
      }

      const deltaMs = Math.max(0, Math.min(100, Math.round((this.game.loop as any)?.delta ?? 16)));
      adapter.advancePredictedRuntime?.(deltaMs);
      const serverNowMs = this.estimateServerNowMs(adapter.getServerNowMs());
      const map = this.resolveServerMapDefinition(snapshot);
      this.createServerWorld(map);
      const clientRuntime = adapter.getLocalRuntime?.();
      if (clientRuntime) {
        this.updateLocalFixtureVisibility(clientRuntime);
        this.updateLocalEnvironment(clientRuntime);
        this.updateLocalTargetMarker(clientRuntime);
        this.updateLocalDoors(clientRuntime);
        this.updateLocalInteractables(clientRuntime);
      }
      const seen = new Set<string>();
      const playerDrawStates: ServerPlayerDrawState[] = [];

      for (const player of snapshot.players) {
        seen.add(player.playerId);
        const isSelf = selfPlayerId === player.playerId;
        const shape = this.ensurePlayerShape(player.playerId, player.displayName, isSelf);

        let drawX = player.x;
        let drawY = player.y;
        let movementVector = {
          x: 0,
          y: 0,
        };
        let isMoving = false;
        if (isSelf && clientRuntime) {
          drawX = clientRuntime.hero.position.x;
          drawY = clientRuntime.hero.position.y;
          shape.renderPosition = { x: drawX, y: drawY };
          movementVector = {
            x: clientRuntime.hero.velocity.x,
            y: clientRuntime.hero.velocity.y,
          };
          isMoving = clientRuntime.hero.animationState === 'walk' || Math.hypot(movementVector.x, movementVector.y) > 0.35;
          shape.facing = clientRuntime.hero.facing;
          shape.container.setAlpha(clientRuntime.hero.hidden ? 0.42 : 1);
        } else {
          const moveCommand = this.getServerActorMoveCommand(snapshot, player.isBot ? 'bot' : 'player', player.playerId);
          const targetPoint = moveCommand
            ? { x: Number(moveCommand.targetX ?? player.x), y: Number(moveCommand.targetY ?? player.y) }
            : {
                x: player.movement?.state === 'moving' ? player.movement.targetX : player.x,
                y: player.movement?.state === 'moving' ? player.movement.targetY : player.y,
              };
          const currentPosition = shape.renderPosition ?? { x: player.x, y: player.y };
          const movementActive = mrIsNetworkMovementActive({
            state: moveCommand ? 'moving' : player.movement?.state,
            nowMs: serverNowMs,
            etaMs: Number(moveCommand?.etaMs ?? player.movement?.etaMs ?? 0),
            position: currentPosition,
            target: targetPoint,
            stopDistance: 4,
          });
          const drawPosition = mrSmoothNetworkPosition({
            current: currentPosition,
            target: movementActive ? targetPoint : { x: player.x, y: player.y },
            speed: Math.max(48, Number(moveCommand?.speed ?? player.movement?.speed ?? 145)),
            dtMs: deltaMs,
            snapDistance: movementActive ? 2.5 : 1.5,
            teleportDistance: 620,
            catchupMultiplier: movementActive ? 1.02 : 1.45,
            minStep: 0.35,
          });
          drawX = drawPosition.x;
          drawY = drawPosition.y;
          movementVector = {
            x: drawX - currentPosition.x,
            y: drawY - currentPosition.y,
          };
          shape.renderPosition = drawPosition;
          isMoving = movementActive || Math.hypot(movementVector.x, movementVector.y) > 0.35;
          shape.facing = isMoving ? facingFromVelocity(movementVector, shape.facing) : shape.facing;
          shape.container.setAlpha(1);
        }
        shape.previousPosition = { x: drawX, y: drawY };
        shape.target.set(drawX, drawY);
        shape.container.setPosition(shape.target.x, shape.target.y);
        shape.label.setPosition(shape.target.x, shape.target.y - 28);
        const playerDepth = 108 + drawY / 10;
        shape.container.setDepth(playerDepth);
        shape.label.setDepth(playerDepth + 1);
        const skinReady = this.syncCharacterSkinSprite(
          shape,
          MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN,
          shape.facing,
          isMoving ? 'walk' : 'idle',
          movementVector,
        );
        shape.body.setVisible(false);
        this.syncServerHeldEquipment(shape, player, isSelf);
        this.syncFootDust(shape.dust, isMoving ? 'walk' : 'idle', movementVector, serverNowMs / 110);
        shape.shadow.setScale(isMoving ? 0.9 : 1.02);
        playerDrawStates.push({
          playerId: player.playerId,
          x: drawX,
          y: drawY,
          isSelf,
          visionRadius: player.visionRadius ?? map.visibilityRadius ?? 620,
          facing: shape.facing,
        });

        if (isSelf) {
          this.latestServerSelfPosition = { x: drawX, y: drawY };
          if (this.cameraSelfCenteredForPlayerId !== player.playerId) {
            this.cameraSelfCenteredForPlayerId = player.playerId;
            this.centerCameraOnPoint({ x: drawX, y: drawY }, false);
          } else {
            this.clampCameraScroll();
          }
        }
      }
      this.syncServerKeyboardMovement();

      const stalePlayerIds: string[] = [];
      this.playerShapes.forEach((_shape, playerId) => {
        if (!seen.has(playerId)) {
          stalePlayerIds.push(playerId);
        }
      });
      stalePlayerIds.forEach((playerId) => {
        const shape = this.playerShapes.get(playerId);
        if (!shape) {
          return;
        }
        shape.container.destroy();
        shape.label.destroy();
        this.playerShapes.delete(playerId);
      });

      this.updateServerPlayerVision(snapshot, map, playerDrawStates);
      this.updateServerNpcs(snapshot, map, serverNowMs, deltaMs);
      this.updateServerFixtureVisibility(snapshot, map);
      this.updateServerMarkers(snapshot, map);
      this.updateServerDoors(snapshot);
      this.updateServerFlashlightEffects(snapshot, map, playerDrawStates);
      this.updateServerShotEffects(snapshot);
      const selfPlayer = snapshot.players.find((player) => player.playerId === selfPlayerId);
      this.audioSystem?.updateServer(snapshot, map, selfPlayer, serverNowMs);
    }

    private resolveServerMapDefinition(snapshot?: MrMatchSnapshot | null): MysteryRoomMapDefinition {
      const expectedMapId = snapshot?.mapId;
      const map = adapter.getMapDefinition?.();
      if (!expectedMapId || map?.id === expectedMapId) {
        return map ?? getMysteryRoomMapDefinition(expectedMapId);
      }
      return getMysteryRoomMapDefinition(expectedMapId);
    }

    private createServerWorld(map: ReturnType<typeof getMysteryRoomMapDefinition>) {
      const nextMapSignature = buildServerMapSignature(map);
      if (this.mapInitializedForServer && this.serverMapSignature === nextMapSignature) {
        this.ensureServerOverlayGraphics();
        return;
      }
      if (this.mapInitializedForServer) {
        this.destroyServerWorld();
      }

      const clientRuntime = adapter.getLocalRuntime?.();
      if (clientRuntime) {
        this.createLocalWorld(clientRuntime.map);
        this.ensureServerOverlayGraphics();
        this.mapInitializedForServer = true;
        this.serverMapSignature = nextMapSignature;
        return;
      }

      this.configureMapCamera(map, 'server');

      this.serverStaticGraphics = this.add.graphics();
      this.serverWallGraphics = this.add.graphics();
      this.serverVisionGraphics = this.add.graphics();
      this.serverLightGraphics = this.add.graphics();
      this.serverEffectGraphics = this.add.graphics();
      this.npcVisionGraphics = this.add.graphics();
      this.serverLabelLayer = this.add.layer();

      this.serverStaticGraphics.fillStyle(0x050b1a, 1);
      this.serverStaticGraphics.fillRect(0, 0, map.width, map.height);

      for (const room of map.rooms) {
        for (const floor of room.floor) {
          this.serverStaticGraphics.fillStyle(room.color, runtimeBaseColorOpacity(room, room.id === 'central_cross' ? 0.75 : 0.94));
          this.serverStaticGraphics.fillRoundedRect(floor.x, floor.y, floor.width, floor.height, 18);
          this.serverStaticGraphics.lineStyle(4, 0xf8fafc, 0.06);
          this.serverStaticGraphics.strokeRoundedRect(floor.x, floor.y, floor.width, floor.height, 18);
          if (room.textures?.length) {
            this.createSurfaceTextureLayerImages(room.textures, floor, 7, 0, 'server');
          } else {
            this.createSurfaceTextureImage(room.floorTexture, floor, 6, 0, 'server');
          }
        }

        const label = this.add.text(room.labelPoint.x, room.labelPoint.y, room.title, {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '18px',
          color: '#f8fafc',
          backgroundColor: 'rgba(2, 6, 23, 0.62)',
          padding: { x: 8, y: 5 },
        });
        label.setOrigin(0.5, 0);
        label.setDepth(40);
        this.serverLabelLayer.add(label);
      }

      this.serverStaticGraphics.lineStyle(2, 0xffffff, 0.04);
      for (let x = 0; x <= map.width; x += 96) {
        this.serverStaticGraphics.lineBetween(x, 0, x, map.height);
      }
      for (let y = 0; y <= map.height; y += 96) {
        this.serverStaticGraphics.lineBetween(0, y, map.width, y);
      }
      this.drawMapDetailsWithStaticGraphics(map, this.serverStaticGraphics);

      this.serverStaticGraphics.fillStyle(0x111827, 0.9);
      this.serverStaticGraphics.lineStyle(2, 0xe2e8f0, 0.08);
      map.occluders.forEach((occluder) => {
        this.serverStaticGraphics.fillRoundedRect(
          occluder.rect.x,
          occluder.rect.y,
          occluder.rect.width,
          occluder.rect.height,
          12,
        );
        this.serverStaticGraphics.strokeRoundedRect(
          occluder.rect.x,
          occluder.rect.y,
          occluder.rect.width,
          occluder.rect.height,
          12,
        );
      });
      this.createServerFixtureObjects(map);

      const serverWallBodyWidth = 14;
      map.walls.forEach((segment) => {
        const bodyWidth = serverWallBodyWidth;
        const outlineWidth = runtimeOutlineWidth(segment, 1);
        const outlineAlpha = runtimeOutlineOpacity(segment, 0.52);
        if (outlineWidth > 0 && outlineAlpha > 0) {
          drawWallSegmentBlock(this.serverWallGraphics, segment, bodyWidth + outlineWidth * 2, segment.outlineColor ?? 0xe2e8f0, outlineAlpha);
        }
        drawWallSegmentBlock(this.serverWallGraphics, segment, bodyWidth, segment.color ?? 0xe2e8f0, runtimeBaseColorOpacity(segment, 0.85));
        if (segment.textures?.length) {
          this.createWallTextureLayerImages(segment, 67, 'server');
        } else {
          this.createWallTextureImage(segment, 66, 'server');
        }
      });
      drawWallJointBlocks(this.serverWallGraphics, map.walls, serverWallBodyWidth, 0.85);
      this.serverWallGraphics.setDepth(65);
      this.serverVisionGraphics.setDepth(18);
      this.serverLightGraphics.setDepth(122);
      this.serverLightGraphics.setBlendMode(Phaser.BlendModes.ADD);
      this.serverEffectGraphics.setDepth(162);
      this.serverEffectGraphics.setBlendMode(Phaser.BlendModes.ADD);
      this.npcVisionGraphics.setDepth(68);

      map.doors.forEach((door) => {
        this.doorShapes.set(door.id, this.createRuntimeDoorShape(door));
      });

      this.mapInitializedForServer = true;
      this.serverMapSignature = nextMapSignature;
    }

    private ensureServerOverlayGraphics() {
      if (!this.serverVisionGraphics) {
        this.serverVisionGraphics = this.add.graphics();
        this.serverVisionGraphics.setDepth(18);
      }
      if (!this.serverLightGraphics) {
        this.serverLightGraphics = this.add.graphics();
        this.serverLightGraphics.setDepth(122);
        this.serverLightGraphics.setBlendMode(Phaser.BlendModes.ADD);
      }
      if (!this.serverEffectGraphics) {
        this.serverEffectGraphics = this.add.graphics();
        this.serverEffectGraphics.setDepth(162);
        this.serverEffectGraphics.setBlendMode(Phaser.BlendModes.ADD);
      }
      if (!this.npcVisionGraphics) {
        this.npcVisionGraphics = this.add.graphics();
        this.npcVisionGraphics.setDepth(68);
      }
    }

    private destroyServerWorld() {
      if (adapter.getLocalRuntime?.()) {
        this.serverVisionGraphics?.destroy();
        this.serverVisionGraphics = null;
        this.serverLightGraphics?.destroy();
        this.serverLightGraphics = null;
        this.serverEffectGraphics?.destroy();
        this.serverEffectGraphics = null;
        this.mapInitializedForServer = false;
        this.serverMapSignature = null;
        this.serverNpcFacingById.clear();
        return;
      }

      this.serverStaticGraphics?.destroy();
      this.serverStaticGraphics = null;
      this.serverWallGraphics?.destroy();
      this.serverWallGraphics = null;
      this.serverVisionGraphics?.destroy();
      this.serverVisionGraphics = null;
      this.serverLightGraphics?.destroy();
      this.serverLightGraphics = null;
      this.serverEffectGraphics?.destroy();
      this.serverEffectGraphics = null;
      this.npcVisionGraphics?.destroy();
      this.npcVisionGraphics = null;
      this.serverLabelLayer?.destroy();
      this.serverLabelLayer = null;
      this.serverSurfaceTextureObjects.forEach((object) => object.destroy?.());
      this.serverSurfaceTextureObjects = [];
      this.doorShapes.forEach((shape) => {
        shape.frame.destroy();
        shape.panelA.destroy();
        shape.panelB.destroy();
        shape.seam.destroy();
        shape.glow.destroy();
      });
      this.doorShapes.clear();
      this.interactableShapes.forEach((shape) => {
        shape.marker.destroy();
        shape.label.destroy();
      });
      this.interactableShapes.clear();
      this.localFixtureObjects.forEach((fixture) => fixture.object.destroy());
      this.localFixtureObjects = [];
      this.localFixtureMaskObjects.forEach((object) => object.destroy?.());
      this.localFixtureMaskObjects = [];
      this.localFixtureMaskSyncObjects = [];
      this.localSurfaceTextureObjects.forEach((object) => object.destroy?.());
      this.localSurfaceTextureObjects = [];
      this.mapInitializedForServer = false;
      this.serverMapSignature = null;
      this.serverNpcFacingById.clear();
    }

    private createServerFixtureObjects(map: MysteryRoomMapDefinition) {
      this.localFixtureObjects.forEach((fixture) => fixture.object.destroy());
      this.localFixtureObjects = [];
      this.localFixtureMaskObjects.forEach((object) => object.destroy?.());
      this.localFixtureMaskObjects = [];
      this.localFixtureMaskSyncObjects = [];

      for (const object of map.customObjects) {
        this.createCustomFixtureObjects(object);
      }
    }

    private updateServerFixtureVisibility(snapshot: MrMatchSnapshot, map: MysteryRoomMapDefinition) {
      const objectsById = new Map(map.customObjects.map((object) => [object.id, object]));
      const usedInteractableIds = new Set(snapshot.usedInteractableIds ?? []);
      const consumedCustomObjectIds = new Set(
        map.interactables
          .filter((entry) => {
            const customObjectId = serverInteractableCustomObjectId(entry);
            return customObjectId && usedInteractableIds.has(entry.id);
          })
          .map((entry) => serverInteractableCustomObjectId(entry) as string),
      );

      for (const fixture of this.localFixtureObjects) {
        const source = fixture.sourceId ? objectsById.get(fixture.sourceId) : null;
        const visible = !source || !consumedCustomObjectIds.has(source.id);
        const labelVisible = fixture.role !== 'label' || !source || source.worldLabelVisibility === 'always';
        fixture.object.setVisible(visible && labelVisible);
      }
    }

    private createRuntimeDoorShape(door: MysteryRoomMapDefinition['doors'][number]): DoorShape {
      const outlineWidth = runtimeOutlineWidth(door, 2);
      const outlineAlpha = runtimeOutlineOpacity(door, 0.65);
      const doorColor =
        door.color ??
        (door.accessMode === 'public' || door.requiredAccess === 'public'
          ? 0x38bdf8
          : door.accessMode === 'npc_only'
            ? 0xa78bfa
            : door.requiredAccess === 'server'
              ? 0xf59e0b
              : door.requiredAccess === 'reactor'
                ? 0xfb7185
                : 0x34d399);
      const frame = this.add.rectangle(
        door.panel.x + door.panel.width / 2,
        door.panel.y + door.panel.height / 2,
        door.panel.width + 6,
        door.panel.height + 6,
        0x0f172a,
        0,
      );
      frame.setStrokeStyle(outlineWidth, door.outlineColor ?? 0xcbd5e1, outlineAlpha);
      frame.setDepth(78);

      const leaves = doorLeafRects(door.panel, door.orientation);
      const panelA = this.add.rectangle(
        leaves.a.x + leaves.a.width / 2,
        leaves.a.y + leaves.a.height / 2,
        leaves.a.width,
        leaves.a.height,
        doorColor,
        runtimeBaseColorOpacity(door, 0.96),
      );
      const panelB = this.add.rectangle(
        leaves.b.x + leaves.b.width / 2,
        leaves.b.y + leaves.b.height / 2,
        leaves.b.width,
        leaves.b.height,
        doorColor,
        runtimeBaseColorOpacity(door, 0.96),
      );
      panelA.setDepth(80);
      panelB.setDepth(80);
      const seam = this.add.rectangle(
        door.panel.x + door.panel.width / 2,
        door.panel.y + door.panel.height / 2,
        door.orientation === 'vertical' ? Math.max(8, door.panel.width - 10) : 5,
        door.orientation === 'vertical' ? 5 : Math.max(8, door.panel.height - 10),
        0xe2e8f0,
        0.64,
      );
      seam.setDepth(81);
      const glow = this.add.ellipse(
        door.panel.x + door.panel.width / 2,
        door.panel.y + door.panel.height / 2,
        door.panel.width * 1.8,
        door.panel.height * 1.8,
        doorColor,
        0.08,
      );
      glow.setDepth(79);

      const shape = {
        frame,
        panelA,
        panelB,
        seam,
        glow,
        baseRect: { ...door.panel },
        orientation: door.orientation,
      };
      applyDoorOpenProgress(shape, 0);
      return shape;
    }

    private estimateServerNowMs(serverNowMs: number) {
      if (!Number.isFinite(serverNowMs) || serverNowMs <= 0) {
        return serverNowMs;
      }
      if (!this.serverClockAnchor || Math.abs(this.serverClockAnchor.serverNowMs - serverNowMs) > 2) {
        this.serverClockAnchor = {
          serverNowMs,
          sceneTimeMs: this.time.now,
        };
      }
      return serverNowMs + Math.max(0, this.time.now - this.serverClockAnchor.sceneTimeMs);
    }

    private updateServerPlayerVision(
      _snapshot: MrMatchSnapshot,
      _map: ReturnType<typeof getMysteryRoomMapDefinition>,
      _playerDrawStates: ServerPlayerDrawState[],
    ) {
      this.serverVisionGraphics?.clear();
    }

    private updateServerDoors(snapshot: MrMatchSnapshot) {
      const doorStateById = new Map((snapshot.doors ?? []).map((door) => [door.id, door]));
      this.doorShapes.forEach((shape, doorId) => {
        const door = doorStateById.get(doorId);
        const locked = door?.locked ?? false;
        const open = door?.open ?? false;
        const tint = locked ? 0xef4444 : open ? 0x38bdf8 : 0x94a3b8;
        const openProgress = open && !locked ? 1 : 0;

        shape.frame.setAlpha(0.88);
        setDoorLeafTint(shape, tint);
        setDoorLeafAlpha(shape, 0.98);
        shape.glow.fillAlpha = locked ? 0.16 : open ? 0.14 : 0.08;
        applyDoorOpenProgress(shape, openProgress);
      });
    }

    private updateServerFlashlightEffects(
      snapshot: MrMatchSnapshot,
      map: ReturnType<typeof getMysteryRoomMapDefinition>,
      playerDrawStates: ServerPlayerDrawState[],
    ) {
      if (!this.serverLightGraphics) {
        return;
      }
      this.serverLightGraphics.clear();
      const playersById = new Map(snapshot.players.map((player) => [player.playerId, player]));
      const blockingSegments = buildSightBlockingSegments(map, buildServerVisibilityDoors(map, snapshot));
      const radius = Math.min(FLASHLIGHT_RADIUS, Math.max(360, map.visibilityRadius * 1.45));

      for (const drawState of playerDrawStates) {
        const player = playersById.get(drawState.playerId);
        if (!player || !this.isServerPlayerFlashlightEnabled(player, drawState.isSelf)) {
          continue;
        }
        const origin = { x: drawState.x, y: drawState.y + HERO_EQUIPMENT_ANCHOR_OFFSET_Y };
        const angle = drawState.isSelf
          ? (this.actorPointerAimAngle(origin, drawState.facing) / 180) * Math.PI
          : facingToAngle(drawState.facing);
        const cone = castVisibilityCone(origin, blockingSegments, radius, angle, FLASHLIGHT_HALF_ANGLE, 44);
        this.serverLightGraphics.fillStyle(0xfffbeb, 0.18);
        this.serverLightGraphics.lineStyle(2, 0xfef3c7, 0.3);
        this.serverLightGraphics.beginPath();
        this.serverLightGraphics.moveTo(origin.x, origin.y);
        cone.forEach((point) => this.serverLightGraphics.lineTo(point.x, point.y));
        this.serverLightGraphics.closePath();
        this.serverLightGraphics.fillPath();
        this.serverLightGraphics.strokePath();
        this.serverLightGraphics.fillStyle(0xfffbeb, 0.72);
        this.serverLightGraphics.fillCircle(origin.x + Math.cos(angle) * 20, origin.y + Math.sin(angle) * 20, 12);
        this.serverLightGraphics.fillStyle(0xfef3c7, 0.12);
        this.serverLightGraphics.fillCircle(origin.x, origin.y, 74);
      }
    }

    private updateServerShotEffects(snapshot: MrMatchSnapshot) {
      if (!this.serverEffectGraphics) {
        return;
      }
      this.serverEffectGraphics.clear();
      const nowMs = this.estimateServerNowMs(adapter.getServerNowMs());
      for (const event of snapshot.publicEvents ?? []) {
        const entry = event as Record<string, unknown>;
        if (entry.type !== 'shoot' && entry.type !== 'hit_confirmed' && entry.type !== 'shot_missed') {
          continue;
        }
        const fromX = Number(entry.fromX);
        const fromY = Number(entry.fromY);
        const toX = Number(entry.toX);
        const toY = Number(entry.toY);
        if (![fromX, fromY, toX, toY].every(Number.isFinite)) {
          continue;
        }
        const ageMs = Math.max(0, nowMs - Number(entry.t ?? nowMs));
        const alpha = Math.max(0, 0.92 * (1 - ageMs / 420));
        if (alpha <= 0) {
          continue;
        }
        this.serverEffectGraphics.lineStyle(3, 0xfef3c7, alpha);
        this.serverEffectGraphics.lineBetween(fromX, fromY, toX, toY);
        this.serverEffectGraphics.fillStyle(0xfffbeb, alpha);
        this.serverEffectGraphics.fillCircle(fromX, fromY, 4);
      }
    }

    private updateServerNpcs(
      snapshot: MrMatchSnapshot,
      map: ReturnType<typeof getMysteryRoomMapDefinition>,
      serverNowMs: number,
      deltaMs: number,
    ) {
      const seen = new Set<string>();
      const blockingSegments = buildSightBlockingSegments(map, buildServerVisibilityDoors(map, snapshot));
      this.npcVisionGraphics?.clear();
      for (const npc of snapshot.npcs ?? []) {
        if (!npc?.npcId) {
          continue;
        }
        seen.add(npc.npcId);
        const shape = this.npcShapes.get(npc.npcId) ?? this.createActorShape(npc.name, npcRoleColor(npc.role), 0xe2e8f0);
        this.npcShapes.set(npc.npcId, shape);
        const moveCommand = this.getServerActorMoveCommand(snapshot, 'npc', npc.npcId);
        const targetPosition = moveCommand
          ? { x: Number(moveCommand.targetX ?? npc.x), y: Number(moveCommand.targetY ?? npc.y) }
          : this.resolveServerNpcCommandTargetPosition(npc, serverNowMs);
        const currentPosition = shape.renderPosition ?? { x: targetPosition.x, y: targetPosition.y };
        const movementActive = moveCommand
          ? this.isServerMoveCommandActive(moveCommand, targetPosition, currentPosition, serverNowMs)
          : this.isServerNpcCommandActive(npc, targetPosition, currentPosition, serverNowMs);
        const drawPosition = mrSmoothNetworkPosition({
          current: currentPosition,
          target: movementActive ? targetPosition : { x: npc.x, y: npc.y },
          speed: Math.max(40, Number(moveCommand?.speed ?? npc.speed ?? 72)),
          dtMs: deltaMs,
          snapDistance: movementActive ? 2.5 : 1.4,
          teleportDistance: 520,
          catchupMultiplier: movementActive ? 1.02 : 1.45,
          minStep: 0.35,
        });
        const movementVector = {
          x: drawPosition.x - currentPosition.x,
          y: drawPosition.y - currentPosition.y,
        };
        shape.previousPosition = currentPosition;
        shape.renderPosition = drawPosition;
        const isMoving = movementActive || Math.hypot(movementVector.x, movementVector.y) > 0.18;
        const facing = this.resolveServerNpcFacing(npc.npcId, drawPosition, shape, movementVector);
        this.drawServerNpcVision(npc, drawPosition, facing, blockingSegments);
        this.syncActorShape(shape, drawPosition, facing, isMoving ? 'walk' : 'idle', npcRoleColor(npc.role), 1, null, movementVector);
        shape.body.setStrokeStyle(2, npc.alertLevel && npc.alertLevel > 0 ? 0xfca5a5 : 0xe2e8f0, 1);
        shape.label.setText(`${npc.name} [${npc.role ?? 'npc'}]`);
      }

      const staleIds: string[] = [];
      this.npcShapes.forEach((_shape, npcId) => {
        if (!seen.has(npcId)) {
          staleIds.push(npcId);
        }
      });
      staleIds.forEach((npcId) => {
        const shape = this.npcShapes.get(npcId);
        shape?.container.destroy();
        this.npcShapes.delete(npcId);
        this.serverNpcFacingById.delete(npcId);
      });
    }

    private resolveServerNpcCommandTargetPosition(
      npc: MrMatchSnapshot['npcs'][number],
      serverNowMs: number,
    ): MysteryRoomVec2 {
      const path = Array.isArray(npc.path) ? npc.path : [];
      const nodeIndex = Number.isFinite(npc.patrolNodeIndex) ? Math.max(0, Math.trunc(npc.patrolNodeIndex ?? 0)) : 0;
      const startedAtMs = Number(npc.startedAtMs ?? 0);
      const etaMs = Number(npc.etaMs ?? 0);
      if (path.length >= 2 && startedAtMs > 0 && etaMs > startedAtMs && serverNowMs <= etaMs + 120) {
        const nextIndex = (nodeIndex + 1) % path.length;
        const target = path[nextIndex];
        if (target) {
          return { x: Number(target[0] ?? npc.x), y: Number(target[1] ?? npc.y) };
        }
      }

      return { x: npc.x, y: npc.y };
    }

    private isServerNpcCommandActive(
      npc: MrMatchSnapshot['npcs'][number],
      target: MysteryRoomVec2,
      position: MysteryRoomVec2,
      serverNowMs: number,
    ) {
      const etaMs = Number(npc.etaMs ?? 0);
      if (!Number.isFinite(etaMs) || etaMs <= 0 || serverNowMs > etaMs + 120) {
        return false;
      }
      return Math.hypot(target.x - position.x, target.y - position.y) > 3;
    }

    private getServerActorMoveCommand(
      snapshot: MrMatchSnapshot,
      actorType: 'player' | 'bot' | 'npc',
      actorId: string,
    ): NonNullable<MrMatchSnapshot['actorCommands']>[number] | null {
      const commands = snapshot.actorCommands ?? [];
      for (let index = commands.length - 1; index >= 0; index -= 1) {
        const command = commands[index];
        if (command?.command === 'move' && command.actorType === actorType && command.actorId === actorId) {
          return command;
        }
      }
      return null;
    }

    private isServerMoveCommandActive(
      command: NonNullable<MrMatchSnapshot['actorCommands']>[number],
      target: MysteryRoomVec2,
      position: MysteryRoomVec2,
      serverNowMs: number,
    ) {
      const etaMs = Number(command.etaMs ?? 0);
      if (!Number.isFinite(etaMs) || etaMs <= 0 || serverNowMs > etaMs + 120) {
        return false;
      }
      return Math.hypot(target.x - position.x, target.y - position.y) > 3;
    }

    private resolveServerNpcFacing(
      npcId: string,
      position: MysteryRoomVec2,
      shape: ActorShape,
      movement?: MysteryRoomVec2,
    ): 'up' | 'down' | 'left' | 'right' {
      const previousFacing = this.serverNpcFacingById.get(npcId) ?? 'down';
      const previousX = shape.previousPosition?.x ?? shape.container.x ?? position.x;
      const previousY = shape.previousPosition?.y ?? shape.container.y ?? position.y;
      const dx = movement?.x ?? position.x - previousX;
      const dy = movement?.y ?? position.y - previousY;
      const distance = Math.hypot(dx, dy);
      const facing = distance >= 0.2 ? facingFromVelocity({ x: dx, y: dy }, previousFacing) : previousFacing;
      this.serverNpcFacingById.set(npcId, facing);
      return facing;
    }

    private drawServerNpcVision(
      npc: MrMatchSnapshot['npcs'][number],
      position: MysteryRoomVec2,
      facing: 'up' | 'down' | 'left' | 'right',
      blockingSegments: MysteryRoomSegment[],
    ) {
      if (!this.npcVisionGraphics) {
        return;
      }
      const conePoints = buildServerNpcVisionOverlayPoints(npc, position, facing, blockingSegments);
      const fillColor = npc.alertLevel && npc.alertLevel > 0 ? 0xfb7185 : npcRoleColor(npc.role);
      this.npcVisionGraphics.fillStyle(fillColor, npc.alertLevel && npc.alertLevel > 0 ? 0.07 : 0.04);
      this.npcVisionGraphics.lineStyle(1, fillColor, npc.alertLevel && npc.alertLevel > 0 ? 0.18 : 0.1);
      this.npcVisionGraphics.beginPath();
      this.npcVisionGraphics.moveTo(conePoints[0].x, conePoints[0].y);
      conePoints.slice(1).forEach((point) => this.npcVisionGraphics.lineTo(point.x, point.y));
      this.npcVisionGraphics.closePath();
      this.npcVisionGraphics.fillPath();
      this.npcVisionGraphics.strokePath();
    }

    private updateServerMarkers(snapshot: MrMatchSnapshot, map: ReturnType<typeof getMysteryRoomMapDefinition>) {
      const seen = new Set<string>();

      for (const markerData of snapshot.visibleMarkers ?? []) {
        const markerId = markerData.interactableId ?? markerData.stepId;
        if (!markerId) {
          continue;
        }
        const interactable = map.interactables.find((entry) => entry.id === markerData.interactableId);
        const runtimeInteractable = interactable ? this.toServerRuntimeInteractable(interactable, snapshot) : null;
        const x = markerData.x ?? interactable?.anchor.x;
        const y = markerData.y ?? interactable?.anchor.y;
        if (typeof x !== 'number' || typeof y !== 'number') {
          continue;
        }
        if (runtimeInteractable?.customObjectId) {
          seen.add(markerId);
          const existing = this.interactableShapes.get(markerId);
          existing?.marker.setVisible(false);
          existing?.label.setVisible(false);
          continue;
        }
        seen.add(markerId);
        const existing = this.interactableShapes.get(markerId);
        const isLightSwitchMarker = runtimeInteractable?.kind === 'light_switch' || markerData.kind === 'light_switch';
        const usesImageMarker = Boolean(runtimeInteractable && usesDefaultPickupImage(runtimeInteractable));
        const marker =
          existing?.marker ??
          (isLightSwitchMarker
            ? this.createLightSwitchMarker(x, y, 40)
            : usesImageMarker && runtimeInteractable
              ? this.createDefaultPickupImageMarker(runtimeInteractable)
              : this.add.circle(x, y, 18, runtimeInteractable ? interactableColor(runtimeInteractable.kind) : serverMarkerColor(markerData.kind), 0.92));
        if (!existing) {
          if (isLightSwitchMarker) {
            this.enableLightSwitchMarkerPointer(marker, 40, (pointer: any) => {
              pointer.event.stopPropagation();
              this.audioSystem?.unlock();
              adapter.onInteractIntent?.(markerId);
            });
          } else if (usesImageMarker) {
            marker.setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 12 });
            marker.on('pointerdown', (pointer: any) => {
              pointer.event.stopPropagation();
              this.audioSystem?.unlock();
              adapter.onInteractIntent?.(markerId);
            });
          } else {
            marker.setInteractive({ useHandCursor: true });
            marker.on('pointerdown', (pointer: any) => {
              pointer.event.stopPropagation();
              this.audioSystem?.unlock();
              adapter.onInteractIntent?.(markerId);
            });
          }
        }
        marker.setPosition(x, y);
        if (isLightSwitchMarker) {
          this.syncLightSwitchMarker(marker, true, true, 0.98);
        } else if (usesImageMarker && runtimeInteractable) {
          const asset = defaultPickupImageAsset(runtimeInteractable);
          const textureKey = defaultPickupImageTextureKey(runtimeInteractable);
          marker.setAlpha(textureKey && this.textures.exists(textureKey) ? 0.98 : 0);
          if (asset) {
            marker.setDisplaySize(asset.display.width * 1.08, asset.display.height * 1.08);
          }
        } else {
          marker.fillColor = runtimeInteractable ? interactableColor(runtimeInteractable.kind) : serverMarkerColor(markerData.kind);
          marker.setStrokeStyle(2, 0xfcd34d, 1);
          marker.setAlpha(0.95);
        }
        const markerDepth = runtimeInteractable ? localLayerDepth(runtimeInteractable.layer, 7) : 135;
        marker.setDepth(markerDepth);
        marker.setVisible(true);

        const label =
          existing?.label ??
          this.add.text(x, y + 22, markerData.label ?? markerId, {
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '11px',
            color: '#e2e8f0',
            backgroundColor: 'rgba(2, 6, 23, 0.82)',
            padding: { x: 6, y: 3 },
          });
        label.setOrigin(0.5, 0);
        label.setPosition(x, y + 22);
        label.setText(markerData.label ?? markerId);
        label.setDepth(markerDepth + 1);
        label.setVisible(!runtimeInteractable || shouldShowInteractableWorldLabel(runtimeInteractable, true));

        if (!existing) {
          this.interactableShapes.set(markerId, { marker, label });
        }
      }

      this.interactableShapes.forEach((shape, markerId) => {
        if (!seen.has(markerId)) {
          shape.marker.setVisible(false);
          shape.label.setVisible(false);
        }
      });
    }

    private toServerRuntimeInteractable(
      interactable: MysteryRoomMapDefinition['interactables'][number],
      snapshot: MrMatchSnapshot,
    ): MysteryRoomLocalRuntime['interactables'][number] {
      const customObjectId = serverInteractableCustomObjectId(interactable);
      return {
        ...interactable,
        anchor: { ...interactable.anchor },
        state: snapshot.usedInteractableIds?.includes(interactable.id) ? 'used' : 'idle',
        source: customObjectId ? 'custom_object' : 'interactable',
        customObjectId,
        pickupKind: serverInteractablePickupKind(interactable),
      };
    }

    private createLocalWorld(map: MysteryRoomLocalRuntime['map']) {
      const nextMapSignature = buildLocalMapSignature(map);
      if (this.mapInitializedForLocal && this.localMapSignature === nextMapSignature) {
        return;
      }
      if (this.mapInitializedForLocal) {
        this.destroyLocalWorld();
      }

      this.configureMapCamera(map, 'local');

      this.localStaticGraphics = this.add.graphics();
      this.localPropShadowGraphics = this.add.graphics();
      this.localPropGraphics = this.add.graphics();
      this.localDarknessGraphics = this.add.graphics();
      this.localLightGraphics = this.add.graphics();
      this.localEffectGraphics = this.add.graphics();
      this.localOccluderGraphics = this.add.graphics();
      this.localWallGraphics = this.add.graphics();
      this.npcVisionGraphics = this.add.graphics();
      this.npcTargetGraphics = this.add.graphics();
      this.localLabelLayer = this.add.layer();

      this.localStaticGraphics.fillStyle(0x050b1a, 1);
      this.localStaticGraphics.fillRect(0, 0, map.width, map.height);

      for (const room of map.rooms) {
        for (const floor of room.floor) {
          this.localStaticGraphics.fillStyle(room.color, runtimeBaseColorOpacity(room, room.id === 'central_cross' ? 0.75 : 0.94));
          this.localStaticGraphics.fillRoundedRect(floor.x, floor.y, floor.width, floor.height, 18);
          this.localStaticGraphics.lineStyle(4, 0xf8fafc, 0.06);
          this.localStaticGraphics.strokeRoundedRect(floor.x, floor.y, floor.width, floor.height, 18);
          if (room.textures?.length) {
            this.createSurfaceTextureLayerImages(room.textures, floor, 7, 0, 'local');
          } else {
            this.createSurfaceTextureImage(room.floorTexture, floor, 6, 0, 'local');
          }
        }

        const label = this.add.text(room.labelPoint.x, room.labelPoint.y, room.title, {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '18px',
          color: '#f8fafc',
          backgroundColor: 'rgba(2, 6, 23, 0.5)',
          padding: { x: 8, y: 5 },
        });
        label.setOrigin(0.5, 0);
        label.setDepth(40);
        this.localLabelLayer.add(label);
      }

      this.localStaticGraphics.lineStyle(2, 0xffffff, 0.04);
      for (let x = 0; x <= map.width; x += 96) {
        this.localStaticGraphics.lineBetween(x, 0, x, map.height);
      }
      for (let y = 0; y <= map.height; y += 96) {
        this.localStaticGraphics.lineBetween(0, y, map.width, y);
      }
      this.drawLocalMapDetails(map);
      this.createLocalFixtureObjects(map);

      this.localOccluderGraphics.fillStyle(0x111827, 0.92);
      this.localOccluderGraphics.lineStyle(2, 0xe2e8f0, 0.08);
      map.occluders.forEach((occluder) => {
        this.localOccluderGraphics.fillRoundedRect(
          occluder.rect.x,
          occluder.rect.y,
          occluder.rect.width,
          occluder.rect.height,
          12,
        );
        this.localOccluderGraphics.strokeRoundedRect(
          occluder.rect.x,
          occluder.rect.y,
          occluder.rect.width,
          occluder.rect.height,
          12,
        );
      });
      this.localOccluderGraphics.setDepth(55);
      this.localPropShadowGraphics.setDepth(58);
      this.localPropGraphics.setDepth(59);
      this.localDarknessGraphics.setDepth(56);
      this.localLightGraphics.setDepth(57);
      this.localLightGraphics.setBlendMode(Phaser.BlendModes.ADD);
      this.localEffectGraphics.setDepth(121);

      const localWallBodyWidth = 14;
      map.walls.forEach((segment) => {
        const bodyWidth = localWallBodyWidth;
        const outlineWidth = runtimeOutlineWidth(segment, 1);
        const outlineAlpha = runtimeOutlineOpacity(segment, 0.52);
        if (outlineWidth > 0 && outlineAlpha > 0) {
          drawWallSegmentBlock(this.localWallGraphics, segment, bodyWidth + outlineWidth * 2, segment.outlineColor ?? 0xe2e8f0, outlineAlpha);
        }
        drawWallSegmentBlock(this.localWallGraphics, segment, bodyWidth, segment.color ?? 0xe2e8f0, runtimeBaseColorOpacity(segment, 0.85));
        if (segment.textures?.length) {
          this.createWallTextureLayerImages(segment, 67, 'local');
        } else {
          this.createWallTextureImage(segment, 66, 'local');
        }
      });
      drawWallJointBlocks(this.localWallGraphics, map.walls, localWallBodyWidth, 0.85);
      this.localWallGraphics.setDepth(65);
      this.npcVisionGraphics.setDepth(68);
      this.npcTargetGraphics.setDepth(69);

      this.ensureLocalLightMaskViewport();

      map.doors.forEach((door) => {
        this.doorShapes.set(door.id, this.createRuntimeDoorShape(door));
      });

      this.mapInitializedForLocal = true;
      this.localMapSignature = nextMapSignature;
    }

    private destroyLocalWorld() {
      this.localStaticGraphics?.destroy();
      this.localStaticGraphics = null;
      this.localPropShadowGraphics?.destroy();
      this.localPropShadowGraphics = null;
      this.localPropGraphics?.destroy();
      this.localPropGraphics = null;
      this.localDarknessGraphics?.destroy();
      this.localDarknessGraphics = null;
      this.localLightGraphics?.destroy();
      this.localLightGraphics = null;
      this.destroyLocalLightMaskResources();
      this.localEffectGraphics?.destroy();
      this.localEffectGraphics = null;
      this.localOccluderGraphics?.destroy();
      this.localOccluderGraphics = null;
      this.localWallGraphics?.destroy();
      this.localWallGraphics = null;
      this.npcVisionGraphics?.destroy();
      this.npcVisionGraphics = null;
      this.npcTargetGraphics?.destroy();
      this.npcTargetGraphics = null;
      this.localLabelLayer?.destroy();
      this.localLabelLayer = null;
      this.heroShape?.container.destroy();
      this.heroShape = null;
      this.latestLocalHeroPosition = null;

      this.npcShapes.forEach((shape) => shape.container.destroy());
      this.npcShapes.clear();
      this.incidentShapes.forEach((shape) => shape.container.destroy());
      this.incidentShapes.clear();
      this.doorShapes.forEach((shape) => {
        shape.frame.destroy();
        shape.panelA.destroy();
        shape.panelB.destroy();
        shape.seam.destroy();
        shape.glow.destroy();
      });
      this.doorShapes.clear();
      this.interactableShapes.forEach((shape) => {
        shape.marker.destroy();
        shape.label.destroy();
      });
      this.interactableShapes.clear();
      this.localFixtureObjects.forEach((fixture) => fixture.object.destroy());
      this.localFixtureObjects = [];
      this.localFixtureMaskObjects.forEach((object) => object.destroy?.());
      this.localFixtureMaskObjects = [];
      this.localFixtureMaskSyncObjects = [];
      this.localSurfaceTextureObjects.forEach((object) => object.destroy?.());
      this.localSurfaceTextureObjects = [];

      this.mapInitializedForLocal = false;
      this.localMapSignature = null;
      this.followsPlayerId = null;
    }

    private drawMapDetailsWithStaticGraphics(map: MysteryRoomLocalRuntime['map'], graphics: any) {
      const previousStaticGraphics = this.localStaticGraphics;
      this.localStaticGraphics = graphics;
      this.drawLocalMapDetails(map);
      this.localStaticGraphics = previousStaticGraphics;
    }

    private drawLocalMapDetails(map: MysteryRoomLocalRuntime['map']) {
      if (!this.localStaticGraphics) {
        return;
      }

      for (const room of map.rooms) {
        for (const floor of room.floor) {
          this.drawFloorPanels(floor, room.id === 'central_cross');
          this.drawRoomWallTrim(floor, room.id);
        }
      }
    }

    private drawFloorPanels(floor: MysteryRoomRect, isCorridor: boolean) {
      if (!this.localStaticGraphics) {
        return;
      }

      const panel = isCorridor ? 96 : 128;
      this.localStaticGraphics.lineStyle(1, isCorridor ? 0x94a3b8 : 0x475569, isCorridor ? 0.14 : 0.12);
      for (let x = floor.x + panel; x < floor.x + floor.width; x += panel) {
        this.localStaticGraphics.lineBetween(x, floor.y + 10, x, floor.y + floor.height - 10);
      }
      for (let y = floor.y + panel; y < floor.y + floor.height; y += panel) {
        this.localStaticGraphics.lineBetween(floor.x + 10, y, floor.x + floor.width - 10, y);
      }

      this.localStaticGraphics.lineStyle(2, 0x020617, 0.22);
      this.localStaticGraphics.strokeRoundedRect(floor.x + 12, floor.y + 12, floor.width - 24, floor.height - 24, 14);
      this.localStaticGraphics.fillStyle(0xffffff, isCorridor ? 0.035 : 0.05);
      this.localStaticGraphics.fillRoundedRect(floor.x + 18, floor.y + 18, floor.width - 36, 16, 6);
    }

    private drawRoomWallTrim(floor: MysteryRoomRect, roomId: string) {
      if (!this.localStaticGraphics) {
        return;
      }

      const trimColor = roomId === 'reactor_room' ? 0xfb7185 : roomId === 'server_room' ? 0x60a5fa : 0xe2e8f0;
      this.localStaticGraphics.lineStyle(8, 0x020617, 0.24);
      this.localStaticGraphics.strokeRoundedRect(floor.x - 3, floor.y - 3, floor.width + 6, floor.height + 6, 20);
      this.localStaticGraphics.lineStyle(3, trimColor, roomId === 'central_cross' ? 0.16 : 0.24);
      this.localStaticGraphics.strokeRoundedRect(floor.x + 5, floor.y + 5, floor.width - 10, floor.height - 10, 15);
    }

    private drawRoomFixtures(roomId: string, floor: MysteryRoomRect) {
      if (!this.localStaticGraphics) {
        return;
      }

      if (roomId === 'airlock_entry') {
        this.drawFixturePanel(floor.x + floor.width * 0.53, floor.y + floor.height * 0.1, floor.width * 0.33, floor.height * 0.13, 0x334155, 0xe2e8f0);
        this.drawFixtureCrates(floor.x + floor.width * 0.1, floor.y + floor.height * 0.12, floor.width * 0.25, floor.height * 0.2, 0x475569, 0xf59e0b);
        this.drawFixtureConsole(floor.x + floor.width * 0.22, floor.y + floor.height * 0.58, floor.width * 0.12, floor.height * 0.28, 0x111827, 0x38bdf8);
        this.drawFixtureGrate(floor.x + floor.width * 0.4, floor.y + floor.height * 0.62, floor.width * 0.34, floor.height * 0.18, 0x0f172a, 0x94a3b8);
        return;
      }

      if (roomId === 'research_lab') {
        this.drawFixturePanel(floor.x + floor.width * 0.12, floor.y + floor.height * 0.17, floor.width * 0.38, floor.height * 0.1, 0xe5e7eb, 0x38bdf8);
        this.drawFixturePanel(floor.x + floor.width * 0.36, floor.y + floor.height * 0.54, floor.width * 0.38, floor.height * 0.12, 0xf8fafc, 0x60a5fa);
        this.drawFixtureConsole(floor.x + floor.width * 0.76, floor.y + floor.height * 0.18, floor.width * 0.08, floor.height * 0.22, 0x0f766e, 0x99f6e4);
        this.drawFixtureInstrument(floor.x + floor.width * 0.23, floor.y + floor.height * 0.38, floor.width * 0.08);
        this.drawFixtureInstrument(floor.x + floor.width * 0.64, floor.y + floor.height * 0.4, floor.width * 0.08);
        return;
      }

      if (roomId === 'med_bay') {
        this.drawFixtureMedicalBed(floor.x + floor.width * 0.43, floor.y + floor.height * 0.34, floor.width * 0.23, floor.height * 0.22);
        this.drawFixtureConsole(floor.x + floor.width * 0.22, floor.y + floor.height * 0.46, floor.width * 0.08, floor.height * 0.22, 0x111827, 0x22d3ee);
        this.drawFixtureConsole(floor.x + floor.width * 0.76, floor.y + floor.height * 0.38, floor.width * 0.08, floor.height * 0.22, 0x111827, 0xfb7185);
        this.drawFixtureGrate(floor.x + floor.width * 0.34, floor.y + floor.height * 0.72, floor.width * 0.34, floor.height * 0.11, 0x0f172a, 0x94a3b8);
        return;
      }

      if (roomId === 'server_room') {
        for (let index = 0; index < 3; index += 1) {
          this.drawFixtureRack(floor.x + floor.width * (0.16 + index * 0.19), floor.y + floor.height * 0.16, floor.width * 0.12, floor.height * 0.56, index === 2 ? 0xef4444 : 0x38bdf8);
        }
        this.drawFixtureConsole(floor.x + floor.width * 0.6, floor.y + floor.height * 0.74, floor.width * 0.26, floor.height * 0.13, 0x1f2937, 0x67e8f9);
        this.drawFixtureGrate(floor.x + floor.width * 0.25, floor.y + floor.height * 0.72, floor.width * 0.2, floor.height * 0.13, 0x020617, 0x475569);
        return;
      }

      if (roomId === 'reactor_room') {
        this.drawFixtureReactor(floor.x + floor.width * 0.42, floor.y + floor.height * 0.22, floor.width * 0.35, floor.height * 0.42);
        this.drawFixtureTurbine(floor.x + floor.width * 0.18, floor.y + floor.height * 0.42, Math.min(floor.width, floor.height) * 0.16);
        this.drawFixturePipe(floor.x + floor.width * 0.73, floor.y + floor.height * 0.25, floor.width * 0.2, floor.height * 0.1, true);
        this.drawFixturePipe(floor.x + floor.width * 0.8, floor.y + floor.height * 0.35, floor.width * 0.08, floor.height * 0.48, false);
        return;
      }

      if (roomId === 'central_cross') {
        this.drawFixtureGrate(floor.x + floor.width * 0.08, floor.y + floor.height * 0.32, floor.width * 0.18, floor.height * 0.22, 0x0f172a, 0x475569);
        this.drawFixtureGrate(floor.x + floor.width * 0.62, floor.y + floor.height * 0.32, floor.width * 0.16, floor.height * 0.22, 0x0f172a, 0x475569);
      }
    }

    private drawFixturePanel(x: number, y: number, width: number, height: number, base: number, accent: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      g.fillStyle(0x020617, 0.2);
      g.fillRoundedRect(x + 8, y + 10, width, height, 8);
      g.fillStyle(base, 0.98);
      g.fillRoundedRect(x, y, width, height, 8);
      g.lineStyle(3, accent, 0.72);
      g.strokeRoundedRect(x + 5, y + 5, width - 10, height - 10, 5);
    }

    private drawFixtureCrates(x: number, y: number, width: number, height: number, base: number, accent: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      const crateWidth = width / 2 - 6;
      const crateHeight = height / 2 - 6;
      for (let row = 0; row < 2; row += 1) {
        for (let col = 0; col < 2; col += 1) {
          const crateX = x + col * (crateWidth + 12);
          const crateY = y + row * (crateHeight + 12);
          g.fillStyle(base, 0.98);
          g.fillRoundedRect(crateX, crateY, crateWidth, crateHeight, 5);
          g.lineStyle(3, accent, 0.82);
          g.strokeRoundedRect(crateX + 5, crateY + 5, crateWidth - 10, crateHeight - 10, 4);
        }
      }
    }

    private drawFixtureConsole(x: number, y: number, width: number, height: number, base: number, accent: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      g.fillStyle(0x020617, 0.25);
      g.fillRoundedRect(x + 8, y + 10, width, height, 8);
      g.fillStyle(base, 0.98);
      g.fillRoundedRect(x, y, width, height, 8);
      g.fillStyle(accent, 0.96);
      g.fillRoundedRect(x + width * 0.16, y + height * 0.12, width * 0.68, height * 0.35, 5);
      g.fillStyle(0xe2e8f0, 0.55);
      g.fillCircle(x + width * 0.5, y + height * 0.72, Math.min(width, height) * 0.1);
    }

    private drawFixtureGrate(x: number, y: number, width: number, height: number, base: number, accent: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      g.fillStyle(base, 0.9);
      g.fillRoundedRect(x, y, width, height, 6);
      g.lineStyle(3, accent, 0.62);
      for (let lineX = x + 18; lineX < x + width; lineX += 28) {
        g.lineBetween(lineX, y + 8, lineX, y + height - 8);
      }
      for (let lineY = y + 18; lineY < y + height; lineY += 28) {
        g.lineBetween(x + 8, lineY, x + width - 8, lineY);
      }
    }

    private drawFixtureInstrument(x: number, y: number, size: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      const radius = size * 0.36;
      g.fillStyle(0x111827, 0.45);
      g.fillEllipse(x + size * 0.5, y + size * 0.7, size * 0.75, size * 0.22);
      g.lineStyle(Math.max(4, size * 0.08), 0x64748b, 0.9);
      g.lineBetween(x + size * 0.32, y + size * 0.62, x + size * 0.62, y + size * 0.32);
      g.fillStyle(0xe2e8f0, 0.86);
      g.fillRoundedRect(x + size * 0.28, y + size * 0.56, size * 0.42, size * 0.18, 4);
      g.fillStyle(0x67e8f9, 0.78);
      g.fillCircle(x + size * 0.68, y + size * 0.28, radius * 0.34);
    }

    private drawFixtureMedicalBed(x: number, y: number, width: number, height: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      g.fillStyle(0x020617, 0.24);
      g.fillRoundedRect(x + 10, y + 12, width, height, 14);
      g.fillStyle(0x334155, 0.88);
      g.fillRoundedRect(x, y, width, height, 14);
      g.fillStyle(0xcbd5e1, 0.9);
      g.fillRoundedRect(x + width * 0.1, y + height * 0.12, width * 0.8, height * 0.76, 12);
      g.fillStyle(0x60a5fa, 0.58);
      g.fillRoundedRect(x + width * 0.18, y + height * 0.18, width * 0.64, height * 0.3, 8);
      g.lineStyle(3, 0xf8fafc, 0.42);
      g.strokeRoundedRect(x + width * 0.05, y + height * 0.06, width * 0.9, height * 0.88, 14);
    }

    private drawFixtureRack(x: number, y: number, width: number, height: number, accent: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      g.fillStyle(0x020617, 0.82);
      g.fillRoundedRect(x, y, width, height, 7);
      g.lineStyle(2, 0x64748b, 0.32);
      g.strokeRoundedRect(x, y, width, height, 7);
      const rows = 7;
      for (let row = 0; row < rows; row += 1) {
        const rowY = y + height * 0.08 + row * ((height * 0.82) / rows);
        g.fillStyle(0x111827, 0.86);
        g.fillRoundedRect(x + width * 0.1, rowY, width * 0.8, height * 0.055, 3);
        g.fillStyle(row % 3 === 0 ? 0xef4444 : accent, 0.86);
        g.fillRect(x + width * 0.66, rowY + height * 0.018, width * 0.18, 3);
      }
    }

    private drawFixtureReactor(x: number, y: number, width: number, height: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      const cx = x + width * 0.5;
      const cy = y + height * 0.5;
      const radius = Math.min(width, height) * 0.34;
      g.fillStyle(0x111827, 0.9);
      g.fillRoundedRect(x, y, width, height, 18);
      g.lineStyle(5, 0x94a3b8, 0.3);
      g.strokeRoundedRect(x + 8, y + 8, width - 16, height - 16, 14);
      g.fillStyle(0x020617, 0.85);
      g.fillCircle(cx, cy, radius);
      g.lineStyle(5, 0xfb7185, 0.72);
      g.strokeCircle(cx, cy, radius * 0.72);
      g.fillStyle(0xfb7185, 0.2);
      g.fillCircle(cx, cy, radius * 0.95);
    }

    private drawFixtureTurbine(cx: number, cy: number, radius: number) {
      const g = this.localStaticGraphics;
      if (!g) return;
      g.fillStyle(0x334155, 0.86);
      g.fillCircle(cx, cy, radius);
      g.lineStyle(4, 0x94a3b8, 0.42);
      g.strokeCircle(cx, cy, radius * 0.86);
      for (let index = 0; index < 6; index += 1) {
        const angle = (Math.PI * 2 * index) / 6;
        g.fillStyle(0x0f172a, 0.72);
        g.fillTriangle(
          cx + Math.cos(angle - 0.18) * radius * 0.24,
          cy + Math.sin(angle - 0.18) * radius * 0.24,
          cx + Math.cos(angle + 0.18) * radius * 0.24,
          cy + Math.sin(angle + 0.18) * radius * 0.24,
          cx + Math.cos(angle) * radius * 0.78,
          cy + Math.sin(angle) * radius * 0.78,
        );
      }
      g.fillStyle(0xf97316, 0.7);
      g.fillCircle(cx, cy, radius * 0.15);
    }

    private drawFixturePipe(x: number, y: number, width: number, height: number, horizontal: boolean) {
      const g = this.localStaticGraphics;
      if (!g) return;
      const lines = 3;
      for (let index = 0; index < lines; index += 1) {
        const offset = (index + 0.5) / lines;
        g.lineStyle(Math.max(8, (horizontal ? height : width) / 5), 0x111827, 0.9);
        if (horizontal) {
          const lineY = y + height * offset;
          g.lineBetween(x, lineY, x + width, lineY);
          g.lineStyle(2, 0xfb7185, 0.35);
          g.lineBetween(x + 12, lineY - 5, x + width - 12, lineY - 5);
        } else {
          const lineX = x + width * offset;
          g.lineBetween(lineX, y, lineX, y + height);
          g.lineStyle(2, 0xfb7185, 0.35);
          g.lineBetween(lineX - 5, y + 12, lineX - 5, y + height - 12);
        }
      }
    }

    private createLocalFixtureObjects(map: MysteryRoomLocalRuntime['map']) {
      this.localFixtureObjects.forEach((fixture) => fixture.object.destroy());
      this.localFixtureObjects = [];
      this.localFixtureMaskObjects.forEach((object) => object.destroy?.());
      this.localFixtureMaskObjects = [];
      this.localFixtureMaskSyncObjects = [];

      for (const object of map.customObjects) {
        this.createCustomFixtureObjects(object);
      }
    }

    private registerLocalFixtureObject(
      object: any,
      rect: MysteryRoomRect,
      sourceId?: string,
      syncRect: MysteryRoomRect = rect,
      stableTexture = false,
      role?: 'label',
    ) {
      const center = rectCenter(syncRect);
      const offset =
        sourceId && typeof object.x === 'number' && typeof object.y === 'number'
          ? { x: object.x - center.x, y: object.y - center.y }
          : undefined;
      if (stableTexture && typeof object.setPosition === 'function' && typeof object.x === 'number' && typeof object.y === 'number') {
        const stablePoint = this.stableTexturePoint(object.x, object.y);
        object.setPosition(stablePoint.x, stablePoint.y);
      }
      this.localFixtureObjects.push({ object, rect, sourceId, offset, stableTexture, role });
      return object;
    }

    private syncLocalDynamicFixtures(map: MysteryRoomLocalRuntime['map']) {
      const objectsById = new Map(map.customObjects.map((object) => [object.id, object]));
      for (const fixture of this.localFixtureObjects) {
        if (!fixture.sourceId || !fixture.offset || typeof fixture.object.setPosition !== 'function') {
          continue;
        }
        const source = objectsById.get(fixture.sourceId);
        if (!source) {
          continue;
        }
        const center = rectCenter(source.rect);
        const nextX = center.x + fixture.offset.x;
        const nextY = center.y + fixture.offset.y;
        const nextPoint = fixture.stableTexture ? this.stableTexturePoint(nextX, nextY) : { x: nextX, y: nextY };
        fixture.object.setPosition(nextPoint.x, nextPoint.y);
        fixture.rect = source.rect;
      }
      for (const fixture of this.localFixtureMaskSyncObjects) {
        if (!fixture.sourceId || typeof fixture.object.setPosition !== 'function') {
          continue;
        }
        const source = objectsById.get(fixture.sourceId);
        if (!source) {
          continue;
        }
        const center = rectCenter(source.rect);
        const nextX = center.x + fixture.offset.x;
        const nextY = center.y + fixture.offset.y;
        const nextPoint = fixture.stableTexture ? this.stableTexturePoint(nextX, nextY) : { x: nextX, y: nextY };
        fixture.object.setPosition(nextPoint.x, nextPoint.y);
        fixture.object.setRotation?.(((source.rotation ?? 0) / 180) * Math.PI);
      }
    }

    private updateLocalFixtureVisibility(runtime: MysteryRoomLocalRuntime) {
      const objectsById = new Map(runtime.map.customObjects.map((object) => [object.id, object]));
      const consumedPickupObjectIds = new Set(
        runtime.interactables
          .filter((entry) => entry.source === 'custom_object' && entry.kind === 'pickup' && entry.state === 'used' && entry.customObjectId)
          .map((entry) => entry.customObjectId as string),
      );
      for (const fixture of this.localFixtureObjects) {
        const source = fixture.sourceId ? objectsById.get(fixture.sourceId) : null;
        const visible =
          !source || !consumedPickupObjectIds.has(source.id)
            ? isRectVisible(runtime.visibility, fixture.rect, source?.visibleThroughWalls ? { requireLineOfSight: false } : undefined)
            : false;
        fixture.object.setVisible(visible && (fixture.role !== 'label' || !source || shouldShowCustomObjectWorldLabel(runtime, source)));
      }
    }

    private createPropFixtureObjects(prop: MysteryRoomPropDefinition) {
      const rect = prop.rect;
      const base = prop.color ?? 0x334155;
      const accent = prop.accentColor ?? 0x38bdf8;
      const shadow = this.add.rectangle(
        rect.x + rect.width / 2 + 8,
        rect.y + rect.height / 2 + 10,
        rect.width,
        rect.height,
        0x020617,
        prop.kind === 'floor_grate' || prop.kind === 'warning_stripe' ? 0.16 : 0.32,
      );
      shadow.setDepth(191);
      this.registerLocalFixtureObject(shadow, rect);

      if (prop.kind === 'turbine') {
        this.addFixtureCircle(rect.x + rect.width / 2, rect.y + rect.height / 2, Math.min(rect.width, rect.height) / 2, base, accent, rect);
        return;
      }

      if (prop.kind === 'reactor_core') {
        const core = this.addFixtureRect(rect, base, 0.94, accent, 5, 193);
        const coreGlow = this.add.circle(rect.x + rect.width * 0.48, rect.y + rect.height * 0.52, Math.min(rect.width, rect.height) * 0.24, accent, 0.22);
        coreGlow.setStrokeStyle(5, accent, 0.65);
        coreGlow.setDepth(194);
        this.registerLocalFixtureObject(core, rect);
        this.registerLocalFixtureObject(coreGlow, rect);
        return;
      }

      if (prop.kind === 'floor_grate') {
        const grate = this.addFixtureRect(rect, 0x020617, 0.9, accent, 2, 193);
        this.registerLocalFixtureObject(grate, rect);
        for (let x = rect.x + 18; x < rect.x + rect.width; x += 32) {
          const line = this.add.line(0, 0, x, rect.y + 8, x, rect.y + rect.height - 8, accent, 0.28);
          line.setOrigin(0);
          line.setDepth(194);
          this.registerLocalFixtureObject(line, rect);
        }
        return;
      }

      if (prop.kind === 'warning_stripe') {
        const stripe = this.addFixtureRect(rect, 0x111827, 0.92, accent, 2, 193);
        this.registerLocalFixtureObject(stripe, rect);
        for (let x = rect.x; x < rect.x + rect.width; x += Math.max(20, rect.height * 1.4)) {
          const line = this.add.line(0, 0, x, rect.y + rect.height, x + rect.height, rect.y, accent, 0.78);
          line.setOrigin(0);
          line.setDepth(194);
          line.setLineWidth(Math.max(3, rect.height * 0.24), Math.max(3, rect.height * 0.24));
          this.registerLocalFixtureObject(line, rect);
        }
        return;
      }

      const body = this.addFixtureRect(rect, base, prop.kind === 'lab_bench' || prop.kind === 'medical_bed' ? 0.96 : 0.94, accent, 3, 193);
      this.registerLocalFixtureObject(body, rect);

      if (
        prop.kind === 'airlock_console' ||
        prop.kind === 'wall_terminal' ||
        prop.kind === 'server_console' ||
        prop.kind === 'med_monitor' ||
        prop.kind === 'sample_tank'
      ) {
        const screen = this.add.rectangle(rect.x + rect.width * 0.5, rect.y + rect.height * 0.32, rect.width * 0.58, rect.height * 0.24, accent, 0.82);
        screen.setDepth(194);
        this.registerLocalFixtureObject(screen, rect);
      }

      if (prop.kind === 'server_rack') {
        for (let row = 0; row < 7; row += 1) {
          const rowY = rect.y + rect.height * 0.1 + row * ((rect.height * 0.78) / 7);
          const slot = this.add.rectangle(rect.x + rect.width * 0.5, rowY, rect.width * 0.78, Math.max(8, rect.height * 0.045), 0x020617, 0.9);
          const led = this.add.rectangle(rect.x + rect.width * 0.73, rowY, rect.width * 0.16, 4, row % 3 === 0 ? 0xef4444 : accent, 0.9);
          slot.setDepth(194);
          led.setDepth(195);
          this.registerLocalFixtureObject(slot, rect);
          this.registerLocalFixtureObject(led, rect);
        }
      }

      if (prop.kind === 'locker') {
        for (let index = 1; index < 4; index += 1) {
          const x = rect.x + (rect.width / 4) * index;
          const line = this.add.line(0, 0, x, rect.y + 8, x, rect.y + rect.height - 8, 0x020617, 0.44);
          line.setOrigin(0);
          line.setDepth(194);
          this.registerLocalFixtureObject(line, rect);
        }
      }
    }

    private createCustomFixtureObjects(object: MysteryRoomCustomObjectDefinition) {
      const rect = object.rect;
      const base = object.color ?? 0x38bdf8;
      const accent = object.accentColor ?? 0xf8fafc;
      const angle = object.rotation ?? 0;
      const depthBase = localCustomObjectLayerDepth(object.layer, object.kind === 'badge' || object.kind === 'weapon' ? 5 : 2);
      const hasCollision = object.collision.kind !== 'none' && (object.collisionEnabled ?? true);
      const assetVisual = getMysteryRoomEffectiveCustomObjectAsset(object);
      const hasAssetVisual = Boolean(assetVisual);
      const hasTextureVisual = hasMysteryRoomCustomObjectTextureSkin(object);
      const hasExternalVisual = hasAssetVisual || hasTextureVisual;
      const colorAlpha = 0;
      const outlineColor = object.outlineColor ?? (object.blocksSight ? 0xfb7185 : accent);
      const outlineAlpha = 0;
      const outlineWidth = 0;
      const shadowAlpha = colorAlpha <= 0 ? 0 : hasCollision ? Math.min(0.3, Math.max(0.12, colorAlpha * 0.35)) : Math.min(0.18, Math.max(0.08, colorAlpha * 0.24));
      const shadow = this.add.rectangle(
        rect.x + rect.width / 2 + 8,
        rect.y + rect.height / 2 + 10,
        rect.width,
        rect.height,
        0x020617,
        shadowAlpha,
      );
      shadow.setAngle(angle);
      shadow.setDepth(depthBase - 5);
      this.registerLocalFixtureObject(shadow, rect, object.id);

      const body = object.shape?.kind === 'polygon'
        ? this.createCustomObjectShapeVisual(object, base, colorAlpha, outlineColor, outlineWidth, outlineAlpha)
        : this.add.rectangle(
            rect.x + rect.width / 2,
            rect.y + rect.height / 2,
            rect.width,
            rect.height,
            base,
            colorAlpha,
          );
      if ('setStrokeStyle' in body) {
        body.setStrokeStyle(outlineWidth, outlineColor, outlineAlpha);
      }
      body.setAngle?.(angle);
      body.setDepth(depthBase);
      this.registerLocalFixtureObject(body, rect, object.id);
      this.enableCustomObjectFixtureInteraction(body, object);

      if (assetVisual) {
        const assetObject = this.createCustomObjectAssetVisual(object, depthBase + 1, assetVisual);
        if (assetObject) {
          this.registerLocalFixtureObject(assetObject, rect, object.id, rect, true);
          this.enableCustomObjectFixtureInteraction(assetObject, object);
        }
      } else if (object.textures?.length) {
        this.createCustomObjectTextureLayerVisuals(object, depthBase + 1);
      }

      const proceduralOverlay = !hasExternalVisual
        ? this.createCustomResourceOverlayObject(object, depthBase + 1.5) ?? this.createCustomItemOverlayObject(object, depthBase + 1.5)
        : null;
      if (proceduralOverlay) {
        this.registerLocalFixtureObject(proceduralOverlay, rect, object.id, rect);
        this.enableCustomObjectFixtureInteraction(proceduralOverlay, object);
      }

      if (!hasExternalVisual && !proceduralOverlay) {
        const fallbackDot = this.add.circle(
          rect.x + rect.width / 2,
          rect.y + rect.height / 2,
          Math.max(4, Math.min(8, Math.min(rect.width, rect.height) * 0.12)),
          base,
          0.72,
        );
        fallbackDot.setStrokeStyle(2, outlineColor, 0.72);
        fallbackDot.setDepth(depthBase + 1.4);
        this.registerLocalFixtureObject(fallbackDot, rect, object.id, rect);
      }

      if (object.label) {
        const labelRect = {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height + 28,
        };
        const label = this.add.text(rect.x + rect.width / 2, rect.y + rect.height + 10, object.label, {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '11px',
          color: '#e2e8f0',
          backgroundColor: 'rgba(2, 6, 23, 0.82)',
          padding: { x: 6, y: 3 },
        });
        label.setOrigin(0.5, 0);
        label.setDepth(depthBase + 2);
        this.registerLocalFixtureObject(label, labelRect, object.id, rect, false, 'label');
      }
    }

    private enableCustomObjectFixtureInteraction(gameObject: any, object: MysteryRoomCustomObjectDefinition) {
      if (!object.interactive || typeof gameObject?.setInteractive !== 'function') {
        return;
      }
      gameObject.setInteractive(
        new Phaser.Geom.Rectangle(-object.rect.width / 2, -object.rect.height / 2, object.rect.width, object.rect.height),
        Phaser.Geom.Rectangle.Contains,
      );
      if (gameObject.input) {
        gameObject.input.cursor = 'pointer';
      }
      gameObject.on?.('pointerdown', (pointer: any) => {
        pointer.event?.stopPropagation?.();
        this.audioSystem?.unlock();
        if (adapter.getMode() === 'local') {
          const runtime = adapter.getLocalRuntime?.();
          const interactableId =
            runtime?.interactables.find((entry) => entry.customObjectId === object.id)?.id ?? object.id;
          adapter.onLocalInteract?.(interactableId);
          return;
        }
        const map = this.resolveServerMapDefinition(adapter.getSnapshot());
        const interactableId =
          map.interactables.find((entry) => serverInteractableCustomObjectId(entry) === object.id)?.id ?? object.id;
        adapter.onInteractIntent?.(interactableId);
      });
    }

    private createCustomResourceOverlayObject(object: MysteryRoomCustomObjectDefinition, depth: number) {
      const rect = object.rect;
      const center = rectCenter(rect);
      const tagSet = new Set(object.tags ?? []);
      const sourceKind: MysteryRoomResourceSourceKind | undefined = object.resourceSourceKind
        ?? (tagSet.has('bush')
          ? 'bush'
          : tagSet.has('scrap')
            ? 'scrap'
            : tagSet.has('rock')
              ? 'rock'
              : undefined);
      if (!sourceKind || !MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS[sourceKind]) {
        return null;
      }

      const source = MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS[sourceKind];
      const children: any[] = [];
      if (sourceKind === 'bush') {
        const leaves = [
          [-0.22, 0.02, 0.28],
          [0.04, -0.12, 0.34],
          [0.28, 0.06, 0.25],
          [-0.02, 0.18, 0.26],
        ] as const;
        const trunk = this.add.rectangle(-rect.width * 0.04, rect.height * 0.18, rect.width * 0.12, rect.height * 0.28, 0x854d0e, 0.88);
        children.push(trunk);
        for (const [x, y, radius] of leaves) {
          const leaf = this.add.circle(x * rect.width, y * rect.height, Math.max(8, Math.min(rect.width, rect.height) * radius), source.color, 0.92);
          leaf.setStrokeStyle(2, source.accentColor, 0.38);
          children.push(leaf);
        }
      } else if (sourceKind === 'scrap') {
        const plateA = this.add.rectangle(-rect.width * 0.12, -rect.height * 0.04, rect.width * 0.58, rect.height * 0.24, 0x64748b, 0.94);
        const plateB = this.add.rectangle(rect.width * 0.12, rect.height * 0.12, rect.width * 0.52, rect.height * 0.2, 0x334155, 0.92);
        const rod = this.add.rectangle(0, -rect.height * 0.18, rect.width * 0.82, Math.max(4, rect.height * 0.08), 0xf59e0b, 0.82);
        plateA.setAngle(-13);
        plateB.setAngle(15);
        rod.setAngle(24);
        plateA.setStrokeStyle(2, 0xcbd5e1, 0.42);
        plateB.setStrokeStyle(2, 0x94a3b8, 0.36);
        children.push(plateA, plateB, rod);
      } else {
        const rocks = [
          [-0.24, 0.1, 0.23],
          [0.04, -0.04, 0.28],
          [0.24, 0.14, 0.2],
        ] as const;
        for (const [x, y, radius] of rocks) {
          const rock = this.add.circle(x * rect.width, y * rect.height, Math.max(8, Math.min(rect.width, rect.height) * radius), source.color, 0.94);
          rock.setScale(1.18, 0.82);
          rock.setStrokeStyle(2, source.accentColor, 0.42);
          children.push(rock);
        }
      }

      const container = this.add.container(center.x, center.y, children);
      container.setSize(rect.width, rect.height);
      container.setAngle(object.rotation ?? 0);
      container.setDepth(depth);
      return container;
    }

    private createCustomItemOverlayObject(object: MysteryRoomCustomObjectDefinition, depth: number) {
      const rect = object.rect;
      const center = rectCenter(rect);
      const tagSet = new Set(object.tags ?? []);
      const isFlashlight = object.itemId === 'flashlight' || tagSet.has('flashlight');
      const isPistol = object.itemId === 'pistol' || tagSet.has('pistol');
      const isBadge = object.kind === 'badge' || object.pickupKind === 'access_card' || tagSet.has('access_card');

      if (!isFlashlight && !isPistol && !isBadge) {
        return null;
      }

      const children: any[] = [];
      if (isFlashlight) {
        const body = this.add.rectangle(-rect.width * 0.08, 0, rect.width * 0.58, rect.height * 0.34, 0xfacc15, 0.96);
        const head = this.add.triangle(
          rect.width * 0.24,
          0,
          0,
          -rect.height * 0.34,
          rect.width * 0.28,
          0,
          0,
          rect.height * 0.34,
          0xfef3c7,
          0.92,
        );
        const lens = this.add.circle(rect.width * 0.34, 0, Math.max(4, Math.min(rect.width, rect.height) * 0.14), 0xfffbeb, 0.98);
        body.setStrokeStyle(2, 0xfef3c7, 0.8);
        head.setStrokeStyle(2, 0xfef3c7, 0.62);
        children.push(body, head, lens);
      } else if (isPistol) {
        const slide = this.add.rectangle(-rect.width * 0.08, -rect.height * 0.16, rect.width * 0.64, rect.height * 0.22, 0x111827, 0.98);
        const barrel = this.add.rectangle(rect.width * 0.28, -rect.height * 0.2, rect.width * 0.24, rect.height * 0.12, 0x020617, 0.98);
        const grip = this.add.rectangle(-rect.width * 0.1, rect.height * 0.22, rect.width * 0.2, rect.height * 0.54, 0x111827, 0.98);
        const guard = this.add.circle(rect.width * 0.09, rect.height * 0.08, Math.max(3, rect.height * 0.1), 0xfacc15, 0.82);
        slide.setStrokeStyle(2, 0xfacc15, 0.72);
        barrel.setStrokeStyle(1, 0xfacc15, 0.42);
        grip.setStrokeStyle(2, 0xfacc15, 0.54);
        grip.setAngle(-10);
        children.push(slide, barrel, grip, guard);
      } else {
        const card = this.add.rectangle(0, 0, rect.width * 0.64, rect.height * 0.84, 0x22d3ee, 0.94);
        const chip = this.add.rectangle(-rect.width * 0.08, -rect.height * 0.08, rect.width * 0.18, rect.height * 0.2, 0xf8fafc, 0.88);
        const strip = this.add.rectangle(0, rect.height * 0.22, rect.width * 0.38, Math.max(3, rect.height * 0.08), 0x020617, 0.58);
        card.setStrokeStyle(2, 0xf8fafc, 0.78);
        children.push(card, chip, strip);
      }

      const container = this.add.container(center.x, center.y, children);
      container.setSize(rect.width, rect.height);
      container.setAngle(object.rotation ?? 0);
      container.setDepth(depth);
      return container;
    }

    private createCustomObjectShapeVisual(
      object: MysteryRoomCustomObjectDefinition,
      fill: number,
      alpha: number,
      stroke: number,
      strokeWidth: number,
      strokeAlpha: number,
    ) {
      const rect = object.rect;
      const center = rectCenter(rect);
      const points = object.shape?.kind === 'polygon' ? object.shape.points : [];
      const graphics = this.add.graphics();
      graphics.setPosition(center.x, center.y);
      const drawFill = alpha > 0;
      const drawStroke = strokeWidth > 0 && strokeAlpha > 0;
      if (points.length === 0) {
        if (drawFill) {
          graphics.fillStyle(fill, alpha);
          graphics.fillRect(-rect.width / 2, -rect.height / 2, rect.width, rect.height);
        }
        if (drawStroke) {
          graphics.lineStyle(strokeWidth, stroke, strokeAlpha);
          graphics.strokeRect(-rect.width / 2, -rect.height / 2, rect.width, rect.height);
        }
        return graphics;
      }
      if (points.length === 1) {
        const point = points[0];
        const radius = Math.max(10, Math.min(rect.width, rect.height) * 0.16);
        if (drawFill) {
          graphics.fillStyle(fill, alpha);
          graphics.fillCircle(point.x * rect.width, point.y * rect.height, radius);
        }
        if (drawStroke) {
          graphics.lineStyle(strokeWidth, stroke, strokeAlpha);
          graphics.strokeCircle(point.x * rect.width, point.y * rect.height, radius);
        }
        return graphics;
      }
      if (points.length === 2) {
        const start = points[0];
        const end = points[1];
        const bodyWidth = Math.max(1, Math.min(rect.width, rect.height) * 0.18);
        if (drawStroke) {
          graphics.lineStyle(bodyWidth + strokeWidth * 2, stroke, strokeAlpha);
          graphics.beginPath();
          graphics.moveTo(start.x * rect.width, start.y * rect.height);
          graphics.lineTo(end.x * rect.width, end.y * rect.height);
          graphics.strokePath();
        }
        if (drawFill) {
          graphics.lineStyle(bodyWidth, fill, alpha);
          graphics.beginPath();
          graphics.moveTo(start.x * rect.width, start.y * rect.height);
          graphics.lineTo(end.x * rect.width, end.y * rect.height);
          graphics.strokePath();
        }
        return graphics;
      }
      graphics.beginPath();
      graphics.moveTo(points[0].x * rect.width, points[0].y * rect.height);
      for (const point of points.slice(1)) {
        graphics.lineTo(point.x * rect.width, point.y * rect.height);
      }
      graphics.closePath();
      if (drawFill) {
        graphics.fillStyle(fill, alpha);
        graphics.fillPath();
      }
      if (drawStroke) {
        graphics.lineStyle(strokeWidth, stroke, strokeAlpha);
        graphics.strokePath();
      }
      return graphics;
    }

    private createCustomObjectAssetVisual(
      object: MysteryRoomCustomObjectDefinition,
      depth: number,
      visualAsset = getMysteryRoomEffectiveCustomObjectAsset(object),
    ) {
      if (!visualAsset) {
        return null;
      }
      const rect = object.rect;
      const center = rectCenter(rect);
      const angle = object.rotation ?? 0;
      const assetTransform = normalizeRuntimeAssetTransform({
        ...object,
        assetTransform: visualAsset.assetTransform ?? object.assetTransform,
      });
      const rotatedOffset = rotateVector(assetTransform.offsetX, assetTransform.offsetY, angle);
      if (!visualAsset.isDefaultItemAsset && isMysteryRoomAnimatedGifObject(object)) {
        const gifVisual = this.createCustomObjectGifVisual(object, depth);
        if (gifVisual) {
          return gifVisual;
        }
      }

      const textureKey = visualAsset.textureKey ?? customObjectAssetTextureKey(object);
      const image = this.add.image(center.x + rotatedOffset.x, center.y + rotatedOffset.y, textureKey);
      this.configureStableTextureVisual(image);
      image.setAngle(angle + assetTransform.rotation);
      image.setDepth(depth);
      image.setAlpha(this.textures.exists(textureKey) ? 0.98 : 0);
      this.applyCustomObjectImageDisplay(image, rect, visualAsset.assetFit, assetTransform);
      this.applyCustomObjectImageClip(image, object, assetTransform);
      this.registerCustomObjectAlphaMaskFromTexture(object, textureKey);

      if (!this.textures.exists(textureKey)) {
        this.loadCustomObjectAssetTexture(textureKey, visualAsset.assetUrl, image, rect, visualAsset.assetFit, {
          ...object,
          assetTransform: visualAsset.assetTransform ?? object.assetTransform,
        });
      }
      return image;
    }

    private createCustomObjectGifVisual(object: MysteryRoomCustomObjectDefinition, depth: number) {
      if (typeof document === 'undefined' || !object.assetUrl) {
        return null;
      }
      const textureKey = `${customObjectAssetTextureKey(object)}_gif_canvas`;
      const rect = object.rect;
      const fallbackCanvasWidth = Math.max(1, Math.round(rect.width));
      const fallbackCanvasHeight = Math.max(1, Math.round(rect.height));
      const texture = (this.textures.exists(textureKey)
        ? this.textures.get(textureKey)
        : this.textures.createCanvas(textureKey, fallbackCanvasWidth, fallbackCanvasHeight)) as any;
      const canvas = texture?.getSourceImage?.() as HTMLCanvasElement | undefined;
      const context = canvas?.getContext?.('2d');
      if (!texture || !canvas || !context) {
        return null;
      }

      const transform = normalizeRuntimeAssetTransform(object);
      const center = rectCenter(rect);
      const rotatedOffset = rotateVector(transform.offsetX, transform.offsetY, object.rotation ?? 0);
      const visual = this.add.image(center.x + rotatedOffset.x, center.y + rotatedOffset.y, textureKey);
      this.configureStableTextureVisual(visual);
      visual.setAngle((object.rotation ?? 0) + transform.rotation);
      visual.setDepth(depth);
      this.applyCustomObjectImageDisplay(visual, rect, object.assetFit, transform);
      visual.setAlpha(0);
      this.applyCustomObjectImageClip(visual, object, transform);

      const sourceImage = document.createElement('img');
      sourceImage.alt = object.label || object.assetName || 'map asset';
      sourceImage.draggable = false;
      const drawFrame = () => {
        if (!visual.scene) {
          return;
        }
        if (!sourceImage.complete || !(sourceImage.naturalWidth || sourceImage.width)) {
          return;
        }
        const frameWidth = Math.max(1, Math.round(sourceImage.naturalWidth || sourceImage.width || fallbackCanvasWidth));
        const frameHeight = Math.max(1, Math.round(sourceImage.naturalHeight || sourceImage.height || fallbackCanvasHeight));
        try {
          if (canvas.width !== frameWidth || canvas.height !== frameHeight) {
            canvas.width = frameWidth;
            canvas.height = frameHeight;
            texture.setSize?.(frameWidth, frameHeight);
            visual.setTexture(textureKey);
          }
          context.clearRect(0, 0, frameWidth, frameHeight);
          context.drawImage(sourceImage, 0, 0, frameWidth, frameHeight);
          texture.refresh?.();
          this.applyCustomObjectImageDisplay(visual, rect, object.assetFit, transform);
          visual.setAlpha(0.98);
        } catch {
          visual.setAlpha(0);
        }
      };
      sourceImage.addEventListener('load', () => {
        drawFrame();
        registerMysteryRoomCustomObjectAlphaMask(object, canvas);
      }, { once: true });
      sourceImage.src = object.assetUrl;
      const timer = this.time.addEvent({
        delay: 100,
        loop: true,
        callback: drawFrame,
      });
      visual.once?.('destroy', () => timer.remove(false));
      return visual;
    }

    private loadCustomObjectAssetTexture(
      textureKey: string,
      url: string,
      image: any,
      rect: MysteryRoomRect,
      fit: MysteryRoomCustomObjectDefinition['assetFit'],
      object: MysteryRoomCustomObjectDefinition,
    ) {
      const fileCompleteEvent = `filecomplete-image-${textureKey}`;
      this.load.once(fileCompleteEvent, () => {
        this.pendingAssetTextureLoads.delete(textureKey);
        if (!image?.scene || !this.textures.exists(textureKey)) {
          return;
        }
        image.setTexture(textureKey);
        image.setAlpha(0.98);
        this.applyCustomObjectImageDisplay(image, rect, fit, normalizeRuntimeAssetTransform(object));
        this.registerCustomObjectAlphaMaskFromTexture(object, textureKey);
      });
      this.load.once('loaderror', (file: { key?: string }) => {
        if (file?.key === textureKey) {
          this.pendingAssetTextureLoads.delete(textureKey);
        }
      });

      if (!this.pendingAssetTextureLoads.has(textureKey)) {
        this.pendingAssetTextureLoads.add(textureKey);
        this.load.image(textureKey, url);
      }
      if (!this.load.isLoading()) {
        this.load.start();
      }
    }

    private createWallTextureImage(segment: MysteryRoomSegment, depth: number, scope: 'local' | 'server') {
      const texture = segment.texture;
      if (!texture?.assetUrl) {
        return;
      }
      const dx = segment.end.x - segment.start.x;
      const dy = segment.end.y - segment.start.y;
      const length = Math.max(1, Math.hypot(dx, dy));
      const thickness = scope === 'local' ? 18 : 16;
      const rect = {
        x: (segment.start.x + segment.end.x) / 2 - length / 2,
        y: (segment.start.y + segment.end.y) / 2 - thickness / 2,
        width: length,
        height: thickness,
      };
      this.createSurfaceTextureImage(texture, rect, depth, (Math.atan2(dy, dx) * 180) / Math.PI, scope);
    }

    private createWallTextureLayerImages(segment: MysteryRoomSegment, depth: number, scope: 'local' | 'server') {
      const dx = segment.end.x - segment.start.x;
      const dy = segment.end.y - segment.start.y;
      const length = Math.max(1, Math.hypot(dx, dy));
      const thickness = scope === 'local' ? 18 : 16;
      const rect = {
        x: (segment.start.x + segment.end.x) / 2 - length / 2,
        y: (segment.start.y + segment.end.y) / 2 - thickness / 2,
        width: length,
        height: thickness,
      };
      this.createSurfaceTextureLayerImages(segment.textures, rect, depth, (Math.atan2(dy, dx) * 180) / Math.PI, scope);
    }

    private createSurfaceTextureLayerImages(
      textures: MysteryRoomTextureLayer[] | undefined,
      rect: MysteryRoomRect,
      depth: number,
      angle: number,
      scope: 'local' | 'server',
    ) {
      (textures ?? []).forEach((texture, index) => {
        if (texture.visible === false) return;
        this.createSurfaceTextureImage(texture, rect, depth + index * 0.02, angle, scope);
      });
    }

    private createCustomObjectTextureLayerVisuals(object: MysteryRoomCustomObjectDefinition, depth: number) {
      (object.textures ?? []).forEach((texture, index) => {
        if (texture.visible === false) return;
        const visual = this.createSurfaceTextureImage(texture, object.rect, depth + index * 0.02, object.rotation ?? 0, 'local');
        if (visual) {
          this.applyCustomObjectImageClip(visual, object);
          this.registerLocalFixtureObject(visual, object.rect, object.id, object.rect, true);
          this.enableCustomObjectFixtureInteraction(visual, object);
        }
      });
    }

    private createSurfaceTextureImage(
      texture: MysteryRoomSurfaceTexture | undefined,
      rect: MysteryRoomRect,
      depth: number,
      angle: number,
      scope: 'local' | 'server',
    ) {
      const textureUrl = resolveMysteryRoomTextureAssetUrl(texture);
      if (!texture || !textureUrl) {
        return null;
      }
      const textureKey = surfaceTextureKey(texture);
      const center = rectCenter(rect);
      const image: any =
        texture.fit === 'repeat'
          ? this.add.tileSprite(center.x, center.y, rect.width, rect.height, textureKey)
          : this.add.image(center.x, center.y, textureKey);
      image.__mrTextureBaseX = center.x;
      image.__mrTextureBaseY = center.y;
      image.__mrTextureBaseAngle = angle;
      this.configureStableTextureVisual(image);
      image.setAngle(angle);
      image.setDepth(depth);
      image.setAlpha(this.textures.exists(textureKey) ? normalizeSurfaceTextureOpacity(texture.opacity) : 0);
      this.applySurfaceTextureDisplay(image, rect, texture.fit, texture);
      this.applySurfaceTextureEffects(image, texture);
      this.applySurfaceTextureClip(image, rect, angle, scope);
      this.trackSurfaceTextureObject(image, scope);

      if (!this.textures.exists(textureKey)) {
        this.loadSurfaceTexture(textureKey, textureUrl, image, rect, texture);
      }
      return image;
    }

    private loadSurfaceTexture(
      textureKey: string,
      url: string,
      image: any,
      rect: MysteryRoomRect,
      texture: MysteryRoomSurfaceTexture,
    ) {
      const fileCompleteEvent = `filecomplete-image-${textureKey}`;
      this.load.once(fileCompleteEvent, () => {
        this.pendingAssetTextureLoads.delete(textureKey);
        if (!image?.scene || !this.textures.exists(textureKey)) {
          return;
        }
        image.setTexture(textureKey);
        image.setAlpha(normalizeSurfaceTextureOpacity(texture.opacity));
        this.applySurfaceTextureDisplay(image, rect, texture.fit, texture);
        this.applySurfaceTextureEffects(image, texture);
      });
      this.load.once('loaderror', (file: { key?: string }) => {
        if (file?.key === textureKey) {
          this.pendingAssetTextureLoads.delete(textureKey);
        }
      });

      if (!this.pendingAssetTextureLoads.has(textureKey)) {
        this.pendingAssetTextureLoads.add(textureKey);
        this.load.image(textureKey, url);
      }
      if (!this.load.isLoading()) {
        this.load.start();
      }
    }

    private applySurfaceTextureDisplay(
      image: any,
      rect: MysteryRoomRect,
      fit: MysteryRoomSurfaceTexture['fit'],
      texture?: MysteryRoomSurfaceTexture,
    ) {
      if (fit === 'repeat') {
        const sourceSize = this.surfaceTextureSourceSize(image, texture);
        const tileSize = getMysteryRoomTextureWorldTileSize(texture, sourceSize);
        const tileScaleX = tileSize.width / Math.max(1, sourceSize.width);
        const tileScaleY = tileSize.height / Math.max(1, sourceSize.height);
        image.setSize?.(rect.width, rect.height);
        image.setDisplaySize?.(rect.width, rect.height);
        image.setTileScale?.(tileScaleX, tileScaleY);
        image.setTilePosition?.(
          -finiteRuntimeNumber(texture?.offsetX, 0) / Math.max(0.0001, tileScaleX),
          -finiteRuntimeNumber(texture?.offsetY, 0) / Math.max(0.0001, tileScaleY),
        );
        return;
      }
      if (fit === 'stretch') {
        image.setDisplaySize(rect.width, rect.height);
        return;
      }
      const frameWidth = image.width || image.displayWidth || rect.width;
      const frameHeight = image.height || image.displayHeight || rect.height;
      const scale =
        fit === 'contain'
          ? Math.min(rect.width / Math.max(1, frameWidth), rect.height / Math.max(1, frameHeight))
          : Math.max(rect.width / Math.max(1, frameWidth), rect.height / Math.max(1, frameHeight));
      image.setDisplaySize(Math.max(1, frameWidth * scale), Math.max(1, frameHeight * scale));
    }

    private applySurfaceTextureEffects(image: any, texture: MysteryRoomSurfaceTexture) {
      const effects = texture.effects ?? {};
      const brightness = normalizeRuntimeEffectIntensity(effects.brightness);
      const shading = normalizeRuntimeEffectIntensity(effects.shading);
      const reflection = normalizeRuntimeEffectIntensity(effects.reflection);
      if (brightness < 0 || shading > 0) {
        const value = Math.max(45, 255 - Math.max(Math.abs(brightness), shading) * 1.8);
        const channel = Math.round(value);
        image.setTint?.((channel << 16) + (channel << 8) + channel);
      } else if (brightness > 0 || reflection > 0) {
        image.setTint?.(0xffffff);
      }
      const baseAngle = finiteRuntimeNumber(image.__mrTextureBaseAngle, image.angle ?? 0);
      if (texture.fit === 'repeat') {
        image.setAngle?.(baseAngle + finiteRuntimeNumber(texture.rotation, 0));
      } else {
        image.setPosition?.(
          finiteRuntimeNumber(image.__mrTextureBaseX, image.x ?? 0) + finiteRuntimeNumber(texture.offsetX, 0),
          finiteRuntimeNumber(image.__mrTextureBaseY, image.y ?? 0) + finiteRuntimeNumber(texture.offsetY, 0),
        );
        image.setAngle?.(baseAngle + finiteRuntimeNumber(texture.rotation, 0));
      }
    }

    private surfaceTextureSourceSize(
      image: any,
      texture: MysteryRoomSurfaceTexture | undefined,
    ): { width: number; height: number } {
      const fallback = getMysteryRoomTextureTileSize(texture);
      const frame = image?.displayFrame ?? image?.frame;
      const source = image?.texture?.getSourceImage?.() ?? image?.texture?.source?.[0]?.image;
      const width = frame?.width || source?.naturalWidth || source?.width || fallback;
      const height = frame?.height || source?.naturalHeight || source?.height || fallback;
      return {
        width: Math.max(1, Math.round(width)),
        height: Math.max(1, Math.round(height)),
      };
    }

    private applySurfaceTextureClip(image: any, rect: MysteryRoomRect, angle: number, scope: 'local' | 'server') {
      if (!image?.setMask) {
        return;
      }
      const center = rectCenter(rect);
      const maskGraphics = this.add.graphics();
      maskGraphics.visible = false;
      maskGraphics.setPosition(center.x, center.y);
      maskGraphics.setRotation((angle / 180) * Math.PI);
      maskGraphics.fillStyle(0xffffff, 1);
      maskGraphics.fillRect(-rect.width / 2, -rect.height / 2, rect.width, rect.height);
      image.setMask(maskGraphics.createGeometryMask());
      this.trackSurfaceTextureObject(maskGraphics, scope);
    }

    private trackSurfaceTextureObject(object: any, scope: 'local' | 'server') {
      if (scope === 'local') {
        this.localSurfaceTextureObjects.push(object);
      } else {
        this.serverSurfaceTextureObjects.push(object);
      }
    }

    private applyCustomObjectImageDisplay(
      image: any,
      rect: MysteryRoomRect,
      fit: MysteryRoomCustomObjectDefinition['assetFit'],
      transform = normalizeRuntimeAssetTransform(),
    ) {
      if (fit === 'stretch') {
        image.setDisplaySize(rect.width * transform.scaleX, rect.height * transform.scaleY);
        return;
      }
      const frameWidth = image.width || image.displayWidth || rect.width;
      const frameHeight = image.height || image.displayHeight || rect.height;
      const scale =
        fit === 'cover'
          ? Math.max(rect.width / Math.max(1, frameWidth), rect.height / Math.max(1, frameHeight))
          : Math.min(rect.width / Math.max(1, frameWidth), rect.height / Math.max(1, frameHeight));
      image.setDisplaySize(
        Math.max(1, frameWidth * scale * transform.scaleX),
        Math.max(1, frameHeight * scale * transform.scaleY),
      );
    }

    private applyCustomObjectImageClip(image: any, object: MysteryRoomCustomObjectDefinition, transform = normalizeRuntimeAssetTransform()) {
      if (!transform.clipToBounds || !image?.setMask) {
        return;
      }
      const rect = object.rect;
      const center = rectCenter(rect);
      const stableCenter = this.stableTexturePoint(center.x, center.y);
      const maskGraphics = this.add.graphics();
      maskGraphics.visible = false;
      maskGraphics.setPosition(stableCenter.x, stableCenter.y);
      maskGraphics.setRotation(((object.rotation ?? 0) / 180) * Math.PI);
      drawCustomObjectImageClipMask(maskGraphics, object);
      image.setMask(maskGraphics.createGeometryMask());
      this.localFixtureMaskObjects.push(maskGraphics);
      this.localFixtureMaskSyncObjects.push({ object: maskGraphics, sourceId: object.id, offset: { x: 0, y: 0 }, stableTexture: true });
    }

    private registerCustomObjectAlphaMaskFromTexture(object: MysteryRoomCustomObjectDefinition, textureKey: string) {
      if (!object.assetAlphaCollisionEnabled || !this.textures.exists(textureKey)) {
        return;
      }
      const texture = this.textures.get(textureKey);
      const sourceImage =
        typeof texture?.getSourceImage === 'function'
          ? texture.getSourceImage()
          : texture?.source?.[0]?.image;
      registerMysteryRoomCustomObjectAlphaMask(object, sourceImage);
    }

    private addFixtureRect(
      rect: MysteryRoomRect,
      base: number,
      alpha: number,
      accent: number,
      lineWidth: number,
      depth: number,
    ) {
      const object = this.add.rectangle(rect.x + rect.width / 2, rect.y + rect.height / 2, rect.width, rect.height, base, alpha);
      object.setStrokeStyle(lineWidth, accent, 0.46);
      object.setDepth(depth);
      return object;
    }

    private addFixtureCircle(cx: number, cy: number, radius: number, base: number, accent: number, rect: MysteryRoomRect) {
      const object = this.add.circle(cx, cy, radius, base, 0.9);
      object.setStrokeStyle(5, accent, 0.52);
      object.setDepth(193);
      this.registerLocalFixtureObject(object, rect);
      for (let index = 0; index < 6; index += 1) {
        const angle = (Math.PI * 2 * index) / 6;
        const blade = this.add.triangle(
          cx,
          cy,
          Math.cos(angle - 0.18) * radius * 0.24,
          Math.sin(angle - 0.18) * radius * 0.24,
          Math.cos(angle + 0.18) * radius * 0.24,
          Math.sin(angle + 0.18) * radius * 0.24,
          Math.cos(angle) * radius * 0.78,
          Math.sin(angle) * radius * 0.78,
          0x0f172a,
          0.72,
        );
        blade.setDepth(194);
        this.registerLocalFixtureObject(blade, rect);
      }
    }

    private drawPropShadow(prop: MysteryRoomPropDefinition) {
      if (!this.localPropShadowGraphics) {
        return;
      }

      const rect = prop.rect;
      this.localPropShadowGraphics.fillStyle(0x020617, prop.kind === 'surgical_lamp' ? 0.22 : 0.34);
      this.localPropShadowGraphics.fillRoundedRect(rect.x + 10, rect.y + 12, rect.width, rect.height, 10);
    }

    private drawLocalProp(prop: MysteryRoomPropDefinition) {
      const graphics = this.localPropGraphics;
      if (!graphics) {
        return;
      }

      const rect = prop.rect;
      const base = prop.color ?? 0x334155;
      const accent = prop.accentColor ?? 0x38bdf8;

      switch (prop.kind) {
        case 'airlock_console':
          this.drawConsoleProp(rect, base, accent);
          break;
        case 'locker':
          this.drawLockerProp(rect, base, accent);
          break;
        case 'crate_stack':
          this.drawCrateStackProp(rect, base, accent);
          break;
        case 'lab_bench':
          this.drawLabBenchProp(rect, base, accent);
          break;
        case 'microscope':
          this.drawMicroscopeProp(rect, base, accent);
          break;
        case 'sample_tank':
          this.drawSampleTankProp(rect, base, accent);
          break;
        case 'wall_terminal':
          this.drawWallTerminalProp(rect, base, accent);
          break;
        case 'server_rack':
          this.drawServerRackProp(rect, base, accent);
          break;
        case 'server_console':
          this.drawServerConsoleProp(rect, base, accent);
          break;
        case 'medical_bed':
          this.drawMedicalBedProp(rect, base, accent);
          break;
        case 'surgical_lamp':
          this.drawSurgicalLampProp(rect, base, accent);
          break;
        case 'med_monitor':
          this.drawMedMonitorProp(rect, base, accent);
          break;
        case 'reactor_core':
          this.drawReactorCoreProp(rect, base, accent);
          break;
        case 'pipe_run':
          this.drawPipeRunProp(rect, base, accent);
          break;
        case 'turbine':
          this.drawTurbineProp(rect, base, accent);
          break;
        case 'floor_grate':
          this.drawFloorGrateProp(rect, base, accent);
          break;
        case 'warning_stripe':
          this.drawWarningStripeProp(rect, base, accent);
          break;
      }
    }

    private drawConsoleProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.96);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 10);
      g.lineStyle(2, 0xe2e8f0, 0.16);
      g.strokeRoundedRect(rect.x, rect.y, rect.width, rect.height, 10);
      g.fillStyle(accent, 0.78);
      g.fillRoundedRect(rect.x + rect.width * 0.18, rect.y + 12, rect.width * 0.64, rect.height * 0.34, 5);
      g.fillStyle(0x020617, 0.55);
      g.fillCircle(rect.x + rect.width * 0.5, rect.y + rect.height * 0.72, Math.min(rect.width, rect.height) * 0.12);
    }

    private drawLockerProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.95);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 8);
      const cellWidth = rect.width / 4;
      for (let index = 1; index < 4; index += 1) {
        const x = rect.x + index * cellWidth;
        g.lineStyle(2, 0x020617, 0.42);
        g.lineBetween(x, rect.y + 8, x, rect.y + rect.height - 8);
      }
      for (let index = 0; index < 4; index += 1) {
        g.fillStyle(accent, 0.45);
        g.fillRoundedRect(rect.x + index * cellWidth + cellWidth * 0.62, rect.y + rect.height * 0.42, 7, 18, 3);
      }
    }

    private drawCrateStackProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      const crateWidth = rect.width / 2 - 6;
      const crateHeight = rect.height / 2 - 6;
      for (let row = 0; row < 2; row += 1) {
        for (let col = 0; col < 2; col += 1) {
          const x = rect.x + col * (crateWidth + 12);
          const y = rect.y + row * (crateHeight + 12);
          g.fillStyle(base, 0.94);
          g.fillRoundedRect(x, y, crateWidth, crateHeight, 6);
          g.lineStyle(2, accent, 0.32);
          g.strokeRoundedRect(x + 5, y + 5, crateWidth - 10, crateHeight - 10, 4);
        }
      }
    }

    private drawLabBenchProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.98);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 12);
      g.lineStyle(2, 0x94a3b8, 0.42);
      g.strokeRoundedRect(rect.x, rect.y, rect.width, rect.height, 12);
      const modules = Math.max(3, Math.floor(rect.width / 95));
      for (let index = 0; index < modules; index += 1) {
        const x = rect.x + 18 + index * ((rect.width - 36) / modules);
        g.fillStyle(0x0f172a, 0.28);
        g.fillRoundedRect(x, rect.y + rect.height * 0.58, 42, rect.height * 0.22, 5);
        g.fillStyle(accent, 0.7);
        g.fillCircle(x + 18, rect.y + rect.height * 0.33, 7);
      }
    }

    private drawMicroscopeProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      const cx = rect.x + rect.width / 2;
      const cy = rect.y + rect.height / 2;
      g.fillStyle(0x111827, 0.42);
      g.fillEllipse(cx, cy + rect.height * 0.32, rect.width * 0.72, rect.height * 0.22);
      g.lineStyle(6, base, 0.95);
      g.lineBetween(cx - rect.width * 0.18, cy + rect.height * 0.18, cx + rect.width * 0.2, cy - rect.height * 0.18);
      g.fillStyle(base, 0.98);
      g.fillRoundedRect(cx - rect.width * 0.18, cy + rect.height * 0.12, rect.width * 0.42, rect.height * 0.2, 5);
      g.fillStyle(accent, 0.86);
      g.fillCircle(cx + rect.width * 0.25, cy - rect.height * 0.22, rect.width * 0.12);
      g.fillStyle(0xf8fafc, 0.82);
      g.fillCircle(cx - rect.width * 0.18, cy - rect.height * 0.1, rect.width * 0.08);
    }

    private drawSampleTankProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(0x031b1a, 0.88);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, rect.width * 0.4);
      g.lineStyle(4, accent, 0.58);
      g.strokeRoundedRect(rect.x + 4, rect.y + 4, rect.width - 8, rect.height - 8, rect.width * 0.36);
      g.fillStyle(base, 0.38);
      g.fillRoundedRect(rect.x + 12, rect.y + rect.height * 0.18, rect.width - 24, rect.height * 0.58, rect.width * 0.24);
      g.fillStyle(0xf8fafc, 0.34);
      g.fillRoundedRect(rect.x + rect.width * 0.26, rect.y + 16, rect.width * 0.16, rect.height - 32, 8);
    }

    private drawWallTerminalProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.96);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 8);
      g.fillStyle(accent, 0.82);
      g.fillRoundedRect(rect.x + 12, rect.y + 10, rect.width * 0.52, rect.height - 20, 5);
      g.fillStyle(0xe2e8f0, 0.68);
      for (let index = 0; index < 4; index += 1) {
        g.fillRect(rect.x + rect.width * 0.68, rect.y + 12 + index * 11, rect.width * 0.2, 3);
      }
    }

    private drawServerRackProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.98);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 8);
      g.lineStyle(2, 0x64748b, 0.34);
      g.strokeRoundedRect(rect.x, rect.y, rect.width, rect.height, 8);
      const rows = Math.max(5, Math.floor(rect.height / 46));
      for (let row = 0; row < rows; row += 1) {
        const y = rect.y + 18 + row * ((rect.height - 36) / rows);
        g.fillStyle(0x020617, 0.78);
        g.fillRoundedRect(rect.x + 10, y, rect.width - 20, 20, 4);
        g.fillStyle(row % 3 === 0 ? 0xef4444 : accent, 0.86);
        g.fillRect(rect.x + rect.width - 28, y + 6, 12, 4);
        g.fillStyle(accent, 0.56);
        g.fillRect(rect.x + 18, y + 7, rect.width * 0.34, 3);
      }
    }

    private drawServerConsoleProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.94);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 12);
      for (let index = 0; index < 3; index += 1) {
        const screenWidth = rect.width * 0.24;
        const x = rect.x + 18 + index * (screenWidth + 18);
        g.fillStyle(0x020617, 0.92);
        g.fillRoundedRect(x, rect.y + 12, screenWidth, rect.height * 0.46, 5);
        g.fillStyle(index === 1 ? 0xef4444 : accent, 0.8);
        g.fillRect(x + 8, rect.y + 24, screenWidth - 16, 4);
        g.fillRect(x + 8, rect.y + 36, screenWidth * 0.55, 3);
      }
      g.fillStyle(0x94a3b8, 0.45);
      g.fillRoundedRect(rect.x + 24, rect.y + rect.height * 0.72, rect.width - 48, 12, 6);
    }

    private drawMedicalBedProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(0x334155, 0.96);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 20);
      g.fillStyle(base, 0.98);
      g.fillRoundedRect(rect.x + 24, rect.y + 18, rect.width - 48, rect.height - 36, 16);
      g.fillStyle(accent, 0.72);
      g.fillRoundedRect(rect.x + 42, rect.y + 34, rect.width - 84, rect.height * 0.38, 12);
      g.fillStyle(0xf8fafc, 0.9);
      g.fillRoundedRect(rect.x + rect.width * 0.66, rect.y + 32, rect.width * 0.18, rect.height * 0.28, 10);
      g.lineStyle(3, 0x0f172a, 0.35);
      g.lineBetween(rect.x + 22, rect.y + rect.height + 4, rect.x + 22, rect.y + rect.height + 42);
      g.lineBetween(rect.x + rect.width - 22, rect.y + rect.height + 4, rect.x + rect.width - 22, rect.y + rect.height + 42);
    }

    private drawSurgicalLampProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      const cx = rect.x + rect.width / 2;
      const cy = rect.y + rect.height / 2;
      g.lineStyle(6, 0x94a3b8, 0.78);
      g.lineBetween(cx - rect.width * 0.35, cy + rect.height * 0.32, cx, cy);
      g.lineBetween(cx, cy, cx + rect.width * 0.32, cy - rect.height * 0.28);
      g.fillStyle(base, 0.96);
      g.fillCircle(cx + rect.width * 0.28, cy - rect.height * 0.28, rect.width * 0.2);
      g.lineStyle(4, accent, 0.72);
      g.strokeCircle(cx + rect.width * 0.28, cy - rect.height * 0.28, rect.width * 0.15);
      g.fillStyle(0xf8fafc, 0.82);
      g.fillCircle(cx + rect.width * 0.28, cy - rect.height * 0.28, rect.width * 0.07);
    }

    private drawMedMonitorProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.96);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 8);
      g.fillStyle(0x020617, 0.92);
      g.fillRoundedRect(rect.x + 10, rect.y + 10, rect.width - 20, rect.height * 0.46, 5);
      g.lineStyle(2, accent, 0.86);
      const midY = rect.y + rect.height * 0.28;
      g.lineBetween(rect.x + 18, midY, rect.x + 34, midY);
      g.lineBetween(rect.x + 34, midY, rect.x + 44, midY - 16);
      g.lineBetween(rect.x + 44, midY - 16, rect.x + 58, midY + 18);
      g.lineBetween(rect.x + 58, midY + 18, rect.x + rect.width - 18, midY);
      g.fillStyle(accent, 0.42);
      g.fillRoundedRect(rect.x + 16, rect.y + rect.height * 0.65, rect.width - 32, 10, 4);
    }

    private drawReactorCoreProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.98);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 22);
      g.lineStyle(5, 0x94a3b8, 0.32);
      g.strokeRoundedRect(rect.x + 8, rect.y + 8, rect.width - 16, rect.height - 16, 18);
      const cx = rect.x + rect.width * 0.48;
      const cy = rect.y + rect.height * 0.52;
      g.fillStyle(0x020617, 0.82);
      g.fillCircle(cx, cy, Math.min(rect.width, rect.height) * 0.28);
      g.lineStyle(6, accent, 0.86);
      g.strokeCircle(cx, cy, Math.min(rect.width, rect.height) * 0.2);
      g.fillStyle(accent, 0.18);
      g.fillCircle(cx, cy, Math.min(rect.width, rect.height) * 0.32);
      for (let index = 0; index < 8; index += 1) {
        const angle = (Math.PI * 2 * index) / 8;
        g.lineStyle(3, 0xf97316, 0.5);
        g.lineBetween(cx, cy, cx + Math.cos(angle) * rect.width * 0.34, cy + Math.sin(angle) * rect.height * 0.34);
      }
    }

    private drawPipeRunProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      const horizontal = rect.width >= rect.height;
      const count = 3;
      for (let index = 0; index < count; index += 1) {
        const offset = (index + 0.5) / count;
        g.lineStyle(Math.max(10, (horizontal ? rect.height : rect.width) / 5), base, 0.96);
        if (horizontal) {
          const y = rect.y + rect.height * offset;
          g.lineBetween(rect.x, y, rect.x + rect.width, y);
          g.lineStyle(2, accent, 0.34);
          g.lineBetween(rect.x + 12, y - 5, rect.x + rect.width - 12, y - 5);
        } else {
          const x = rect.x + rect.width * offset;
          g.lineBetween(x, rect.y, x, rect.y + rect.height);
          g.lineStyle(2, accent, 0.34);
          g.lineBetween(x - 5, rect.y + 12, x - 5, rect.y + rect.height - 12);
        }
      }
    }

    private drawTurbineProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      const cx = rect.x + rect.width / 2;
      const cy = rect.y + rect.height / 2;
      const radius = Math.min(rect.width, rect.height) / 2;
      g.fillStyle(base, 0.96);
      g.fillCircle(cx, cy, radius);
      g.lineStyle(5, 0x94a3b8, 0.42);
      g.strokeCircle(cx, cy, radius * 0.86);
      for (let index = 0; index < 6; index += 1) {
        const angle = (Math.PI * 2 * index) / 6;
        g.fillStyle(0x0f172a, 0.72);
        g.fillTriangle(
          cx + Math.cos(angle - 0.18) * radius * 0.22,
          cy + Math.sin(angle - 0.18) * radius * 0.22,
          cx + Math.cos(angle + 0.18) * radius * 0.22,
          cy + Math.sin(angle + 0.18) * radius * 0.22,
          cx + Math.cos(angle) * radius * 0.78,
          cy + Math.sin(angle) * radius * 0.78,
        );
      }
      g.fillStyle(accent, 0.72);
      g.fillCircle(cx, cy, radius * 0.16);
    }

    private drawFloorGrateProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.62);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 8);
      g.lineStyle(2, accent, 0.24);
      for (let x = rect.x + 18; x < rect.x + rect.width; x += 28) {
        g.lineBetween(x, rect.y + 8, x, rect.y + rect.height - 8);
      }
      for (let y = rect.y + 18; y < rect.y + rect.height; y += 28) {
        g.lineBetween(rect.x + 8, y, rect.x + rect.width - 8, y);
      }
    }

    private drawWarningStripeProp(rect: MysteryRoomRect, base: number, accent: number) {
      const g = this.localPropGraphics;
      if (!g) return;
      g.fillStyle(base, 0.78);
      g.fillRoundedRect(rect.x, rect.y, rect.width, rect.height, 5);
      g.lineStyle(Math.max(4, rect.height * 0.32), accent, 0.72);
      for (let x = rect.x - rect.height; x < rect.x + rect.width; x += rect.height * 1.4) {
        g.lineBetween(x, rect.y + rect.height, x + rect.height, rect.y);
      }
    }

    private createKeyboardInput(Phaser: PhaserModule) {
      this.keys = this.input.keyboard?.addKeys({
        up: Phaser.Input.Keyboard.KeyCodes.W,
        down: Phaser.Input.Keyboard.KeyCodes.S,
        left: Phaser.Input.Keyboard.KeyCodes.A,
        right: Phaser.Input.Keyboard.KeyCodes.D,
        upAlt: Phaser.Input.Keyboard.KeyCodes.UP,
        downAlt: Phaser.Input.Keyboard.KeyCodes.DOWN,
        leftAlt: Phaser.Input.Keyboard.KeyCodes.LEFT,
        rightAlt: Phaser.Input.Keyboard.KeyCodes.RIGHT,
      });
      this.input.keyboard?.on('keydown-E', (event: KeyboardEvent) => {
        if (event.repeat || adapter.getMode() !== 'server') {
          return;
        }
        const runtime = adapter.getLocalRuntime?.();
        const action = runtime ? getPrimaryLocalAction(runtime) : null;
        if (action?.kind === 'interactable' || action?.kind === 'door') {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          adapter.onLocalInteract?.(action.kind === 'door' ? `door:${action.targetId}` : action.targetId);
        }
      });
    }

    private updateLocalMode() {
      this.syncLocalInput();
      const runtime = adapter.getLocalRuntime?.();
      if (!runtime) {
        return;
      }

      this.createLocalWorld(runtime.map);
      this.syncLocalDynamicFixtures(runtime.map);
      this.updateLocalFixtureVisibility(runtime);
      this.updateLocalEnvironment(runtime);
      this.updateLocalTargetMarker(runtime);
      this.updateLocalNpcVision(runtime);
      this.updateLocalHero(runtime);
      this.updateLocalNpcs(runtime);
      this.updateLocalBots(runtime);
      this.updateLocalIncidents(runtime);
      this.updateLocalDoors(runtime);
      this.updateLocalInteractables(runtime);
      this.audioSystem?.updateLocal(runtime, this.time.now);
    }

    private syncLocalInput() {
      if (!this.keys) {
        return;
      }
      if (this.isLocalRespawnSpectating()) {
        if (this.lastMoveVector.x !== 0 || this.lastMoveVector.y !== 0) {
          this.lastMoveVector = { x: 0, y: 0 };
          adapter.onLocalMoveVectorChange?.(0, 0);
        }
        return;
      }

      const { x, y } = this.currentKeyboardMoveVector();

      if (x === this.lastMoveVector.x && y === this.lastMoveVector.y) {
        return;
      }

      this.lastMoveVector = { x, y };
      adapter.onLocalMoveVectorChange?.(x, y);
    }

    private syncServerKeyboardMovement() {
      if (!this.keys) {
        return;
      }

      const vector = this.currentKeyboardMoveVector();
      const changed = vector.x !== this.lastServerMoveVector.x || vector.y !== this.lastServerMoveVector.y;
      if (changed) {
        this.lastServerMoveVector = vector;
        adapter.onLocalMoveVectorChange?.(vector.x, vector.y);
      }
    }

    private currentKeyboardMoveVector() {
      return mysteryRoomMoveVectorFromKeys({
        up: this.keys?.up?.isDown,
        down: this.keys?.down?.isDown,
        left: this.keys?.left?.isDown,
        right: this.keys?.right?.isDown,
        upAlt: this.keys?.upAlt?.isDown,
        downAlt: this.keys?.downAlt?.isDown,
        leftAlt: this.keys?.leftAlt?.isDown,
        rightAlt: this.keys?.rightAlt?.isDown,
      });
    }

    private updateLocalTargetMarker(runtime: MysteryRoomLocalRuntime) {
      if (!runtime.input.pointerTarget) {
        this.targetMarker?.setVisible(false);
        return;
      }

      this.targetMarker?.setPosition(runtime.input.pointerTarget.x, runtime.input.pointerTarget.y);
      this.targetMarker?.setVisible(true);
    }

    private updateLocalHero(runtime: MysteryRoomLocalRuntime) {
      const heroShape = this.heroShape ?? this.createActorShape('Оператор', 0xf59e0b, 0xfffbeb);
      this.heroShape = heroShape;
      this.latestLocalHeroPosition = { ...runtime.hero.position };
      this.syncActorShape(
        heroShape,
        runtime.hero.position,
        runtime.hero.facing,
        runtime.hero.animationState,
        0xf59e0b,
        1,
        MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN,
        runtime.hero.velocity,
      );
      this.syncHeroHeldWeapon(heroShape, runtime);
      this.syncHeroHeldFlashlight(heroShape, runtime);
      this.setLocalCursor(isMysteryRoomPistolActive(runtime));
      heroShape.container.setAlpha(
        runtime.hero.hidden ? 0.42 : runtime.gameStatus === 'respawning' || !runtime.hero.alive ? 0.72 : 1,
      );
      heroShape.body.setStrokeStyle(2, heroAlertStrokeColor(runtime.hero.alertState), 1);
      heroShape.label.setText(heroLabel(runtime.hero.hidden, runtime.hero.alertState));
      if (this.localCameraFollowHero) {
        this.clampCameraScroll();
      }
      this.publishCameraDebug();
    }

    private syncHeroHeldWeapon(shape: ActorShape, runtime: MysteryRoomLocalRuntime) {
      const active = isMysteryRoomPistolActive(runtime);
      const targetAngle = this.heroPointerAimAngle(runtime);
      this.syncHeldWeaponVisual(shape, active, targetAngle);
    }

    private syncHeroHeldFlashlight(shape: ActorShape, runtime: MysteryRoomLocalRuntime) {
      const active = runtime.flashlightEnabled && hasInventoryFlashlight(runtime) && !isMysteryRoomPistolActive(runtime);
      const angle = this.heroPointerAimAngle(runtime);
      this.syncHeldFlashlightVisual(shape, active, angle);
    }

    private syncServerHeldEquipment(shape: PlayerShape, player: MrMatchSnapshot['players'][number], isSelf: boolean) {
      const serverEquipment = isSelf ? adapter.getServerEquipment?.() : null;
      const localRuntime = isSelf ? adapter.getLocalRuntime?.() : null;
      const activeInventoryItemId = localRuntime?.activeInventoryItemId ?? serverEquipment?.activeInventoryItemId ?? player.activeInventoryItemId ?? null;
      const inventory = isSelf ? localRuntime?.inventory ?? player.inventory ?? [] : player.inventory ?? [];
      const activeItem = activeInventoryItemId
        ? inventory.find((item) => item.id === activeInventoryItemId && (item.quantity ?? 1) > 0)
        : null;
      const activeWeapon = Boolean(activeItem?.isWeapon || (!isSelf && player.activeInventoryItemIsWeapon) || activeInventoryItemId === 'pistol');
      const flashlightEnabled = Boolean((localRuntime?.flashlightEnabled ?? serverEquipment?.flashlightEnabled ?? player.flashlightEnabled) && !activeWeapon);
      const hasFlashlight = isSelf
        ? inventory.some((item) => item.id === 'flashlight' && (item.quantity ?? 1) > 0)
        : Boolean(player.flashlightEnabled);
      const angle = this.actorPointerAimAngle(
        { x: shape.target.x, y: shape.target.y + HERO_EQUIPMENT_ANCHOR_OFFSET_Y },
        shape.facing,
      );

      this.syncHeldWeaponVisual(shape, activeWeapon, angle);
      this.syncHeldFlashlightVisual(shape, flashlightEnabled && hasFlashlight, angle);
    }

    private isServerWeaponActive(): boolean {
      const snapshot = adapter.getSnapshot();
      const selfPlayerId = adapter.getSelfPlayerId();
      const localRuntime = adapter.getLocalRuntime?.();
      const activeInventoryItemId = localRuntime?.activeInventoryItemId ?? adapter.getServerEquipment?.()?.activeInventoryItemId ?? null;
      if (!snapshot || !selfPlayerId || !activeInventoryItemId) {
        return false;
      }
      const selfPlayer = snapshot.players.find((player) => player.playerId === selfPlayerId);
      const inventory = localRuntime?.inventory ?? selfPlayer?.inventory ?? [];
      const activeItem = inventory.find((item) => item.id === activeInventoryItemId && (item.quantity ?? 1) > 0);
      return Boolean(activeItem?.isWeapon || activeInventoryItemId === 'pistol' || activeInventoryItemId.includes('glock'));
    }

    private isServerPlayerFlashlightEnabled(player: MrMatchSnapshot['players'][number], isSelf: boolean): boolean {
      const equipment = isSelf ? adapter.getServerEquipment?.() : null;
      const localRuntime = isSelf ? adapter.getLocalRuntime?.() : null;
      const enabled = Boolean(localRuntime?.flashlightEnabled ?? equipment?.flashlightEnabled ?? player.flashlightEnabled);
      if (!enabled) {
        return false;
      }
      if (!isSelf) {
        return true;
      }
      return Boolean((localRuntime?.inventory ?? player.inventory ?? []).some((item) => item.id === 'flashlight' && (item.quantity ?? 1) > 0));
    }

    private syncHeldWeaponVisual(shape: HeldEquipmentShape, active: boolean, targetAngle: number) {
      shape.heldWeapon.setVisible(active);
      if (!active) {
        return;
      }
      this.ensureStaticImageTexture(MYSTERY_ROOM_GLOCK19_ASSETS.gameTextureKey, MYSTERY_ROOM_GLOCK19_ASSETS.gameUrl, () => {
        if (!shape.heldWeapon?.scene || !this.textures.exists(MYSTERY_ROOM_GLOCK19_ASSETS.gameTextureKey)) {
          return;
        }
        shape.heldWeapon.setTexture(MYSTERY_ROOM_GLOCK19_ASSETS.gameTextureKey);
        shape.heldWeapon.setAlpha(0.98);
      });
      shape.heldWeapon.setAlpha(this.textures.exists(MYSTERY_ROOM_GLOCK19_ASSETS.gameTextureKey) ? 0.98 : 0);
      shape.heldWeapon.setDisplaySize(41, 41);
      shape.heldWeapon.setFlipX(false);
      const radians = (targetAngle / 180) * Math.PI;
      shape.heldWeapon.setPosition(Math.cos(radians) * 21, HERO_EQUIPMENT_ANCHOR_OFFSET_Y + Math.sin(radians) * 21);
      shape.heldWeapon.setAngle(targetAngle - 180);
    }

    private syncHeldFlashlightVisual(shape: HeldEquipmentShape, active: boolean, angle: number) {
      shape.heldTool.setVisible(active);
      if (!active) {
        return;
      }
      this.ensureStaticImageTexture(MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldTextureKey, MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldUrl, () => {
        if (!shape.heldTool?.scene || !this.textures.exists(MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldTextureKey)) {
          return;
        }
        shape.heldTool.setTexture(MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldTextureKey);
        shape.heldTool.setAlpha(0.98);
      });
      shape.heldTool.setAlpha(this.textures.exists(MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldTextureKey) ? 0.98 : 0);
      shape.heldTool.setDisplaySize(36, 36);
      const radians = (angle / 180) * Math.PI;
      shape.heldTool.setPosition(Math.cos(radians) * 19, HERO_EQUIPMENT_ANCHOR_OFFSET_Y + Math.sin(radians) * 19);
      shape.heldTool.setAngle(angle - 180);
    }

    private heroPointerAimAngle(runtime: MysteryRoomLocalRuntime): number {
      const pointer = this.latestPointerWorldPoint;
      if (!pointer) {
        return (facingToAngle(runtime.hero.facing) * 180) / Math.PI;
      }
      const anchor = this.heroEquipmentAnchor(runtime);
      return this.actorPointerAimAngle(anchor, runtime.hero.facing);
    }

    private actorPointerAimAngle(anchor: MysteryRoomVec2, fallbackFacing: 'up' | 'down' | 'left' | 'right'): number {
      const pointer = this.latestPointerWorldPoint;
      if (!pointer) {
        return (facingToAngle(fallbackFacing) * 180) / Math.PI;
      }
      return (Math.atan2(pointer.y - anchor.y, pointer.x - anchor.x) * 180) / Math.PI;
    }

    private heroEquipmentAnchor(runtime: MysteryRoomLocalRuntime): MysteryRoomVec2 {
      return {
        x: runtime.hero.position.x,
        y: runtime.hero.position.y + HERO_EQUIPMENT_ANCHOR_OFFSET_Y,
      };
    }

    private setLocalCursor(pistolActive: boolean) {
      const mode = pistolActive ? 'pistol' : 'default';
      if (this.localCursorMode === mode) {
        return;
      }
      this.localCursorMode = mode;
      this.input.setDefaultCursor(pistolActive ? PISTOL_CROSSHAIR_CURSOR : 'default');
      const canvas = this.game.canvas as HTMLCanvasElement | undefined;
      if (canvas) {
        canvas.style.cursor = pistolActive ? PISTOL_CROSSHAIR_CURSOR : '';
      }
    }

    private createDefaultPickupImageMarker(interactable: MysteryRoomLocalRuntime['interactables'][number]) {
      const asset = defaultPickupImageAsset(interactable);
      const textureKey = asset?.textureKey ?? MYSTERY_ROOM_GLOCK19_ASSETS.worldTextureKey;
      const url = asset?.url ?? MYSTERY_ROOM_GLOCK19_ASSETS.worldUrl;
      const display = asset?.display ?? { width: 74, height: 56 };
      const image = this.add.image(interactable.anchor.x, interactable.anchor.y, textureKey);
      this.configureStableTextureVisual(image);
      image.setDisplaySize(display.width, display.height);
      image.setAlpha(this.textures.exists(textureKey) ? 0.98 : 0);
      this.ensureStaticImageTexture(textureKey, url, () => {
        if (!image?.scene || !this.textures.exists(textureKey)) {
          return;
        }
        image.setTexture(textureKey);
        image.setDisplaySize(display.width, display.height);
        image.setAlpha(0.98);
      });
      return image;
    }

    private updateLocalNpcs(runtime: MysteryRoomLocalRuntime) {
      const seen = new Set<string>();

      for (const npc of runtime.npcs) {
        seen.add(npc.id);
        const shape = this.npcShapes.get(npc.id) ?? this.createActorShape(npc.name, npc.color, 0xe2e8f0);
        this.npcShapes.set(npc.id, shape);
        const visible = isCircleVisible(runtime.visibility, npc.position, npc.radius);
        const renderVisible = visible && !npc.invisible;
        shape.container.setVisible(renderVisible);
        shape.label.setVisible(renderVisible);
        if (!visible) {
          continue;
        }
        this.syncActorShape(shape, npc.position, npc.facing, npc.alive ? npc.animationState : 'idle', npc.alive ? npc.color : 0x475569, npc.scale);
        shape.container.setAlpha(npc.alive ? 1 : 0.58);
        shape.body.setStrokeStyle(2, npc.alive ? awarenessStrokeColor(npc.awarenessState) : 0xfb7185, npc.alive ? 1 : 0.8);
        shape.label.setText(
          npc.alive
            ? `${npc.name} [${npc.role}] ${npc.currentRoleAction ?? npc.currentTask?.kind ?? npc.awarenessState}`
            : `${npc.name} [нейтрализован]`,
        );
      }

      const staleIds: string[] = [];
      this.npcShapes.forEach((_shape, npcId) => {
        if (!seen.has(npcId)) {
          staleIds.push(npcId);
        }
      });
      staleIds.forEach((npcId) => {
        const shape = this.npcShapes.get(npcId);
        shape?.container.destroy();
        this.npcShapes.delete(npcId);
      });
    }

    // Зачем: рендер локальных ботов из runtime.localBots (без сети) с ленивым созданием/удалением шейпов.
    private updateLocalBots(runtime: MysteryRoomLocalRuntime) {
      const bots = runtime.localBots ?? [];
      const seen = new Set<string>();

      for (const bot of bots) {
        seen.add(bot.id);
        const shape = this.localBotShapes.get(bot.id) ?? this.createActorShape(bot.name, bot.color, 0xe2e8f0);
        this.localBotShapes.set(bot.id, shape);
        // Зачем: remotePeer — живые игроки P2P; иначе они пропадают за пределами конуса видимости локального героя.
        const visible =
          !runtime.visibility || bot.remotePeer || isCircleVisible(runtime.visibility, bot.position, bot.radius);
        shape.container.setVisible(visible);
        shape.label.setVisible(visible);
        if (!visible) {
          continue;
        }
        const skin = bot.remotePeer ? MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN : null;
        this.syncActorShape(shape, bot.position, bot.facing, bot.animationState, bot.color, bot.scale, skin);
        shape.body.setStrokeStyle(2, bot.remotePeer ? 0xfffbeb : bot.color, 1);
        shape.label.setText(bot.remotePeer ? `▸ ${bot.name}` : bot.name);
      }

      const staleIds: string[] = [];
      this.localBotShapes.forEach((_shape, botId) => {
        if (!seen.has(botId)) {
          staleIds.push(botId);
        }
      });
      staleIds.forEach((botId) => {
        const shape = this.localBotShapes.get(botId);
        shape?.container.destroy();
        this.localBotShapes.delete(botId);
      });
    }

    private updateLocalIncidents(runtime: MysteryRoomLocalRuntime) {
      const seen = new Set<string>();

      for (const incident of runtime.incidents) {
        seen.add(incident.id);
        const shape = this.incidentShapes.get(incident.id) ?? this.createIncidentShape(incident.kind);
        this.incidentShapes.set(incident.id, shape);

        const visible = isCircleVisible(runtime.visibility, incident.position, 18);
        shape.container.setVisible(visible);
        if (!visible) {
          continue;
        }

        shape.container.setPosition(incident.position.x, incident.position.y);
        shape.container.setDepth(120 + incident.position.y / 10);
        shape.marker.fillColor = incidentFillColor(incident.kind);
        const overlayState = resolveIncidentOverlayState(incident.status);
        shape.marker.setAlpha(overlayState.alpha);
        shape.marker.setStrokeStyle(2, overlayState.stroke, 1);
        shape.label.setText(
          `${incident.kind} · ${incident.status}\n${incident.sourceType} · ${incident.resolutionAction ?? 'none'}\n${incident.assignedNpcId ?? 'unassigned'} · ${incident.resolutionRemainingMs}`,
        );
      }

      const staleIds: string[] = [];
      this.incidentShapes.forEach((_shape, incidentId) => {
        if (!seen.has(incidentId)) {
          staleIds.push(incidentId);
        }
      });
      staleIds.forEach((incidentId) => {
        const shape = this.incidentShapes.get(incidentId);
        shape?.container.destroy();
        this.incidentShapes.delete(incidentId);
      });
    }

    private updateLocalNpcVision(runtime: MysteryRoomLocalRuntime) {
      if (!this.npcVisionGraphics) {
        return;
      }

      const blockingSegments = buildSightBlockingSegments(runtime.map, runtime.doors);
      this.npcVisionGraphics.clear();
      this.npcTargetGraphics?.clear();

      for (const npc of runtime.npcs) {
        if (!npc.alive) {
          continue;
        }
        const visible = isCircleVisible(runtime.visibility, npc.position, npc.radius);
        if (!visible) {
          continue;
        }

        const coneFacing = resolveNpcVisionFacing(npc);
        const conePoints = buildNpcVisionOverlayPoints(
          npc.position,
          coneFacing,
          npc.visionRange,
          npc.visionHalfAngleDeg,
          blockingSegments,
        );
        const fillColor = awarenessFillColor(npc.awarenessState);
        const fillAlpha =
          npc.awarenessState === 'alert' ? 0.16 : npc.awarenessState === 'search' ? 0.12 : npc.awarenessState === 'suspicious' ? 0.1 : 0.08;
        const lineAlpha =
          npc.awarenessState === 'alert' ? 0.42 : npc.awarenessState === 'search' ? 0.3 : npc.awarenessState === 'suspicious' ? 0.28 : 0.22;

        this.npcVisionGraphics.fillStyle(fillColor, fillAlpha);
        this.npcVisionGraphics.lineStyle(2, fillColor, lineAlpha);
        this.npcVisionGraphics.beginPath();
        this.npcVisionGraphics.moveTo(conePoints[0].x, conePoints[0].y);
        conePoints.slice(1).forEach((point) => this.npcVisionGraphics.lineTo(point.x, point.y));
        this.npcVisionGraphics.closePath();
        this.npcVisionGraphics.fillPath();
        this.npcVisionGraphics.strokePath();

        if (npc.investigateTarget) {
          this.npcTargetGraphics?.lineStyle(2, fillColor, 0.55);
          this.npcTargetGraphics?.lineBetween(
            npc.position.x,
            npc.position.y,
            npc.investigateTarget.x,
            npc.investigateTarget.y,
          );
          this.npcTargetGraphics?.strokeCircle(npc.investigateTarget.x, npc.investigateTarget.y, 12);
        }
      }
    }

    private updateLocalDoors(runtime: MysteryRoomLocalRuntime) {
      runtime.doors.forEach((door) => {
        const shape = this.doorShapes.get(door.id);
        if (!shape) {
          return;
        }
        const visible = isRectVisible(runtime.visibility, shape.baseRect, { requireLineOfSight: false });
        shape.glow.setVisible(visible);
        shape.frame.setAlpha(visible ? 0.88 : 0.28);
        setDoorLeafAlpha(shape, visible ? 0.98 : 0.24);

        const tint =
          door.state === 'locked'
            ? 0xef4444
            : door.state === 'open'
              ? 0x38bdf8
              : door.state === 'opening' || door.state === 'closing'
                ? 0xf59e0b
                : 0x94a3b8;
        setDoorLeafTint(shape, tint);
        shape.glow.fillAlpha = door.state === 'open' ? 0.18 : door.state === 'locked' ? 0.14 : 0.08;
        applyDoorOpenProgress(shape, door.openProgress);
      });
    }

    private updateLocalEnvironment(runtime: MysteryRoomLocalRuntime) {
      this.localDarknessGraphics?.clear();
      this.localLightGraphics?.clear();
      this.ensureLocalLightMaskViewport();
      this.clearLocalLightMaskTexture();
      this.localEffectGraphics?.clear();

      if (!this.localLightGraphics || !this.localLightMaskTexture || !this.localLightMaskContext || !this.localEffectGraphics) {
        return;
      }

      for (const light of runtime.lights) {
        if (!light.enabled || light.intensity <= 0) {
          continue;
        }

        const flicker = lightFlickerFactor(light, this.time.now, runtime.elapsedMs);
        const intensity = light.intensity * flicker;
        this.drawSoftLightMask(light.position, light.radius, intensity);
        this.drawVisibleLightGlow(light.position, light.radius, lightVisibleColor(light), intensity);
      }

      if (runtime.flashlightEnabled && hasInventoryFlashlight(runtime)) {
        this.drawFlashlightCone(runtime);
      }
      this.refreshLocalLightMaskTexture();

      for (const hazard of runtime.hazards) {
        if (!isCircleVisible(runtime.visibility, hazard.position, hazard.radius)) {
          continue;
        }

        if (hazard.kind === 'smoke') {
          this.drawSmokeHazard(hazard.position, hazard.radius, hazard.intensity);
        } else {
          this.drawSparkHazard(hazard.position, hazard.radius, hazard.intensity);
        }
      }

      this.drawCombatEffects(runtime);
      this.drawTriggerOverlays(runtime);
      this.drawSoundSourceOverlays(runtime);
    }

    private drawCombatEffects(runtime: MysteryRoomLocalRuntime) {
      if (!this.localEffectGraphics) {
        return;
      }

      for (const effect of runtime.impactEffects) {
        if (!isCircleVisible(runtime.visibility, effect.position, effect.radius, { requireLineOfSight: false })) {
          continue;
        }
        const age = Math.max(0, runtime.elapsedMs - effect.createdAtMs);
        const progress = Math.min(1, age / Math.max(1, effect.ttlMs));
        if (effect.kind === 'blood_pool') {
          const alpha = Math.max(0.12, 0.34 * (1 - progress * 0.28));
          this.localEffectGraphics.fillStyle(0x7f1d1d, alpha);
          this.localEffectGraphics.fillEllipse(effect.position.x, effect.position.y + 8, effect.radius * 1.36, effect.radius * 0.72);
          this.localEffectGraphics.lineStyle(2, 0x450a0a, alpha * 0.7);
          this.localEffectGraphics.strokeEllipse(effect.position.x, effect.position.y + 8, effect.radius * 1.36, effect.radius * 0.72);
          continue;
        }
        if (effect.kind === 'blood_splash') {
          const alpha = Math.max(0, 0.78 * (1 - progress));
          this.localEffectGraphics.fillStyle(0xdc2626, alpha);
          this.localEffectGraphics.fillCircle(effect.position.x, effect.position.y, Math.max(5, effect.radius * (0.28 + progress * 0.18)));
          for (let index = 0; index < 6; index += 1) {
            const angle = index * 1.047 + effect.createdAtMs * 0.003;
            const distance = effect.radius * (0.26 + index * 0.035 + progress * 0.42);
            this.localEffectGraphics.fillCircle(
              effect.position.x + Math.cos(angle) * distance,
              effect.position.y + Math.sin(angle) * distance,
              Math.max(2, effect.radius * 0.06),
            );
          }
          continue;
        }

        const alpha = Math.max(0, 0.9 * (1 - progress));
        this.localEffectGraphics.fillStyle(0xfef3c7, alpha);
        this.localEffectGraphics.fillCircle(effect.position.x, effect.position.y, 5 + progress * 8);
        for (let index = 0; index < 8; index += 1) {
          const angle = effect.createdAtMs * 0.017 + index * 0.785;
          const length = effect.radius * (0.22 + progress * 0.48);
          this.localEffectGraphics.lineStyle(2, index % 2 === 0 ? 0xfacc15 : 0x38bdf8, alpha);
          this.localEffectGraphics.lineBetween(
            effect.position.x + Math.cos(angle) * 4,
            effect.position.y + Math.sin(angle) * 4,
            effect.position.x + Math.cos(angle) * length,
            effect.position.y + Math.sin(angle) * length,
          );
        }
      }

      for (const projectile of runtime.projectiles) {
        const alpha = Math.max(0, 0.88 * (1 - (runtime.elapsedMs - projectile.createdAtMs) / Math.max(1, projectile.ttlMs)));
        if (alpha <= 0) {
          continue;
        }
        this.localEffectGraphics.lineStyle(3, 0xfef3c7, alpha);
        this.localEffectGraphics.lineBetween(projectile.from.x, projectile.from.y, projectile.to.x, projectile.to.y);
        this.localEffectGraphics.fillStyle(0xfffbeb, alpha);
        this.localEffectGraphics.fillCircle(projectile.from.x, projectile.from.y, 4);
      }
    }

    private drawTriggerOverlays(runtime: MysteryRoomLocalRuntime) {
      if (!this.localEffectGraphics) {
        return;
      }

      for (const trigger of runtime.triggers) {
        if (!isRectVisible(runtime.visibility, trigger.rect, { requireLineOfSight: false })) {
          continue;
        }
        const justActivated =
          trigger.lastActivatedAtMs !== null && runtime.elapsedMs - trigger.lastActivatedAtMs < 600;
        const baseColor = trigger.color ?? 0xa78bfa;
        const baseAlpha = runtimeBaseColorOpacity(trigger, 0.05);
        this.localEffectGraphics.fillStyle(justActivated ? 0xf59e0b : baseColor, justActivated ? Math.max(baseAlpha, 0.12) : baseAlpha);
        this.localEffectGraphics.fillRoundedRect(trigger.rect.x, trigger.rect.y, trigger.rect.width, trigger.rect.height, 10);
        this.localEffectGraphics.lineStyle(2, justActivated ? 0xfef3c7 : 0xc4b5fd, justActivated ? 0.66 : 0.28);
        this.localEffectGraphics.strokeRoundedRect(trigger.rect.x, trigger.rect.y, trigger.rect.width, trigger.rect.height, 10);
      }
    }

    private drawSoundSourceOverlays(runtime: MysteryRoomLocalRuntime) {
      if (!this.localEffectGraphics) {
        return;
      }

      for (const source of runtime.soundSources) {
        if (!isCircleVisible(runtime.visibility, source.position, 20, { requireLineOfSight: false })) {
          continue;
        }
        const active = source.active || source.loop || (source.lastPlayedAtMs !== null && runtime.elapsedMs - source.lastPlayedAtMs < 1200);
        this.localEffectGraphics.fillStyle(0x22d3ee, active ? 0.2 : 0.08);
        this.localEffectGraphics.fillCircle(source.position.x, source.position.y, active ? 9 : 6);
        this.localEffectGraphics.lineStyle(2, 0x67e8f9, active ? 0.28 : 0.12);
        this.localEffectGraphics.strokeCircle(source.position.x, source.position.y, Math.max(24, source.radius * 0.18));
        if (active) {
          const pulse = (this.time.now / 12) % Math.max(40, source.radius);
          this.localEffectGraphics.lineStyle(2, 0x67e8f9, 0.16);
          this.localEffectGraphics.strokeCircle(source.position.x, source.position.y, Math.min(source.radius, Math.max(22, pulse)));
        }
      }
    }

    private ensureLocalLightMaskViewport() {
      const camera = this.cameras.main;
      const width = Math.max(LIGHT_MASK_MIN_SIZE, Math.min(LIGHT_MASK_MAX_SIZE, Math.ceil(camera.width || this.scale.width || 1280)));
      const height = Math.max(LIGHT_MASK_MIN_SIZE, Math.min(LIGHT_MASK_MAX_SIZE, Math.ceil(camera.height || this.scale.height || 720)));
      const sameSize =
        this.localLightMaskTexture &&
        this.localLightMaskSize.width === width &&
        this.localLightMaskSize.height === height;

      if (!sameSize) {
        const previousTextureKey = this.localLightMaskTextureKey;
        this.localLightMaskTextureKey = `mr-local-light-mask-${Date.now()}-${Math.round(Math.random() * 1_000_000)}`;
        this.localLightMaskTexture = this.textures.createCanvas(this.localLightMaskTextureKey, width, height);
        const canvas = this.localLightMaskTexture?.getSourceImage?.() as HTMLCanvasElement | undefined;
        this.localLightMaskContext =
          this.localLightMaskTexture?.getContext?.() ??
          canvas?.getContext?.('2d') ??
          null;
        this.localLightMaskSize = { width, height };

        this.ensureLocalLightDarknessOverlay(this.localLightMaskTextureKey, width, height);
        if (previousTextureKey) {
          this.textures.remove(previousTextureKey);
        }
      } else if (this.localLightMaskTextureKey) {
        this.ensureLocalLightDarknessOverlay(this.localLightMaskTextureKey, width, height);
      }

      this.syncLocalLightDarknessOverlayViewport(width, height);
    }

    private destroyLocalLightMaskResources() {
      this.localLightDarknessOverlay?.destroy();
      this.localLightDarknessOverlay = null;
      if (this.localLightMaskTextureKey) {
        this.textures.remove(this.localLightMaskTextureKey);
      }
      this.localLightMaskTexture = null;
      this.localLightMaskTextureKey = null;
      this.localLightMaskContext = null;
      this.localLightMaskSize = { width: 0, height: 0 };
    }

    private getLocalLightOverlayScene() {
      const overlayScene = this.scene.get('UIScene') as any;
      if (!overlayScene?.add || !overlayScene?.cameras) {
        return null;
      }
      return overlayScene;
    }

    private ensureLocalLightDarknessOverlay(textureKey: string, width: number, height: number) {
      const overlayScene = this.getLocalLightOverlayScene();
      if (!overlayScene) {
        return;
      }

      if (this.localLightDarknessOverlay && this.localLightDarknessOverlay.scene !== overlayScene) {
        this.localLightDarknessOverlay.destroy();
        this.localLightDarknessOverlay = null;
      }

      if (!this.localLightDarknessOverlay) {
        this.localLightDarknessOverlay = overlayScene.add.image(0, 0, textureKey);
        this.localLightDarknessOverlay.setOrigin(0);
        this.localLightDarknessOverlay.setDepth(-100);
      } else {
        this.localLightDarknessOverlay.setTexture(textureKey);
      }

      this.syncLocalLightDarknessOverlayViewport(width, height);
    }

    private syncLocalLightDarknessOverlayViewport(width: number, height: number) {
      if (!this.localLightDarknessOverlay) {
        return;
      }
      const overlayScene = this.localLightDarknessOverlay.scene as any;
      overlayScene?.cameras?.resize?.(width, height);
      overlayScene?.cameras?.main?.setScroll?.(0, 0);
      overlayScene?.cameras?.main?.setZoom?.(1);
      this.localLightDarknessOverlay.scrollFactorX = 0;
      this.localLightDarknessOverlay.scrollFactorY = 0;
      this.localLightDarknessOverlay.setPosition(0, 0);
      this.localLightDarknessOverlay.setDisplaySize(width, height);
      this.localLightDarknessOverlay.setScrollFactor(0, 0);
    }

    private clearLocalLightMaskTexture() {
      const context = this.localLightMaskContext;
      if (!context) {
        return;
      }
      const { width, height } = this.localLightMaskSize;
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.globalCompositeOperation = 'source-over';
      context.clearRect(0, 0, width, height);
      context.fillStyle = 'rgba(2, 6, 23, 0.86)';
      context.fillRect(0, 0, width, height);
      context.restore();
    }

    private refreshLocalLightMaskTexture() {
      this.localLightMaskTexture?.refresh?.();
    }

    private worldToLightMaskPoint(point: MysteryRoomVec2): MysteryRoomVec2 {
      const camera = this.cameras.main as any;
      const originX = camera.width * (camera.originX ?? 0.5);
      const originY = camera.height * (camera.originY ?? 0.5);
      const zoomX = camera.zoomX ?? camera.zoom ?? 1;
      const zoomY = camera.zoomY ?? camera.zoom ?? 1;
      return {
        x: (camera.x ?? 0) + originX + (point.x - camera.scrollX - originX) * zoomX,
        y: (camera.y ?? 0) + originY + (point.y - camera.scrollY - originY) * zoomY,
      };
    }

    private isLightMaskCircleVisible(center: MysteryRoomVec2, radius: number) {
      return (
        center.x + radius >= 0 &&
        center.y + radius >= 0 &&
        center.x - radius <= this.localLightMaskSize.width &&
        center.y - radius <= this.localLightMaskSize.height
      );
    }

    private eraseLocalLightMaskCircle(
      center: MysteryRoomVec2,
      radius: number,
      alpha: number,
      stops: { core: number; feather: number } = { core: 0.42, feather: 0.76 },
    ) {
      const context = this.localLightMaskContext;
      if (!context || radius <= 0) {
        return;
      }
      const safeAlpha = Math.max(0, Math.min(1, alpha));
      const gradient = context.createRadialGradient(center.x, center.y, 0, center.x, center.y, radius);
      gradient.addColorStop(0, `rgba(255, 255, 255, ${safeAlpha})`);
      gradient.addColorStop(Math.max(0.01, Math.min(0.98, stops.core)), `rgba(255, 255, 255, ${safeAlpha * 0.88})`);
      gradient.addColorStop(Math.max(0.02, Math.min(0.99, stops.feather)), `rgba(255, 255, 255, ${safeAlpha * 0.22})`);
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      context.save();
      context.globalCompositeOperation = 'destination-out';
      context.fillStyle = gradient;
      context.beginPath();
      context.arc(center.x, center.y, radius, 0, Math.PI * 2);
      context.fill();
      context.restore();
    }

    private eraseLocalLightMaskPolygon(points: MysteryRoomVec2[], alpha: number) {
      const context = this.localLightMaskContext;
      if (!context || points.length < 3) {
        return;
      }

      const safeAlpha = Math.max(0, Math.min(1, alpha));
      context.save();
      context.globalCompositeOperation = 'destination-out';
      context.fillStyle = `rgba(255, 255, 255, ${safeAlpha})`;
      context.beginPath();
      context.moveTo(points[0].x, points[0].y);
      for (const point of points.slice(1)) {
        context.lineTo(point.x, point.y);
      }
      context.closePath();
      context.fill();
      context.restore();
    }

    private drawSoftLightMask(position: MysteryRoomVec2, radius: number, intensity: number) {
      if (!this.localLightMaskContext) {
        return;
      }

      const normalizedIntensity = Math.max(0.05, Math.min(1.4, intensity));
      const camera = this.cameras.main;
      const softRadius = Math.max(72, radius * 1.25) * camera.zoom;
      const screen = this.worldToLightMaskPoint(position);
      if (!this.isLightMaskCircleVisible(screen, softRadius)) {
        return;
      }
      this.eraseLocalLightMaskCircle(
        { x: Math.round(screen.x), y: Math.round(screen.y) },
        softRadius,
        Math.min(0.96, Math.max(0.42, normalizedIntensity * 0.78)),
      );
    }

    private drawVisibleLightGlow(position: MysteryRoomVec2, radius: number, color: number, intensity: number) {
      if (!this.localLightGraphics) {
        return;
      }
      const alpha = Math.max(0.025, Math.min(0.18, intensity * 0.11));
      this.localLightGraphics.fillStyle(color, alpha);
      this.localLightGraphics.fillCircle(position.x, position.y, radius * 0.72);
      this.localLightGraphics.fillStyle(color, alpha * 0.68);
      this.localLightGraphics.fillCircle(position.x, position.y, radius * 0.36);
    }

    private drawFlashlightCone(runtime: MysteryRoomLocalRuntime) {
      if (!this.localLightGraphics) {
        return;
      }

      const facingAngle = (this.heroPointerAimAngle(runtime) / 180) * Math.PI;
      const segments = runtime.visibility?.blockingSegments ?? buildSightBlockingSegments(runtime.map, runtime.doors);
      const radius = Math.min(FLASHLIGHT_RADIUS, Math.max(360, runtime.map.visibilityRadius * 1.45));
      const origin = this.heroEquipmentAnchor(runtime);
      const cone = castVisibilityCone(
        origin,
        segments,
        radius,
        facingAngle,
        FLASHLIGHT_HALF_ANGLE,
        44,
      );
      const coreCone = castVisibilityCone(
        origin,
        segments,
        radius * 0.82,
        facingAngle,
        FLASHLIGHT_HALF_ANGLE * 0.46,
        28,
      );

      const conePolygon = [origin, ...cone];
      const screenCone = conePolygon.map((point) => this.worldToLightMaskPoint(point));
      this.eraseLocalLightMaskPolygon(screenCone, FLASHLIGHT_MASK_ALPHA);
      const screenOrigin = this.worldToLightMaskPoint(origin);
      this.eraseLocalLightMaskCircle(screenOrigin, 72 * this.cameras.main.zoom, 0.88, { core: 0.55, feather: 0.92 });

      const screenCoreCone = [origin, ...coreCone].map((point) => this.worldToLightMaskPoint(point));
      this.eraseLocalLightMaskPolygon(screenCoreCone, 0.32);
      this.localLightGraphics.fillStyle(0xfffbeb, 0.72);
      this.localLightGraphics.fillCircle(
        origin.x + Math.cos(facingAngle) * 20,
        origin.y + Math.sin(facingAngle) * 20,
        12,
      );
    }

    private drawSmokeHazard(position: MysteryRoomVec2, radius: number, intensity: number) {
      if (!this.localEffectGraphics) {
        return;
      }

      for (let i = 0; i < 6; i += 1) {
        const phase = this.time.now / 760 + i * 1.37;
        const driftX = Math.cos(phase) * radius * 0.24 + (i - 2.5) * radius * 0.08;
        const driftY = Math.sin(phase * 0.8) * radius * 0.16 - i * 5;
        const pulse = 0.78 + Math.sin(phase * 1.6) * 0.22;
        this.localEffectGraphics.fillStyle(0x94a3b8, Math.max(0.04, intensity * 0.12 * pulse));
        this.localEffectGraphics.fillCircle(position.x + driftX, position.y + driftY, radius * (0.28 + i * 0.035));
      }
    }

    private drawSparkHazard(position: MysteryRoomVec2, radius: number, intensity: number) {
      if (!this.localEffectGraphics) {
        return;
      }

      const tick = Math.floor(this.time.now / 80);
      this.localEffectGraphics.fillStyle(0xfef3c7, 0.35 * intensity);
      this.localEffectGraphics.fillCircle(position.x, position.y, 8);
      for (let i = 0; i < 8; i += 1) {
        if ((tick + i) % 3 === 0) {
          continue;
        }

        const angle = tick * 0.17 + i * 0.92;
        const length = radius * (0.16 + ((tick + i) % 4) * 0.035);
        const startX = position.x + Math.cos(angle) * 8;
        const startY = position.y + Math.sin(angle) * 8;
        const endX = position.x + Math.cos(angle) * length;
        const endY = position.y + Math.sin(angle) * length;
        this.localEffectGraphics.lineStyle(2, i % 2 === 0 ? 0xfacc15 : 0x38bdf8, 0.34 + intensity * 0.42);
        this.localEffectGraphics.lineBetween(startX, startY, endX, endY);
      }
    }

    private createLightSwitchMarker(x: number, y: number, size: number) {
      const plate = this.add.rectangle(0, 0, size, size, 0x020617, 0.98);
      const socket = this.add.rectangle(0, 0, size * 0.68, size * 0.68, 0x111827, 0.98);
      const button = this.add.rectangle(0, 0, size * 0.43, size * 0.43, 0x22c55e, 0.96);
      const shine = this.add.circle(size * 0.13, -size * 0.13, Math.max(2, size * 0.06), 0xf8fafc, 0.76);
      plate.setStrokeStyle(3, 0xe2e8f0, 0.78);
      socket.setStrokeStyle(2, 0x475569, 0.92);
      button.setStrokeStyle(2, 0x86efac, 1);

      const marker = this.add.container(x, y, [plate, socket, button, shine]);
      marker.setSize(size, size);
      marker.setData('switchPlate', plate);
      marker.setData('switchSocket', socket);
      marker.setData('switchButton', button);
      marker.setData('switchShine', shine);
      marker.setData('switchSize', size);
      return marker;
    }

    private enableLightSwitchMarkerPointer(marker: any, size: number, onPointerDown: (pointer: any) => void) {
      marker.setInteractive(
        new Phaser.Geom.Rectangle(-size / 2, -size / 2, size, size),
        Phaser.Geom.Rectangle.Contains,
      );
      if (marker.input) {
        marker.input.cursor = 'pointer';
      }
      marker.on('pointerdown', onPointerDown);
    }

    private syncLightSwitchMarker(marker: any, enabled: boolean, highlighted: boolean, alpha: number) {
      const plate = marker.getData?.('switchPlate');
      const socket = marker.getData?.('switchSocket');
      const button = marker.getData?.('switchButton');
      const shine = marker.getData?.('switchShine');
      const statusFill = enabled ? 0x22c55e : 0xef4444;
      const statusStroke = enabled ? 0x86efac : 0xfca5a5;

      plate?.setFillStyle(0x020617, alpha);
      plate?.setStrokeStyle(3, highlighted ? 0xfcd34d : 0xe2e8f0, highlighted ? 1 : 0.78);
      socket?.setFillStyle(0x111827, alpha);
      socket?.setStrokeStyle(2, 0x475569, 0.92);
      button?.setFillStyle(statusFill, Math.min(1, alpha + 0.02));
      button?.setStrokeStyle(2, statusStroke, 1);
      shine?.setFillStyle(0xf8fafc, enabled ? 0.82 : 0.58);
      marker.setAlpha(alpha);
    }

    private updateLocalInteractables(runtime: MysteryRoomLocalRuntime) {
      const visible = getVisibleLocalInteractables(runtime);
      const primaryInteractable = getNearestLocalInteractable(runtime);
      const visibleCustomObjectIds = new Set<string>();
      const seen = new Set<string>();

      for (const interactable of visible) {
        if (interactable.customObjectId) {
          visibleCustomObjectIds.add(interactable.customObjectId);
          const existing = this.interactableShapes.get(interactable.id);
          existing?.marker.setVisible(false);
          existing?.label.setVisible(false);
          continue;
        }

        seen.add(interactable.id);
        const existing = this.interactableShapes.get(interactable.id);
        const missionTarget = this.resolveLocalMissionTarget(interactable.id, runtime);
        const marker =
          existing?.marker ??
          (interactable.kind === 'light_switch'
            ? this.createLightSwitchMarker(interactable.anchor.x, interactable.anchor.y, 40)
            : usesDefaultPickupImage(interactable)
              ? this.createDefaultPickupImageMarker(interactable)
            : this.add.circle(interactable.anchor.x, interactable.anchor.y, 18, interactableColor(interactable.kind), 0.92));
        if (!existing) {
          if (interactable.kind === 'light_switch') {
            this.enableLightSwitchMarkerPointer(marker, 40, (pointer: any) => {
              pointer.event.stopPropagation();
              this.audioSystem?.unlock();
              adapter.onLocalInteract?.(interactable.id);
            });
          } else if (usesDefaultPickupImage(interactable)) {
            marker.setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 12 });
            marker.on('pointerdown', (pointer: any) => {
              pointer.event.stopPropagation();
              this.audioSystem?.unlock();
              adapter.onLocalInteract?.(interactable.id);
            });
          } else {
            marker.setInteractive({ useHandCursor: true });
            marker.on('pointerdown', (pointer: any) => {
              pointer.event.stopPropagation();
              this.audioSystem?.unlock();
              adapter.onLocalInteract?.(interactable.id);
            });
          }
        }
        const isPrimary = primaryInteractable?.id === interactable.id;
        if (interactable.kind === 'light_switch') {
          this.syncLightSwitchMarker(
            marker,
            isLocalLightSwitchEnabled(runtime, interactable.id),
            isPrimary,
            interactable.state === 'used' ? 0.62 : 0.98,
          );
        } else if (usesDefaultPickupImage(interactable)) {
          const asset = defaultPickupImageAsset(interactable);
          const textureKey = defaultPickupImageTextureKey(interactable);
          marker.setAlpha(interactable.state === 'used' ? 0.5 : textureKey && this.textures.exists(textureKey) ? 0.98 : 0);
          if (asset) {
            const scale = isPrimary ? 1.14 : 1;
            marker.setDisplaySize(asset.display.width * scale, asset.display.height * scale);
          }
        } else {
          marker.setStrokeStyle(2, isPrimary ? 0xfcd34d : 0xf8fafc, isPrimary ? 1 : 0.7);
          marker.fillColor = interactableColor(interactable.kind);
          marker.setAlpha(interactable.state === 'used' ? 0.5 : 0.95);
        }
        const markerDepth = localLayerDepth(interactable.layer, 7);
        marker.setDepth(markerDepth);
        if (!usesDefaultPickupImage(interactable)) {
          const missionPulse = missionTarget ? 1 + Math.sin(runtime.elapsedMs / 220) * 0.08 : 1;
          marker.setScale((isPrimary ? 1.14 : 1) * missionPulse);
        }

        const label =
          existing?.label ??
          this.add.text(interactable.anchor.x, interactable.anchor.y + 22, interactable.label, {
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '11px',
            color: '#e2e8f0',
            backgroundColor: 'rgba(2, 6, 23, 0.82)',
            padding: { x: 6, y: 3 },
        });
        label.setOrigin(0.5, 0);
        label.setDepth(markerDepth + 1);

        marker.setPosition(interactable.anchor.x, interactable.anchor.y);
        label.setPosition(interactable.anchor.x, interactable.anchor.y + 22);
        label.setText(missionTarget?.label ?? interactable.label);
        label.setColor(missionTarget ? (missionTarget.completed ? '#86efac' : '#fde047') : '#e2e8f0');
        label.setBackgroundColor(missionTarget ? 'rgba(62, 42, 3, 0.92)' : 'rgba(2, 6, 23, 0.82)');
        label.setAlpha(isPrimary ? 1 : 0.86);
        marker.setVisible(true);
        label.setVisible(shouldShowInteractableWorldLabel(interactable, isPrimary));

        if (!existing) {
          this.interactableShapes.set(interactable.id, { marker, label });
        }
      }

      this.updateInteractiveCustomObjectEffects(runtime, visibleCustomObjectIds, primaryInteractable?.customObjectId ?? null);

      this.interactableShapes.forEach((shape, interactableId) => {
        if (!seen.has(interactableId)) {
          shape.marker.setVisible(false);
          shape.label.setVisible(false);
        }
      });
    }

    private resolveLocalMissionTarget(
      interactableId: string,
      runtime: MysteryRoomLocalRuntime,
    ): { label: string; completed: boolean } | null {
      const mission = runtime.globalMission;
      if (!mission.active || mission.completed) {
        return null;
      }
      if (mission.key === 'evac') {
        const sampleUsed = runtime.interactables.some((entry) => entry.id === 'research_sample_case' && entry.state === 'used');
        if (!sampleUsed && interactableId === 'research_sample_case') {
          return { label: 'ЦЕЛЬ: Забрать образец', completed: false };
        }
        if (sampleUsed && interactableId === 'mission_evac_beacon') {
          return { label: 'ЦЕЛЬ: Эвакуация через шлюз', completed: false };
        }
        return null;
      }
      if (mission.key === 'last_voice') {
        const serverDone = runtime.interactables.some((entry) => entry.id === 'server_audit_terminal' && entry.state === 'used');
        const reactorDone = runtime.interactables.some((entry) => entry.id === 'reactor_console' && entry.state === 'used');
        if (!serverDone && interactableId === 'server_audit_terminal') {
          return { label: 'ЦЕЛЬ: Проверить аудит', completed: false };
        }
        if (!reactorDone && interactableId === 'reactor_console') {
          return { label: 'ЦЕЛЬ: Проверить реактор', completed: false };
        }
        if (interactableId === 'mission_signal_locator') {
          return { label: 'Сигнал подтверждён', completed: serverDone && reactorDone };
        }
        return null;
      }
      if (mission.key === 'cleanup' && interactableId === 'mission_cleanup_board') {
        return { label: `ЦЕЛЬ: Зачистка ${mission.current}/${mission.target}`, completed: mission.completed };
      }
      return null;
    }

    private updateInteractiveCustomObjectEffects(
      runtime: MysteryRoomLocalRuntime,
      visibleCustomObjectIds: Set<string>,
      primaryCustomObjectId: string | null,
    ) {
      const objectsById = new Map(runtime.map.customObjects.map((object) => [object.id, object]));
      const phase = this.time.now / 780;

      for (const fixture of this.localFixtureObjects) {
        if (!fixture.sourceId) {
          continue;
        }
        const object = objectsById.get(fixture.sourceId);
        const active = Boolean(object?.interactive && visibleCustomObjectIds.has(fixture.sourceId));
        const highlighted = active && primaryCustomObjectId === fixture.sourceId;
        const pulse = active ? 1 + Math.sin(phase + seededPhase(fixture.sourceId)) * 0.028 + (highlighted ? 0.035 : 0) : 1;
        this.setInteractiveFixtureScale(fixture.object, pulse);
      }

      if (!primaryCustomObjectId || !this.localEffectGraphics) {
        return;
      }
      const primaryObject = objectsById.get(primaryCustomObjectId);
      if (!primaryObject?.interactive || !visibleCustomObjectIds.has(primaryCustomObjectId)) {
        return;
      }
      const glow = 0.12 + Math.sin(phase * 1.3 + seededPhase(primaryCustomObjectId)) * 0.025;
      this.drawCustomObjectInteractionHighlight(primaryObject, glow);
    }

    private setInteractiveFixtureScale(object: any, multiplier: number) {
      if (!object || object.type === 'Text' || typeof object.setScale !== 'function') {
        return;
      }
      const previousMultiplier = finiteRuntimeNumber(object.__mrInteractiveScaleMultiplier, 1);
      const baseScaleX = finiteRuntimeNumber(object.scaleX, 1) / Math.max(0.0001, previousMultiplier);
      const baseScaleY = finiteRuntimeNumber(object.scaleY, 1) / Math.max(0.0001, previousMultiplier);
      object.__mrInteractiveScaleMultiplier = multiplier;
      object.setScale(baseScaleX * multiplier, baseScaleY * multiplier);
    }

    private drawCustomObjectInteractionHighlight(object: MysteryRoomCustomObjectDefinition, alpha: number) {
      if (!this.localEffectGraphics) {
        return;
      }
      const rect = object.rect;
      const pad = Math.max(10, Math.min(rect.width, rect.height) * 0.16);
      const x = rect.x - pad;
      const y = rect.y - pad;
      const width = rect.width + pad * 2;
      const height = rect.height + pad * 2;
      const radius = Math.max(8, Math.min(18, Math.min(width, height) * 0.16));
      this.localEffectGraphics.fillStyle(0x67e8f9, alpha);
      this.localEffectGraphics.fillRoundedRect(x, y, width, height, radius);
      this.localEffectGraphics.lineStyle(2, 0xe0f2fe, Math.min(0.58, alpha * 3.4));
      this.localEffectGraphics.strokeRoundedRect(x, y, width, height, radius);
    }

    private createActorShape(label: string, fillColor: number, strokeColor: number): ActorShape {
      const shadow = this.add.ellipse(0, 29, 28, 12, 0x020617, 0.4);
      const dust = this.add.graphics();
      const skin = this.add.image(0, 0, MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.idle.key);
      this.configureStableTextureVisual(skin);
      skin.setAlpha(0);
      const body = this.add.rectangle(0, 0, 24, 32, fillColor, 0.98);
      body.setStrokeStyle(2, strokeColor, 1);
      const visor = this.add.rectangle(0, -7, 10, 6, 0xffffff, 0.75);
      const direction = this.add.rectangle(0, 0, 8, 4, strokeColor, 0.95);
      const heldWeapon = this.add.image(0, 0, MYSTERY_ROOM_GLOCK19_ASSETS.gameTextureKey);
      this.configureStableTextureVisual(heldWeapon);
      heldWeapon.setDisplaySize(41, 41);
      heldWeapon.setVisible(false);
      heldWeapon.setAlpha(this.textures.exists(MYSTERY_ROOM_GLOCK19_ASSETS.gameTextureKey) ? 0.98 : 0);
      const heldTool = this.add.image(0, 0, MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldTextureKey);
      this.configureStableTextureVisual(heldTool);
      heldTool.setDisplaySize(36, 36);
      heldTool.setVisible(false);
      heldTool.setAlpha(this.textures.exists(MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldTextureKey) ? 0.98 : 0);
      const text = this.add.text(0, 28, label, {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: '12px',
        color: '#e2e8f0',
        backgroundColor: 'rgba(2, 6, 23, 0.82)',
        padding: { x: 6, y: 3 },
      });
      text.setOrigin(0.5, 0);

      const container = this.add.container(0, 0, [shadow, dust, body, visor, direction, skin, heldTool, heldWeapon, text]);
      container.setDepth(100);
      return {
        container,
        shadow,
        dust,
        skin,
        body,
        visor,
        direction,
        heldWeapon,
        heldTool,
        label: text,
        seed: Math.random() * Math.PI * 2,
        skinFrameKey: null,
        previousPosition: null,
        renderPosition: null,
      };
    }

    private createIncidentShape(kind: string): IncidentShape {
      const marker = this.add.circle(0, 0, 14, incidentFillColor(kind), 0.92);
      marker.setStrokeStyle(2, 0xf8fafc, 0.95);
      const label = this.add.text(0, 22, kind, {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: '11px',
        color: '#f8fafc',
        backgroundColor: 'rgba(2, 6, 23, 0.86)',
        padding: { x: 6, y: 3 },
      });
      label.setOrigin(0.5, 0);

      const container = this.add.container(0, 0, [marker, label]);
      container.setDepth(122);
      return {
        container,
        marker,
        label,
      };
    }

    private syncActorShape(
      shape: ActorShape,
      position: { x: number; y: number },
      facing: 'up' | 'down' | 'left' | 'right',
      animationState: 'idle' | 'walk',
      fillColor: number,
      sizeScale = 1,
      skin: MysteryRoomCharacterSkin | null = null,
      movement: MysteryRoomVec2 | null = null,
    ) {
      const phase = this.time.now / (animationState === 'walk' ? 110 : 420) + shape.seed;
      const bob = animationState === 'walk' ? Math.sin(phase) * 3.2 : Math.sin(phase) * 0.7;
      const stretch = animationState === 'walk' ? 1 + Math.abs(Math.sin(phase)) * 0.06 : 1 + Math.abs(Math.sin(phase)) * 0.02;
      shape.container.setPosition(position.x, position.y + bob);
      shape.container.setDepth(100 + position.y / 10);
      shape.container.setScale(sizeScale);
      shape.shadow.setScale(animationState === 'walk' ? 0.9 : 1.02);
      shape.body.fillColor = fillColor;
      shape.body.setScale(1, stretch);
      shape.visor.setScale(1, stretch * 0.95);
      const skinReady = this.syncCharacterSkinSprite(shape, skin, facing, animationState, movement);
      const showFallback = !skin && !skinReady;
      shape.body.setVisible(showFallback);
      shape.visor.setVisible(showFallback);
      shape.direction.setVisible(showFallback);
      this.syncFootDust(shape.dust, animationState, movement, phase);

      switch (facing) {
        case 'up':
          shape.direction.setPosition(0, -20);
          shape.direction.setAngle(0);
          break;
        case 'down':
          shape.direction.setPosition(0, 16);
          shape.direction.setAngle(0);
          break;
        case 'left':
          shape.direction.setPosition(-16, -2);
          shape.direction.setAngle(90);
          break;
        case 'right':
          shape.direction.setPosition(16, -2);
          shape.direction.setAngle(90);
          break;
      }
    }

    private syncCharacterSkinSprite(
      shape: Pick<ActorShape | PlayerShape, 'skin' | 'skinFrameKey'> & { skinFrameKey: string | null },
      skin: MysteryRoomCharacterSkin | null,
      facing: 'up' | 'down' | 'left' | 'right',
      animationState: 'idle' | 'walk',
      movement?: MysteryRoomVec2 | null,
    ) {
      if (!skin) {
        shape.skin.setVisible(false);
        shape.skin.setAlpha(0);
        shape.skinFrameKey = null;
        return false;
      }

      const frame = resolveMysteryRoomCharacterFrame(skin, facing, animationState, this.time.now, movement);
      const ready = this.textures.exists(frame.key);
      shape.skin.setVisible(true);
      shape.skin.setPosition(0, skin.worldDisplay.offsetY);
      shape.skin.setDisplaySize(skin.worldDisplay.width, skin.worldDisplay.height);

      if (ready && shape.skinFrameKey !== frame.key) {
        shape.skin.setTexture(frame.key);
        shape.skinFrameKey = frame.key;
      }
      shape.skin.setAlpha(ready || shape.skinFrameKey ? 1 : 0);

      this.ensureStaticImageTexture(frame.key, frame.url, () => {
        if (!shape.skin?.scene || !this.textures.exists(frame.key)) {
          return;
        }
        shape.skin.setTexture(frame.key);
        shape.skin.setDisplaySize(skin.worldDisplay.width, skin.worldDisplay.height);
        shape.skin.setAlpha(1);
        shape.skinFrameKey = frame.key;
      });

      return ready;
    }

    private syncFootDust(
      dust: any,
      animationState: 'idle' | 'walk',
      movement: MysteryRoomVec2 | null | undefined,
      phase: number,
    ) {
      if (!dust) {
        return;
      }
      dust.clear();
      if (animationState !== 'walk') {
        dust.setVisible(false);
        return;
      }
      const speed = Math.min(1, Math.hypot(movement?.x ?? 0, movement?.y ?? 0) / 90);
      const pulse = 0.55 + Math.abs(Math.sin(phase)) * 0.45;
      const alpha = 0.08 + speed * 0.11;
      dust.setVisible(true);
      dust.fillStyle(0xb9a078, alpha * pulse);
      dust.fillEllipse(-8 - pulse * 3, 30, 16 + pulse * 8, 5 + pulse * 3);
      dust.fillStyle(0xd6c5a3, alpha * 0.55);
      dust.fillEllipse(9 + pulse * 2, 27, 11 + pulse * 6, 4 + pulse * 2);
      dust.fillStyle(0x8b7355, alpha * 0.36);
      dust.fillCircle(-2 + Math.sin(phase * 1.7) * 9, 25 + Math.cos(phase) * 2, 2.2 + speed * 2.2);
    }

    private ensurePlayerShape(playerId: string, displayName: string, isSelf: boolean): PlayerShape {
      const existing = this.playerShapes.get(playerId);
      if (existing) {
        return existing;
      }

      const shadow = this.add.ellipse(0, 29, isSelf ? 34 : 30, isSelf ? 14 : 12, 0x020617, 0.4);
      const dust = this.add.graphics();
      const body = this.add.circle(0, 0, isSelf ? 14 : 12, mysteryRoomPlayerColor(isSelf), 0.9);
      body.setStrokeStyle(2, mysteryRoomPlayerStrokeColor(isSelf), 1);
      const skin = this.add.image(0, 0, MYSTERY_ROOM_DEFAULT_CHARACTER_SKIN.idle.key);
      this.configureStableTextureVisual(skin);
      skin.setAlpha(0);
      const heldWeapon = this.add.image(0, 0, MYSTERY_ROOM_GLOCK19_ASSETS.gameTextureKey);
      this.configureStableTextureVisual(heldWeapon);
      heldWeapon.setDisplaySize(41, 41);
      heldWeapon.setVisible(false);
      heldWeapon.setAlpha(this.textures.exists(MYSTERY_ROOM_GLOCK19_ASSETS.gameTextureKey) ? 0.98 : 0);
      const heldTool = this.add.image(0, 0, MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldTextureKey);
      this.configureStableTextureVisual(heldTool);
      heldTool.setDisplaySize(36, 36);
      heldTool.setVisible(false);
      heldTool.setAlpha(this.textures.exists(MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldTextureKey) ? 0.98 : 0);
      const container = this.add.container(0, 0, [shadow, dust, body, skin, heldTool, heldWeapon]);
      const label = this.add.text(0, 0, displayName, {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: '12px',
        color: '#e2e8f0',
        backgroundColor: 'rgba(2, 6, 23, 0.72)',
        padding: { x: 8, y: 2 },
      });
      label.setOrigin(0.5);
      label.setDepth(21);

      const created: PlayerShape = {
        container,
        shadow,
        dust,
        body,
        skin,
        heldWeapon,
        heldTool,
        label,
        target: new Phaser.Math.Vector2(0, 0),
        facing: 'down',
        previousPosition: null,
        renderPosition: null,
        skinFrameKey: null,
      };
      this.playerShapes.set(playerId, created);
      return created;
    }
  };
}

function buildLocalMapSignature(map: MysteryRoomLocalRuntime['map']) {
  return [
    map.id,
    map.width,
    map.height,
    map.rooms.length,
    map.doors.length,
    map.walls.length,
    map.occluders.length,
    map.incidentAnchors.length,
    map.interactables.length,
    map.lights.length,
    map.hazards.length,
    map.props.length,
    map.customObjects.length,
    map.triggers.length,
    map.soundDefinitions.length,
    map.soundSources.length,
    map.rooms.map((entry) => [
      entry.id,
      entry.color,
      entry.colorEnabled === false ? 0 : 1,
      entry.colorOpacity ?? '',
      visualOutlineSignature(entry),
      entry.floorTexture?.assetId ?? '',
      entry.floorTexture?.assetUrl ?? '',
      entry.floorTexture?.fit ?? '',
      entry.floorTexture?.opacity ?? '',
      JSON.stringify(entry.textures ?? []),
    ].join('|')).join(','),
    map.doors.map((entry) => [
      entry.id,
      entry.color ?? '',
      entry.colorEnabled === false ? 0 : 1,
      entry.colorOpacity ?? '',
      visualOutlineSignature(entry),
      JSON.stringify(entry.textures ?? []),
    ].join('|')).join(','),
    map.walls.map((entry) => [
      entry.layer ?? '',
      entry.color ?? '',
      entry.colorEnabled === false ? 0 : 1,
      entry.colorOpacity ?? '',
      visualOutlineSignature(entry),
      entry.texture?.assetId ?? '',
      entry.texture?.assetUrl ?? '',
      entry.texture?.fit ?? '',
      entry.texture?.opacity ?? '',
      JSON.stringify(entry.textures ?? []),
    ].join('|')).join(','),
    map.customObjects.map((entry) => [
      entry.layer ?? '',
      entry.color ?? '',
      entry.colorEnabled === false ? 0 : 1,
      entry.colorOpacity ?? '',
      visualOutlineSignature(entry),
      entry.assetId ?? '',
      entry.assetUrl ?? '',
      entry.assetMimeType ?? '',
      entry.assetFit ?? '',
      entry.assetAlphaCollisionEnabled ? 1 : 0,
      entry.visibleThroughWalls ? 1 : 0,
      entry.itemId ?? '',
      entry.pickupKind ?? '',
      entry.resourceKind ?? '',
      entry.resourceAmount ?? '',
      entry.resourceSourceKind ?? '',
      JSON.stringify(entry.tags ?? []),
      JSON.stringify(entry.assetTransform ?? {}),
      JSON.stringify(entry.shape ?? {}),
      JSON.stringify(entry.textures ?? []),
    ].join('|')).join(','),
    map.interactables.map((entry) => [
      entry.layer ?? '',
      entry.color ?? '',
      entry.colorEnabled === false ? 0 : 1,
      entry.colorOpacity ?? '',
      visualOutlineSignature(entry),
      JSON.stringify(entry.textures ?? []),
    ].join('|')).join(','),
    map.lights.map((entry) => [
      entry.id,
      entry.color,
      entry.colorEnabled === false ? 0 : 1,
      entry.colorOpacity ?? '',
      visualOutlineSignature(entry),
      JSON.stringify(entry.textures ?? []),
    ].join('|')).join(','),
    map.triggers.map((entry) => [
      entry.id,
      entry.color ?? '',
      entry.colorEnabled === false ? 0 : 1,
      entry.colorOpacity ?? '',
      visualOutlineSignature(entry),
      JSON.stringify(entry.textures ?? []),
    ].join('|')).join(','),
  ].join(':');
}

function visualOutlineSignature(entry: {
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
}) {
  return [entry.outlineColor ?? '', entry.outlineEnabled === false ? 0 : 1, entry.outlineOpacity ?? '', entry.outlineWidth ?? ''].join(',');
}

function buildServerMapSignature(map: MysteryRoomMapDefinition) {
  return buildLocalMapSignature(map);
}

function localLayerDepth(layer: number | undefined, fallback: number) {
  return 180 + normalizeLocalLayer(layer, fallback) * 8;
}

function localCustomObjectLayerDepth(layer: number | undefined, fallback: number) {
  return 72 + normalizeLocalLayer(layer, fallback) * 10;
}

function normalizeLocalLayer(layer: number | undefined, fallback: number) {
  const normalized = typeof layer === 'number' && Number.isFinite(layer) ? layer : fallback;
  return Math.max(0, Math.min(20, Math.round(normalized)));
}

function seededPhase(seed: string) {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) % 997;
  }
  return hash / 997 * Math.PI * 2;
}

class MysteryRoomRuntimeAudioSystem {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private readonly synthLoops = new Map<string, MysteryRoomAudioLoop>();
  private readonly htmlLoops = new Map<string, HTMLAudioElement>();
  private readonly playedOneShots = new Map<string, number>();
  private readonly playedProjectileIds = new Set<string>();
  private readonly playedServerShotKeys = new Set<string>();
  private lastFootstepAtMs = 0;
  private audioSettings: MysteryRoomAudioSettings = { musicVolume: 0.3, sfxVolume: 1 };
  private audioSettingsSubscribed = false;
  private debug: MysteryRoomAudioDebugState = {
    unlocked: false,
    activeLoops: 0,
    oneShots: 0,
    footsteps: 0,
  };
  private readonly audioSettingsHandler = (event: Event) => {
    const detail = (event as CustomEvent<Partial<MysteryRoomAudioSettings>>).detail;
    this.applyAudioSettings(detail);
  };

  unlock() {
    if (typeof window === 'undefined') {
      return;
    }
    if (!this.context) {
      const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) {
        return;
      }
      this.context = new AudioContextCtor();
      this.masterGain = this.context.createGain();
      this.masterGain.gain.value = 1;
      this.masterGain.connect(this.context.destination);
      this.loadAudioSettingsFromStorage();
    }
    if (!this.audioSettingsSubscribed) {
      window.addEventListener(MYSTERY_ROOM_AUDIO_SETTINGS_EVENT, this.audioSettingsHandler as EventListener);
      this.audioSettingsSubscribed = true;
    }
    void this.context.resume();
    this.debug.unlocked = true;
    this.publishDebug();
  }

  updateLocal(runtime: MysteryRoomLocalRuntime, sceneNowMs: number) {
    const heroPosition = runtime.hero.position;
    const definitions = new Map(runtime.map.soundDefinitions.map((definition) => [definition.id, definition]));
    this.updateSourceLoops(runtime.soundSources, definitions, heroPosition);
    this.updateOneShots(runtime.soundSources, definitions);
    this.updateProjectileShots(runtime, heroPosition);
    this.updateAlarmLoop(runtime.worldAlarm.state === 'active');
    this.updateFootsteps(runtime.hero.animationState === 'walk', sceneNowMs);
    this.publishDebug();
  }

  updateServer(
    snapshot: MrMatchSnapshot,
    map: MysteryRoomMapDefinition,
    selfPlayer: MrMatchSnapshot['players'][number] | undefined,
    sceneNowMs: number,
  ) {
    if (!selfPlayer) {
      this.stopAllLoops();
      this.publishDebug();
      return;
    }
    const position = { x: selfPlayer.x, y: selfPlayer.y };
    const definitions = new Map(map.soundDefinitions.map((definition) => [definition.id, definition]));
    const loopSources = map.soundSources.map((source) => ({ ...source, active: source.loop, lastPlayedAtMs: null }));
    this.updateSourceLoops(loopSources, definitions, position);
    this.updateServerShots(snapshot, position);
    this.updateFootsteps(selfPlayer.movement?.state === 'moving', sceneNowMs);
    this.updateAlarmLoop(false);
    this.publishDebug();
  }

  private updateProjectileShots(runtime: MysteryRoomLocalRuntime, listener: MysteryRoomVec2) {
    const activeProjectileIds = new Set<string>();
    for (const projectile of runtime.projectiles) {
      activeProjectileIds.add(projectile.id);
      if (this.playedProjectileIds.has(projectile.id)) {
        continue;
      }
      const volume = Math.max(0.08, Math.min(0.45, 0.42 * distanceAttenuation(listener, projectile.from, 1200)));
      this.playSynthOneShot('impact', this.applySfxVolume(volume));
      this.playedProjectileIds.add(projectile.id);
      this.debug.oneShots += 1;
    }
    for (const id of Array.from(this.playedProjectileIds)) {
      if (!activeProjectileIds.has(id)) {
        this.playedProjectileIds.delete(id);
      }
    }
  }

  private updateServerShots(snapshot: MrMatchSnapshot, listener: MysteryRoomVec2) {
    for (const event of snapshot.publicEvents ?? []) {
      const entry = event as Record<string, unknown>;
      if (entry.type !== 'shoot' && entry.type !== 'hit_confirmed' && entry.type !== 'shot_missed') {
        continue;
      }
      const fromX = Number(entry.fromX);
      const fromY = Number(entry.fromY);
      if (!Number.isFinite(fromX) || !Number.isFinite(fromY)) {
        continue;
      }
      const key = `${String(entry.serverTick ?? '0')}:${String(entry.shooterId ?? 'unknown')}:${Math.round(fromX)}:${Math.round(fromY)}`;
      if (this.playedServerShotKeys.has(key)) {
        continue;
      }
      const volume = Math.max(0.07, Math.min(0.38, 0.4 * distanceAttenuation(listener, { x: fromX, y: fromY }, 1200)));
      this.playSynthOneShot('impact', this.applySfxVolume(volume));
      this.playedServerShotKeys.add(key);
      this.debug.oneShots += 1;
    }
    if (this.playedServerShotKeys.size > 80) {
      const recentKeys = new Set<string>();
      for (const event of snapshot.publicEvents ?? []) {
        const entry = event as Record<string, unknown>;
        const fromX = Number(entry.fromX);
        const fromY = Number(entry.fromY);
        if (!Number.isFinite(fromX) || !Number.isFinite(fromY)) {
          continue;
        }
        recentKeys.add(`${String(entry.serverTick ?? '0')}:${String(entry.shooterId ?? 'unknown')}:${Math.round(fromX)}:${Math.round(fromY)}`);
      }
      for (const key of Array.from(this.playedServerShotKeys)) {
        if (!recentKeys.has(key)) {
          this.playedServerShotKeys.delete(key);
        }
      }
    }
  }

  private updateSourceLoops(
    sources: MysteryRoomLocalRuntime['soundSources'],
    definitions: Map<string, MysteryRoomMapDefinition['soundDefinitions'][number]>,
    listener: MysteryRoomVec2,
  ) {
    const seen = new Set<string>();
    for (const source of sources) {
      if (!source.loop || !source.active) {
        continue;
      }
      const definition = definitions.get(source.soundId);
      const volume = this.applyMusicVolume(source.volume * soundSourceLoopAttenuation(listener, source));
      if (volume <= 0.005) {
        continue;
      }

      const loopId = `source:${source.id}`;
      seen.add(loopId);
      if (definition?.url) {
        this.ensureHtmlLoop(loopId, definition.url, volume);
      } else {
        this.ensureSynthLoop(loopId, definition?.kind ?? 'ambient', volume);
      }
    }

    this.stopStaleLoops(seen);
    this.debug.activeLoops = this.synthLoops.size + this.htmlLoops.size;
  }

  private updateOneShots(
    sources: MysteryRoomLocalRuntime['soundSources'],
    definitions: Map<string, MysteryRoomMapDefinition['soundDefinitions'][number]>,
  ) {
    for (const source of sources) {
      if (source.loop || !source.active || source.lastPlayedAtMs === null) {
        continue;
      }
      if (this.playedOneShots.get(source.id) === source.lastPlayedAtMs) {
        continue;
      }

      const definition = definitions.get(source.soundId);
      const volume = this.applySfxVolume(Math.max(0.02, Math.min(1, source.volume)));
      if (definition?.url) {
        this.playHtmlOneShot(definition.url, volume);
      } else {
        this.playSynthOneShot(definition?.kind ?? 'impact', volume);
      }
      this.playedOneShots.set(source.id, source.lastPlayedAtMs);
      this.debug.oneShots += 1;
    }
  }

  private updateAlarmLoop(active: boolean) {
    const alarmId = 'world:alarm';
    if (active) {
      this.ensureSynthLoop(alarmId, 'alarm', this.applySfxVolume(0.28));
      return;
    }
    this.stopLoop(alarmId);
  }

  private updateFootsteps(walking: boolean, sceneNowMs: number) {
    if (!walking || sceneNowMs - this.lastFootstepAtMs < 265) {
      return;
    }
    this.lastFootstepAtMs = sceneNowMs;
    this.playSynthOneShot('footstep', this.applySfxVolume(0.16));
    this.debug.footsteps += 1;
  }

  private applyMusicVolume(volume: number) {
    return clampAudioVolume(volume * this.audioSettings.musicVolume);
  }

  private applySfxVolume(volume: number) {
    return clampAudioVolume(volume * this.audioSettings.sfxVolume);
  }

  private applyAudioSettings(settings: Partial<MysteryRoomAudioSettings> | null | undefined) {
    if (!settings) {
      return;
    }
    const nextMusic = clampAudioVolume(
      typeof settings.musicVolume === 'number' ? settings.musicVolume : this.audioSettings.musicVolume,
    );
    const nextSfx = clampAudioVolume(
      typeof settings.sfxVolume === 'number' ? settings.sfxVolume : this.audioSettings.sfxVolume,
    );
    this.audioSettings = { musicVolume: nextMusic, sfxVolume: nextSfx };
  }

  private loadAudioSettingsFromStorage() {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const raw = window.localStorage.getItem(MYSTERY_ROOM_AUDIO_SETTINGS_STORAGE_KEY);
      if (!raw) {
        return;
      }
      this.applyAudioSettings(JSON.parse(raw) as Partial<MysteryRoomAudioSettings>);
    } catch {
      // Настройки громкости не критичны для запуска игры.
    }
  }

  private ensureSynthLoop(loopId: string, kind: MysteryRoomMapDefinition['soundDefinitions'][number]['kind'], volume: number) {
    if (!this.context || !this.masterGain) {
      return;
    }
    let loop = this.synthLoops.get(loopId);
    if (!loop) {
      const gain = this.context.createGain();
      gain.gain.value = 0;
      gain.connect(this.masterGain);
      const sources = this.createSynthLoopSources(kind, gain);
      loop = { gain, sources };
      this.synthLoops.set(loopId, loop);
    }
    loop.gain.gain.setTargetAtTime(Math.min(0.6, volume), this.context.currentTime, 0.08);
  }

  private createSynthLoopSources(
    kind: MysteryRoomMapDefinition['soundDefinitions'][number]['kind'],
    destination: AudioNode,
  ): AudioScheduledSourceNode[] {
    const context = this.context;
    if (!context) {
      return [];
    }
    const oscillator = context.createOscillator();
    oscillator.type = kind === 'alarm' ? 'square' : kind === 'fan' ? 'sawtooth' : 'triangle';
    oscillator.frequency.value = kind === 'alarm' ? 520 : kind === 'fan' ? 82 : 128;
    oscillator.connect(destination);
    oscillator.start();
    return [oscillator];
  }

  private ensureHtmlLoop(loopId: string, url: string, volume: number) {
    if (!this.debug.unlocked || typeof Audio === 'undefined') {
      return;
    }
    let audio = this.htmlLoops.get(loopId);
    if (!audio) {
      audio = new Audio(url);
      audio.loop = true;
      audio.preload = 'auto';
      audio.volume = Math.max(0, Math.min(1, volume));
      this.htmlLoops.set(loopId, audio);
      void audio.play().catch(() => undefined);
    }
    audio.volume = Math.max(0, Math.min(1, volume));
    if (audio.paused) {
      void audio.play().catch(() => undefined);
    }
  }

  private playSynthOneShot(kind: MysteryRoomMapDefinition['soundDefinitions'][number]['kind'], volume: number) {
    if (!this.context || !this.masterGain) {
      return;
    }
    const now = this.context.currentTime;
    const gain = this.context.createGain();
    const oscillator = this.context.createOscillator();
    const duration = kind === 'alarm' ? 0.46 : kind === 'impact' ? 0.22 : 0.12;
    oscillator.type = kind === 'impact' ? 'square' : kind === 'alarm' ? 'sawtooth' : 'triangle';
    oscillator.frequency.setValueAtTime(kind === 'footstep' ? 96 : kind === 'alarm' ? 620 : 360, now);
    oscillator.frequency.exponentialRampToValueAtTime(kind === 'footstep' ? 44 : kind === 'alarm' ? 920 : 180, now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.02, volume), now + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.masterGain);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private playHtmlOneShot(url: string, volume: number) {
    if (!this.debug.unlocked || typeof Audio === 'undefined') {
      return;
    }
    const audio = new Audio(url);
    audio.preload = 'auto';
    audio.volume = Math.max(0, Math.min(1, volume));
    void audio.play().catch(() => undefined);
  }

  private stopStaleLoops(seen: Set<string>) {
    for (const loopId of Array.from(this.synthLoops.keys())) {
      if (!seen.has(loopId) && loopId !== 'world:alarm') {
        this.stopLoop(loopId);
      }
    }
    for (const loopId of Array.from(this.htmlLoops.keys())) {
      if (!seen.has(loopId)) {
        this.stopLoop(loopId);
      }
    }
  }

  private stopAllLoops() {
    for (const loopId of [...Array.from(this.synthLoops.keys()), ...Array.from(this.htmlLoops.keys())]) {
      this.stopLoop(loopId);
    }
    this.debug.activeLoops = 0;
  }

  private stopLoop(loopId: string) {
    const synth = this.synthLoops.get(loopId);
    if (synth) {
      synth.gain.disconnect();
      synth.sources.forEach((source) => {
        try {
          source.stop();
        } catch {
          // Source may already be stopped by the browser; cleanup still succeeds.
        }
      });
      this.synthLoops.delete(loopId);
    }
    const html = this.htmlLoops.get(loopId);
    if (html) {
      html.pause();
      html.currentTime = 0;
      this.htmlLoops.delete(loopId);
    }
  }

  private publishDebug() {
    this.debug.activeLoops = this.synthLoops.size + this.htmlLoops.size;
    if (typeof window !== 'undefined') {
      (window as typeof window & { __mysteryRoomAudioDebug?: MysteryRoomAudioDebugState }).__mysteryRoomAudioDebug = { ...this.debug };
    }
  }
}

function distanceAttenuation(listener: MysteryRoomVec2, source: MysteryRoomVec2, radius: number): number {
  const distance = Math.hypot(listener.x - source.x, listener.y - source.y);
  if (distance >= radius) {
    return 0;
  }
  const normalized = 1 - distance / Math.max(1, radius);
  return Math.pow(normalized, 1.35);
}

/** Зацикленные клипы: учёт флагов равномерной громкости из определения источника на карте. */
function soundSourceLoopAttenuation(
  listener: MysteryRoomVec2,
  source: Pick<
    MysteryRoomMapDefinition['soundSources'][number],
    'position' | 'radius' | 'uniformMapVolume' | 'uniformVolumeInRadius'
  >,
): number {
  if (source.uniformMapVolume) {
    return 1;
  }
  const radius = Math.max(1, source.radius);
  const distance = Math.hypot(listener.x - source.position.x, listener.y - source.position.y);
  if (distance >= radius) {
    return 0;
  }
  if (source.uniformVolumeInRadius) {
    return 1;
  }
  return distanceAttenuation(listener, source.position, radius);
}

function clampAudioVolume(value: number) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(1, value));
}

function customObjectAssetTextureKey(object: MysteryRoomCustomObjectDefinition): string {
  const source = object.assetId || object.assetUrl || object.id;
  return `mr_custom_asset_${source.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 96)}`;
}

function surfaceTextureKey(texture: MysteryRoomSurfaceTexture): string {
  const source = texture.assetId || texture.builtinTextureId || resolveMysteryRoomTextureAssetUrl(texture) || texture.assetName || 'surface';
  const safe = source.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 72);
  return `mr_surface_${safe}_${stableTextureHash(resolveMysteryRoomTextureAssetUrl(texture) ?? source)}`;
}

function stableTextureHash(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash).toString(36);
}

function normalizeSurfaceTextureOpacity(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0.92;
  }
  return Math.max(0, Math.min(1, Math.round(value * 100) / 100));
}

function normalizeBaseColorOpacity(value: number | null | undefined, fallback = 0.9): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(0, Math.min(1, Math.round(value * 100) / 100));
}

function runtimeBaseColorOpacity(entry: { colorEnabled?: boolean; colorOpacity?: number }, fallback = 0.9): number {
  return entry.colorEnabled === false ? 0 : normalizeBaseColorOpacity(entry.colorOpacity, fallback);
}

function runtimeOutlineOpacity(entry: { outlineEnabled?: boolean; outlineOpacity?: number }, fallback = 0.5): number {
  return entry.outlineEnabled === false ? 0 : normalizeBaseColorOpacity(entry.outlineOpacity, fallback);
}

function runtimeOutlineWidth(entry: { outlineEnabled?: boolean; outlineWidth?: number }, fallback = 2): number {
  if (entry.outlineEnabled === false) {
    return 0;
  }
  if (typeof entry.outlineWidth !== 'number' || !Number.isFinite(entry.outlineWidth)) {
    return fallback;
  }
  return Math.max(0, Math.min(100, Math.round(entry.outlineWidth)));
}

function drawWallSegmentBlock(graphics: any, segment: MysteryRoomSegment, width: number, color: number, alpha: number) {
  if (!graphics || width <= 0 || alpha <= 0) return;
  const dx = segment.end.x - segment.start.x;
  const dy = segment.end.y - segment.start.y;
  const length = Math.hypot(dx, dy);
  if (length <= 0.001) return;
  const half = width / 2;
  const nx = (-dy / length) * half;
  const ny = (dx / length) * half;
  graphics.fillStyle(color, alpha);
  graphics.beginPath();
  graphics.moveTo(segment.start.x + nx, segment.start.y + ny);
  graphics.lineTo(segment.end.x + nx, segment.end.y + ny);
  graphics.lineTo(segment.end.x - nx, segment.end.y - ny);
  graphics.lineTo(segment.start.x - nx, segment.start.y - ny);
  graphics.closePath();
  graphics.fillPath();
}

function wallRuntimeNodeKey(point: MysteryRoomVec2): string {
  return `${Math.round(point.x)}:${Math.round(point.y)}`;
}

function wallJointPolygonPoints(node: MysteryRoomVec2, segments: MysteryRoomSegment[], width: number): MysteryRoomVec2[] {
  const half = width / 2;
  const points: MysteryRoomVec2[] = [];
  segments.forEach((segment) => {
    const usesStart = wallRuntimeNodeKey(segment.start) === wallRuntimeNodeKey(node);
    const usesEnd = wallRuntimeNodeKey(segment.end) === wallRuntimeNodeKey(node);
    if (!usesStart && !usesEnd) return;
    const other = usesStart ? segment.end : segment.start;
    const dx = other.x - node.x;
    const dy = other.y - node.y;
    const length = Math.hypot(dx, dy);
    if (length <= 0.001) return;
    const nx = (-dy / length) * half;
    const ny = (dx / length) * half;
    points.push({ x: node.x + nx, y: node.y + ny }, { x: node.x - nx, y: node.y - ny });
  });
  return points.sort((left, right) => Math.atan2(left.y - node.y, left.x - node.x) - Math.atan2(right.y - node.y, right.x - node.x));
}

function drawWallJointPolygon(graphics: any, points: MysteryRoomVec2[], color: number, alpha: number) {
  if (!graphics || points.length < 3 || alpha <= 0) return;
  graphics.fillStyle(color, alpha);
  graphics.beginPath();
  graphics.moveTo(points[0].x, points[0].y);
  for (const point of points.slice(1)) {
    graphics.lineTo(point.x, point.y);
  }
  graphics.closePath();
  graphics.fillPath();
}

function drawWallJointBlocks(graphics: any, segments: MysteryRoomSegment[], bodyWidth: number, bodyAlphaFallback: number) {
  const nodes = new Map<string, { node: MysteryRoomVec2; segments: MysteryRoomSegment[] }>();
  segments.forEach((segment) => {
    [segment.start, segment.end].forEach((point) => {
      const node = { x: Math.round(point.x), y: Math.round(point.y) };
      const key = wallRuntimeNodeKey(node);
      const entry = nodes.get(key) ?? { node, segments: [] };
      entry.segments.push(segment);
      nodes.set(key, entry);
    });
  });
  nodes.forEach(({ node, segments: connectedSegments }) => {
    if (connectedSegments.length < 2) return;
    const baseSegment = connectedSegments[0];
    const outlineWidth = runtimeOutlineWidth(baseSegment, 1);
    const outlineAlpha = runtimeOutlineOpacity(baseSegment, 0.52);
    if (outlineWidth > 0 && outlineAlpha > 0) {
      drawWallJointPolygon(
        graphics,
        wallJointPolygonPoints(node, connectedSegments, bodyWidth + outlineWidth * 2),
        baseSegment.outlineColor ?? 0xe2e8f0,
        outlineAlpha,
      );
    }
    drawWallJointPolygon(
      graphics,
      wallJointPolygonPoints(node, connectedSegments, bodyWidth),
      baseSegment.color ?? 0xe2e8f0,
      runtimeBaseColorOpacity(baseSegment, bodyAlphaFallback),
    );
  });
}

function isMysteryRoomAnimatedGifObject(object: MysteryRoomCustomObjectDefinition): boolean {
  const mime = object.assetMimeType?.toLowerCase() ?? '';
  const url = object.assetUrl?.split('?')[0]?.toLowerCase() ?? '';
  const name = object.assetName?.toLowerCase() ?? '';
  return mime === 'image/gif' || url.endsWith('.gif') || name.endsWith('.gif');
}

function drawCustomObjectImageClipMask(graphics: any, object: MysteryRoomCustomObjectDefinition) {
  const rect = object.rect;
  const points = customObjectClipLocalPoints(object);
  graphics.fillStyle(0xffffff, 1);
  if (points.length === 1) {
    graphics.fillCircle(points[0].x, points[0].y, Math.max(8, Math.min(rect.width, rect.height) * 0.16));
    return;
  }
  if (points.length === 2) {
    const width = Math.max(8, Math.min(rect.width, rect.height) * 0.18);
    graphics.lineStyle(width, 0xffffff, 1);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    graphics.lineTo(points[1].x, points[1].y);
    graphics.strokePath();
    graphics.fillCircle(points[0].x, points[0].y, width / 2);
    graphics.fillCircle(points[1].x, points[1].y, width / 2);
    return;
  }
  graphics.beginPath();
  graphics.moveTo(points[0].x, points[0].y);
  for (const point of points.slice(1)) {
    graphics.lineTo(point.x, point.y);
  }
  graphics.closePath();
  graphics.fillPath();
}

function customObjectClipLocalPoints(object: MysteryRoomCustomObjectDefinition): MysteryRoomVec2[] {
  if (getMysteryRoomEffectiveCustomObjectAsset(object)?.isDefaultItemAsset) {
    return [
      { x: -object.rect.width / 2, y: -object.rect.height / 2 },
      { x: object.rect.width / 2, y: -object.rect.height / 2 },
      { x: object.rect.width / 2, y: object.rect.height / 2 },
      { x: -object.rect.width / 2, y: object.rect.height / 2 },
    ];
  }
  const points =
    object.shape?.kind === 'polygon' && object.shape.points.length > 0
      ? object.shape.points
      : [
          { x: -0.5, y: -0.5 },
          { x: 0.5, y: -0.5 },
          { x: 0.5, y: 0.5 },
          { x: -0.5, y: 0.5 },
        ];
  return points.map((point) => ({
    x: point.x * object.rect.width,
    y: point.y * object.rect.height,
  }));
}

function normalizeRuntimeAssetTransform(object?: MysteryRoomCustomObjectDefinition) {
  const transform = object?.assetTransform;
  return {
    offsetX: finiteRuntimeNumber(transform?.offsetX, 0),
    offsetY: finiteRuntimeNumber(transform?.offsetY, 0),
    scaleX: positiveRuntimeNumber(transform?.scaleX, 1),
    scaleY: positiveRuntimeNumber(transform?.scaleY, 1),
    rotation: finiteRuntimeNumber(transform?.rotation, 0),
    clipToBounds: transform?.clipToBounds ?? true,
  };
}

function rotateVector(x: number, y: number, degrees: number): MysteryRoomVec2 {
  const radians = (degrees / 180) * Math.PI;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  };
}

function finiteRuntimeNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function positiveRuntimeNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function normalizeRuntimeEffectIntensity(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  return Math.max(-100, Math.min(100, Math.round(value)));
}

function buildServerVisibilityDoors(map: MysteryRoomMapDefinition, snapshot: MrMatchSnapshot): MysteryRoomDoorRuntimeState[] {
  const stateById = new Map((snapshot.doors ?? []).map((door) => [door.id, door]));
  return map.doors.map((door) => {
    const state = stateById.get(door.id);
    const locked = state?.locked ?? false;
    const open = state?.open ?? false;
    return {
      id: door.id,
      label: door.label ?? door.id,
      requiredAccess: door.requiredAccess,
      accessMode: door.accessMode ?? (door.requiredAccess === 'public' ? 'public' : 'card_required'),
      accessCardObjectIds: [...(door.accessCardObjectIds ?? [])],
      fromZoneId: door.fromZoneId,
      toZoneId: door.toZoneId,
      state: locked ? 'locked' : open ? 'open' : 'closed',
      openProgress: !locked && open ? 1 : 0,
      panel: { ...door.panel },
      trigger: { ...door.trigger },
      orientation: door.orientation,
      holdOpenMs: 0,
    };
  });
}

function interactableColor(kind: MysteryRoomInteractableKind) {
  switch (kind) {
    case 'access_grant':
      return 0x22d3ee;
    case 'hide_zone':
      return 0x34d399;
    case 'inspect':
      return 0xf59e0b;
    case 'pickup':
      return 0xfacc15;
    case 'light_switch':
      return 0xa78bfa;
  }
}

function shouldShowCustomObjectWorldLabel(runtime: MysteryRoomLocalRuntime, object: MysteryRoomCustomObjectDefinition): boolean {
  const mode = object.worldLabelVisibility ?? 'hidden';
  if (mode === 'always') {
    return true;
  }
  if (mode !== 'near_interactive' || !object.interactive) {
    return false;
  }
  return getNearestLocalInteractable(runtime)?.customObjectId === object.id;
}

function shouldShowInteractableWorldLabel(
  interactable: MysteryRoomLocalRuntime['interactables'][number],
  isPrimary: boolean,
): boolean {
  const mode = interactable.worldLabelVisibility ?? 'hidden';
  if (mode === 'always') {
    return true;
  }
  return mode === 'near_interactive' && isPrimary;
}

function serverInteractableCustomObjectId(interactable: MysteryRoomMapDefinition['interactables'][number]): string | undefined {
  const value = (interactable as { customObjectId?: unknown }).customObjectId;
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function serverInteractablePickupKind(
  interactable: MysteryRoomMapDefinition['interactables'][number],
): MysteryRoomLocalRuntime['interactables'][number]['pickupKind'] {
  const value = (interactable as { pickupKind?: unknown }).pickupKind;
  return value === 'weapon' || value === 'tool' || value === 'access_card' ? value : undefined;
}

function usesDefaultPickupImage(interactable: MysteryRoomLocalRuntime['interactables'][number]) {
  return Boolean(defaultPickupImageAsset(interactable));
}

function defaultPickupImageTextureKey(interactable: MysteryRoomLocalRuntime['interactables'][number]): string | null {
  return defaultPickupImageAsset(interactable)?.textureKey ?? null;
}

function defaultPickupImageAsset(interactable: MysteryRoomLocalRuntime['interactables'][number]) {
  if (interactable.kind === 'access_grant') {
    const visual = getMysteryRoomAccessCardVisual(interactable.grantsAccess);
    return {
      textureKey: visual.worldTextureKey,
      url: visual.worldUrl,
      display: visual.worldDisplay,
    };
  }
  if (interactable.kind !== 'pickup') {
    return null;
  }
  const visual = getMysteryRoomDefaultItemVisual(
    interactable.pickupKind === 'access_card' ? 'access_card' : interactable.itemId,
  );
  if (visual) {
    return {
      textureKey: visual.worldTextureKey,
      url: visual.worldUrl,
      display: visual.worldDisplay,
    };
  }
  return null;
}

function lightVisibleColor(light: MysteryRoomLocalRuntime['lights'][number]): number {
  return light.color ?? kelvinToRgbNumber(light.colorTemperatureK ?? 4200);
}

function lightFlickerFactor(light: MysteryRoomLocalRuntime['lights'][number], sceneTimeMs: number, elapsedMs: number): number {
  const mode = light.flickerMode ?? (light.flicker ? 'flicker' : 'steady');
  const amount = Math.max(0, Math.min(0.92, light.flickerIntensity ?? (light.flicker ? 0.32 : 0.26)));
  if (mode === 'steady' || amount <= 0) {
    return 1;
  }
  if (mode === 'event_flicker' && (light.eventFlickerUntilMs ?? 0) <= elapsedMs) {
    return 1;
  }

  if (mode === 'random_bulb') {
    const minMs = Math.max(40, light.flickerMinIntervalMs ?? 90);
    const maxMs = Math.max(minMs, light.flickerMaxIntervalMs ?? 260);
    const bucket = Math.floor(sceneTimeMs / minMs);
    const random = seededUnit(bucket + parseInt(stableTextureHash(light.id), 36) + Math.round(light.position.x * 13));
    const interval = minMs + (maxMs - minMs) * random;
    const phase = (sceneTimeMs % interval) / interval;
    const dip = phase < 0.22 ? 1 - Math.sin((phase / 0.22) * Math.PI) * amount : 1;
    return Math.max(0.08, dip * (0.94 + seededUnit(bucket + 17) * 0.12));
  }

  const pulse = Math.sin(sceneTimeMs / Math.max(40, light.flickerMinIntervalMs ?? 95) + light.position.x * 0.01);
  return Math.max(0.08, 1 - amount * 0.5 + pulse * amount * 0.5);
}

function seededUnit(seed: number): number {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
}

function kelvinToRgbNumber(kelvin: number): number {
  const temp = Math.max(10, Math.min(400, kelvin / 100));
  const red = temp <= 66 ? 255 : clampColor(329.698727446 * Math.pow(temp - 60, -0.1332047592));
  const green = temp <= 66
    ? clampColor(99.4708025861 * Math.log(temp) - 161.1195681661)
    : clampColor(288.1221695283 * Math.pow(temp - 60, -0.0755148492));
  const blue = temp >= 66 ? 255 : temp <= 19 ? 0 : clampColor(138.5177312231 * Math.log(temp - 10) - 305.0447927307);
  return (red << 16) + (green << 8) + blue;
}

function clampColor(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function awarenessFillColor(awareness: 'patrol' | 'suspicious' | 'alert' | 'search') {
  switch (awareness) {
    case 'alert':
      return 0xef4444;
    case 'suspicious':
      return 0xfacc15;
    case 'search':
      return 0xf59e0b;
    case 'patrol':
      return 0x38bdf8;
  }
}

function awarenessStrokeColor(awareness: 'patrol' | 'suspicious' | 'alert' | 'search') {
  switch (awareness) {
    case 'alert':
      return 0xfca5a5;
    case 'suspicious':
      return 0xfef08a;
    case 'search':
      return 0xfcd34d;
    case 'patrol':
      return 0xe2e8f0;
  }
}

function incidentFillColor(kind: string) {
  switch (kind) {
    case 'alarm_call':
      return 0xfacc15;
    case 'alarm':
      return 0xf97316;
    case 'fire':
      return 0xef4444;
    case 'door_failure':
      return 0xf59e0b;
    case 'reactor_fault':
      return 0xfb7185;
    case 'server_fault':
      return 0x38bdf8;
    case 'injured_person':
      return 0x34d399;
    default:
      return 0xe2e8f0;
  }
}

function serverMarkerColor(kind: string) {
  switch (kind) {
    case 'contract_step':
      return 0xf59e0b;
    case 'personal_task':
      return 0x22d3ee;
    case 'evidence':
      return 0x38bdf8;
    case 'danger':
      return 0xef4444;
    default:
      return 0xfcd34d;
  }
}

function npcRoleColor(role: string | null | undefined) {
  switch (role) {
    case 'guard':
      return 0x38bdf8;
    case 'scientist':
      return 0xe5e7eb;
    case 'technician':
      return 0xf59e0b;
    case 'medic':
      return 0x34d399;
    default:
      return 0xa5b4fc;
  }
}

export function resolveIncidentOverlayState(status: 'active' | 'assigned' | 'resolving' | 'resolved') {
  switch (status) {
    case 'active':
      return { alpha: 0.94, stroke: 0xf8fafc };
    case 'assigned':
      return { alpha: 0.82, stroke: 0xfcd34d };
    case 'resolving':
      return { alpha: 0.7, stroke: 0x7dd3fc };
    case 'resolved':
      return { alpha: 0.38, stroke: 0x94a3b8 };
  }
}

function heroAlertStrokeColor(alertState: 'clear' | 'suspicious' | 'spotted') {
  switch (alertState) {
    case 'spotted':
      return 0xfca5a5;
    case 'suspicious':
      return 0xfef08a;
    case 'clear':
      return 0xfffbeb;
  }
}

function heroLabel(hidden: boolean, alertState: 'clear' | 'suspicious' | 'spotted') {
  if (hidden) {
    return 'Оператор [hidden]';
  }

  if (alertState === 'spotted') {
    return 'Оператор [spotted]';
  }

  if (alertState === 'suspicious') {
    return 'Оператор [suspicious]';
  }

  return 'Оператор';
}

export function resolveNpcVisionFacing(
  npc: Pick<MysteryRoomLocalRuntime['npcs'][number], 'awarenessState' | 'investigateTarget' | 'position' | 'facing'>,
) {
  if (npc.awarenessState === 'suspicious' && npc.investigateTarget) {
    return facingFromVelocity(
      {
        x: npc.investigateTarget.x - npc.position.x,
        y: npc.investigateTarget.y - npc.position.y,
      },
      npc.facing,
    );
  }

  return npc.facing;
}

export function buildNpcVisionOverlayPoints(
  position: MysteryRoomVec2,
  facing: 'up' | 'down' | 'left' | 'right',
  range: number,
  halfAngleDeg: number,
  blockingSegments: MysteryRoomSegment[],
) {
  const facingAngle = facingToAngle(facing);
  const halfAngleRad = (halfAngleDeg * Math.PI) / 180;
  const steps = 14;
  const points = [position];

  for (let index = 0; index <= steps; index += 1) {
    const t = index / steps;
    const angle = facingAngle - halfAngleRad + t * halfAngleRad * 2;
    points.push(castVisionOverlayRay(position, angle, range, blockingSegments));
  }

  return points;
}

export function buildServerNpcVisionOverlayPoints(
  npc: Pick<MrMatchSnapshot['npcs'][number], 'visionCone'>,
  renderPosition: MysteryRoomVec2,
  facing: 'up' | 'down' | 'left' | 'right',
  blockingSegments: MysteryRoomSegment[],
) {
  const range = Math.max(120, npc.visionCone?.range ?? 280);
  const halfAngle = Math.max(20, Math.min(80, (npc.visionCone?.angle ?? 70) / 2));
  return buildNpcVisionOverlayPoints(renderPosition, facing, range, halfAngle, blockingSegments);
}

function castVisionOverlayRay(
  origin: MysteryRoomVec2,
  angle: number,
  range: number,
  blockingSegments: MysteryRoomSegment[],
) {
  const rayEnd = {
    x: origin.x + Math.cos(angle) * range,
    y: origin.y + Math.sin(angle) * range,
  };
  let closestHit = rayEnd;
  let closestDistanceSq = squaredDistance(origin, rayEnd);

  for (const segment of blockingSegments) {
    const hit = raySegmentIntersection(origin, rayEnd, segment.start, segment.end);
    if (!hit) {
      continue;
    }

    const distanceSq = squaredDistance(origin, hit);
    if (distanceSq < closestDistanceSq) {
      closestHit = hit;
      closestDistanceSq = distanceSq;
    }
  }

  return closestHit;
}

function raySegmentIntersection(
  rayStart: MysteryRoomVec2,
  rayEnd: MysteryRoomVec2,
  segStart: MysteryRoomVec2,
  segEnd: MysteryRoomVec2,
) {
  const r = {
    x: rayEnd.x - rayStart.x,
    y: rayEnd.y - rayStart.y,
  };
  const s = {
    x: segEnd.x - segStart.x,
    y: segEnd.y - segStart.y,
  };
  const denominator = cross(r, s);

  if (Math.abs(denominator) < 0.0000001) {
    return null;
  }

  const qp = {
    x: segStart.x - rayStart.x,
    y: segStart.y - rayStart.y,
  };
  const t = cross(qp, s) / denominator;
  const u = cross(qp, r) / denominator;

  if (t < 0 || t > 1 || u < 0 || u > 1) {
    return null;
  }

  return {
    x: rayStart.x + t * r.x,
    y: rayStart.y + t * r.y,
  };
}

function squaredDistance(a: MysteryRoomVec2, b: MysteryRoomVec2) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function cross(a: MysteryRoomVec2, b: MysteryRoomVec2) {
  return a.x * b.y - a.y * b.x;
}

function facingToAngle(facing: 'up' | 'down' | 'left' | 'right') {
  switch (facing) {
    case 'up':
      return -Math.PI / 2;
    case 'down':
      return Math.PI / 2;
    case 'left':
      return Math.PI;
    case 'right':
      return 0;
  }
}
